import { EventBus } from './EventBus.js';
import { InputManager } from './InputManager.js';
import { Renderer } from './Renderer.js';
import { Camera } from './Camera.js';
import { Player } from '../entities/Player.js';
import { Boss } from '../entities/Boss.js';
import { Skirmisher } from '../entities/Skirmisher.js';
import { CombatSystem } from '../systems/combat/CombatSystem.js';
import { UltimateSystem } from '../systems/combat/UltimateSystem.js';
import { GrazeChain } from '../systems/combat/GrazeChain.js';
import { Resonance } from '../systems/combat/Resonance.js';
import { fireRelease } from '../systems/combat/polarityRelease.js';
import { createToneCaster } from '../systems/combat/attunement.js';
import { SpawningSystem } from '../systems/spawning/SpawningSystem.js';
import { CaptureTracker } from '../systems/spawning/CaptureTracker.js';
import { cancelBullets, bulletCancelTier, bulletCancelXp } from '../systems/spawning/bulletCancel.js';
import { MinionWaveSystem, randomMinionTypeSet } from '../systems/spawning/MinionWaveSystem.js';
import { PickupSystem } from '../systems/PickupSystem.js';
import { CoverSystem } from '../systems/CoverSystem.js';
import { DamageLedger } from '../systems/DamageLedger.js';
import { TutorialDirector } from '../systems/TutorialDirector.js';
import { buildEchoBossDef } from '../data/echoBoss.js';
import { ProgressionSystem } from '../systems/progression/ProgressionSystem.js';
import { calcEchoes, calcGlimmer, calcLeaderboardScoreBreakdown, getAscensionRewardMultiplier, getEndlessEchoMultiplier, SHARDS_PER_BOSS, ECHO_BONUS_PER_BOSS, TIDE_CLEAR_BONUS, TIDE_PAR_SECONDS } from '../systems/progression/Currency.js';
import { ParticleSystem } from '../systems/vfx/ParticleSystem.js';
import { JuiceDirector } from '../systems/vfx/JuiceDirector.js';
import { audio } from '../systems/audio/AudioSystem.js';
import { META_UPGRADES } from '../data/metaUpgrades.js';
import { SHARD_UNLOCKS } from '../data/shardUnlocks.js';
import { getPetBonuses } from '../data/pets.js';
import { getWorldBonuses, getCounterPressure } from '../data/repriseWorld.js';
import { LevelUpPicker } from '../ui/LevelUpPicker.js';
import { SpoilsChest } from '../ui/SpoilsChest.js';
import { SpoilsChestDrop } from '../entities/SpoilsChestDrop.js';
import { FUSION_DEFS } from '../data/fusions.js';
import { OPENING_CHEST_ROLLS, chestTier } from '../data/spoils.js';
import { TIDE, tideWaveConfig, tideEchoes } from '../data/tide.js';
import { RunEndOverlay } from '../ui/RunEndOverlay.js';
import { PauseOverlay } from '../ui/PauseOverlay.js';
import { TouchControls } from '../ui/TouchControls.js';
import { FinalWarningOverlay } from '../ui/FinalWarningOverlay.js';
import { controlsHintText, controlsHintAlpha, ultimateKeyLabel } from '../ui/controlsHint.js';
import { TutorialOverlay } from '../ui/TutorialOverlay.js';
import { ActOpeningOverlay } from '../ui/ActOpeningOverlay.js';
import { SurviveOverlay } from '../ui/SurviveOverlay.js';
import { drawSprite, mergePaletteOverride, hexLuminance } from './Sprite.js';
import { PLAYER_SPRITES, BOSS_SPRITES, WEAPON_SPRITES, MINION_SPRITES, HEALTH_POD_SPRITE, HAT_SPRITES, SPOILS_CHEST_SPRITE, SPOILS_CHEST_TRIM } from '../data/sprites.js';
import { COSMETICS, cosmeticMilestoneMet } from '../data/cosmetics.js';
import { ACT_OPENINGS, BOSS_LORE } from '../data/lore.js';
import { BOSSES, MINIBOSSES } from '../data/bosses.js';
import { CHALLENGE_ROSTER, challengeLevelForBoss } from '../data/challengeBosses.js';
import { CAMPAIGN_BOSSES, ACT2_ECHO_BONUS, ACT2_SHARDS } from '../data/act2Bosses.js';
import { ACT3_ECHO_BONUS, ACT3_SHARDS } from '../data/act3Bosses.js';
import { actForStartIndex, actStartLevel, ACT3_START_INDEX, FULL_ROSTER } from '../data/acts.js';
import { LevySystem } from '../systems/act3/LevySystem.js';
import { ManifoldSystem } from '../systems/act3/ManifoldSystem.js';
import { VerseSystem } from '../systems/act3/VerseSystem.js';
import { LevyPrompt } from '../ui/LevyPrompt.js';
import { VersePicker } from '../ui/VersePicker.js';
import { bossDeathOutcome } from './campaignFlow.js';
import { applyGravityWell } from '../systems/combat/gravityWell.js';
import { createAscensionRules, getAscension } from '../data/ascensions.js';
import { WEAPON_FIRE_FLASH_DURATION } from '../systems/combat/Weapon.js';
import { ARENA, CANCEL, CAPTURE, DEATHBOMB, FOCUS, GRAZE, MOTES, PLAYER, POLARITY, RENDER, VIRTUAL_WIDTH, VIRTUAL_HEIGHT, SIM_DT, UI_FONT } from '../../config/constants.js';
import { formatTime } from './formatTime.js';
import { createRunRng } from './Rng.js';
import { normalizeSeed } from '../data/seeds.js';

const MAX_FRAME_TIME = 0.25;
// Cascade (level-up card): blast radius of a killed minion's detonation.
// Roughly a minion's own body plus a step, so it clips the neighbours in a
// packed wave without reaching across the arena.
const CASCADE_RADIUS = 46;
// Per-act minion vocabularies a boss def may declare on its waveConfig, merged
// onto the random roll in _waveConfig. One field per act deliberately — an act
// edits its own list without touching another's — and one list here so adding
// an act is a line rather than another branch.
const ACT_MINION_TYPE_FIELDS = ['deepMinionTypes', 'songMinionTypes'];

export class Game {
  /**
   * @param {HTMLCanvasElement} canvas
   * @param {{character: object, save: import('../save/SaveManager.js').SaveManager, onReturnToMenu: () => void}} opts
   *   `character` comes from data/characters.js (CharacterSelect); `save` is
   *   the app-lifetime SaveManager instance shared across menu <-> run
   *   transitions so Echoes/meta upgrades never go stale between screens.
   */
  constructor(canvas, {
    character, save, onReturnToMenu, ascension = 0, endless = false,
    disableEchoUpgrades = false, disableShardPerks = false, disablePetBonuses = false,
    // Boss Challenge (see ui/BossSelect.js): a practice fight against a
    // single BOSSES[] entry, chosen instead of the normal full gauntlet
    // march. No wave-defense stage, no herald gauntlet even for The Unmade
    // King, and no Echoes/Shards/leaderboard reward on top (see
    // _onBossDied/_endRun) — otherwise this would just be free, repeatable
    // currency farming against whichever boss is weakest for a build.
    challengeBossIndex = null,
    // Card practice (BULLET-HELL.md §D8, see ui/PatternPracticeMenu.js): a
    // Boss Challenge narrowed to ONE named spell card, played on repeat
    // against a boss that cannot be killed. Danmaku's onboarding unit is the
    // card, not the fight — the fight is where you meet a card once every few
    // minutes. Only meaningful alongside challengeBossIndex; ignored without
    // one, since the name alone doesn't say which boss's tuning to play it at.
    practicePattern = null,
    // The scripted first-run tutorial (systems/TutorialDirector.js): a third
    // mode alongside the gauntlet and Boss Challenge. No waves, no boss, no
    // reward, no leaderboard — it ends by calling onTutorialComplete rather
    // than through _endRun at all.
    tutorial = false,
    onTutorialComplete = null,
    // Act select (data/acts.js): which campaign boss the run starts on. 0 is
    // the normal campaign. Anything higher is a practice start — a catch-up
    // build and no rewards, statistics, leaderboard, or Reprise contribution,
    // for the reasons acts.js spells out.
    startBossIndex = 0,
    // Reprise world buffs (REPRISE.md §5, R2): a snapshot from
    // RepriseClient.getBuffState() — { restoredIds, contributedIds,
    // restoredCount }. Absent (offline / no season) means the identity
    // snapshot below, so a run with no world plays exactly as before Reprise.
    worldState = null,
    // The Undersong Tide (BULLET-HEAVEN.md §H7): one arena, no gauntlet, a
    // continuous 20-minute rising curve, a reaper at the clock. A different
    // stage graph over the same systems — see data/tide.js.
    tide = false,
    // Seeded runs (BULLET-HELL.md §D8, see core/Rng.js and data/seeds.js): a
    // seed string — today's daily or one the player typed — that fixes what this
    // run is MADE of, so the leaderboard finally has a fair comparison frame.
    // Null (every route but the seeded one) means an ordinary unseeded run whose
    // every roll is still a bare Math.random, unchanged.
    seed = null,
  } = {}) {
    // Identifies this continuous play session for the leaderboard (see
    // _endRun's run:ended payload) — generated once here, not in
    // _continueEndless, so every "Continue to Endless" loop clear of the
    // *same* run still reports the same id. Without that, each loop cleared
    // submitted its own brand-new leaderboard row (the server has no
    // dedup — see server/index.js's POST /scores), so one long endless
    // session could flood the board with a dozen+ entries under one name
    // instead of a single row that keeps updating to that run's best score.
    this.runId = crypto.randomUUID();
    // Seeded runs (BULLET-HELL.md §D8). Resolved up here because the systems
    // built further down take their generators from it.
    //
    // ONE stream per domain, each handed to exactly one system (see core/Rng.js
    // for why they can't share): waves to every MinionWaveSystem, cards to every
    // SpawningSystem, level-ups to ProgressionSystem, spoils to SpoilsChest.
    // Anything not in that list — particle jitter, spawn positions, mote
    // scatter, per-kill suicide-bullet chances — deliberately stays on
    // Math.random: a seed pins what the run is made of, not the frames.
    this.seed = normalizeSeed(seed);
    this.rng = createRunRng(this.seed);
    this._waveRng = this.rng.stream('waves');
    this._cardRng = this.rng.stream('cards');
    // Read in several places below (progression, the stage set up at the end
    // of this constructor, _update/_render), so it's resolved first.
    this.isTutorial = tutorial;
    this.isTide = tide;
    this._onTutorialComplete = onTutorialComplete;
    // Clamped so a stale/edited start index can never point past the roster.
    // FULL_ROSTER, not CAMPAIGN_BOSSES: since Act III (ACT3-BIBLE §1) the
    // roster runs past the campaign into the standalone branch's six slots.
    this.startBossIndex = Math.max(0, Math.min(Math.floor(startBossIndex) || 0, FULL_ROSTER.length - 1));
    // Which act this run belongs to (data/acts.js). Everything that used to be
    // derived from "startBossIndex > 0" reads the act's own properties instead,
    // because every act is now a rewarded standalone run and an act-select
    // start is the only way into Acts II and III.
    this.act = actForStartIndex(this.startBossIndex);
    // Every rewardless consequence of an act-select start hangs off this one
    // flag (see _onBossDied and _endRun), so there's a single thing to read
    // when asking "does this run count". No act sets it any more — Boss
    // Challenge is the surviving rewardless mode — but the contract stays.
    this.isPractice = this.act.practice === true;
    // Acts II and III open on a story beat rather than mid-run, so both need a
    // prologue screen of their own before the first wave (see _startStage).
    this.isStandaloneAct = this.act.startIndex > 0;
    this.eventBus = new EventBus();
    // `save`, not `this.save` — that's assigned three lines down.
    this.input = new InputManager(window, { save });
    this.renderer = new Renderer(canvas, save);
    this.camera = new Camera();
    this.save = save;
    this.ascension = getAscension(ascension);
    this.ascensionRules = createAscensionRules(ascension);
    this.rewardMultiplier = getAscensionRewardMultiplier(this.ascension.level);
    this.endless = endless;
    // Per-run opt-out of permanent progression, e.g. for a "clean" run or a
    // fair leaderboard/challenge comparison — reads the save's *current*
    // state everywhere below except reports the neutral/off baseline when
    // the matching flag is set (see _getUpgradeLevel/_isPerkUnlocked and
    // petBonuses just below). Nothing is spent, unlocked, or lost in the
    // save either way; this only changes what this one run sees.
    this.disableEchoUpgrades = disableEchoUpgrades;
    this.disableShardPerks = disableShardPerks;
    this.disablePetBonuses = disablePetBonuses;

    this.character = character;
    // Equipped cosmetics (data/cosmetics.js) resolved once per run — purely
    // visual, so there's no disable-flag gate like the combat brackets have.
    // Each is the resolved COSMETICS entry or null (default look).
    this._cosmetics = {
      skin: COSMETICS[save.getEquippedCosmetic('skin')] ?? null,
      trail: COSMETICS[save.getEquippedCosmetic('trail')] ?? null,
      fx: COSMETICS[save.getEquippedCosmetic('fx')] ?? null,
      hat: COSMETICS[save.getEquippedCosmetic('hat')] ?? null,
    };
    // Owned-pet collection bonuses (data/pets.js) — a third bracket
    // alongside the meta shop and character passive. Computed once per run;
    // pets can only be acquired in the shop, never mid-run.
    this.petBonuses = getPetBonuses(this.disablePetBonuses ? [] : save.equippedPets, save.ownedPets);
    // Reprise world buffs — a FOURTH bracket alongside meta/character/pets
    // (data/repriseWorld.js). Base tier for every restored district, amplified
    // for the ones this player contributed to. Identity when there's no world
    // state (offline / no season), so nothing below changes for a plain run.
    // Counter-pressure scales enemy HP/damage up slightly per restored
    // district — the season's "world better, foes worse" arc (REPRISE.md §5).
    this.worldBonuses = getWorldBonuses(worldState?.restoredIds ?? [], worldState?.contributedIds ?? []);
    this.worldEnemyMultiplier = getCounterPressure(worldState?.restoredCount ?? 0);
    this.player = new Player(this.eventBus, this._playerBonuses(character));
    this.player.incomingDamageMultiplier = this.ascensionRules.enemyDamageMultiplier * this.worldEnemyMultiplier;
    if (this.ascensionRules.frailty) {
      this.player.maxHp *= 0.8;
      this.player.hp = this.player.maxHp;
    }

    this.combat = new CombatSystem(this.eventBus);
    const startingWeapon = character.createStartingWeapon();
    // Foundry (Ashen Ward) — restored districts can start weapons a level or
    // two up (REPRISE.md §5). Applied before addWeapon wires it in.
    if (this.worldBonuses.weaponStartLevel > 0) startingWeapon.levelUp(this.worldBonuses.weaponStartLevel);
    this.combat.addWeapon(startingWeapon);
    this.combat.damageMultiplier =
      (1 + META_UPGRADES.damage.perLevel * this._getUpgradeLevel('damage'))
      * character.passive.damageMultiplier
      * this.petBonuses.damageMultiplier
      * this.worldBonuses.damageMultiplier
      * this._equinoxMultiplier();
    // Fire's burn stat (common→epic fire pets) + Solaris's Ignite legendary
    // both bank a fraction of each hit as an ignite burn (see CombatSystem).
    this.combat.ignitePercent = this.petBonuses.petBurnPercent
      + 0.18 * (this.petBonuses.uniques.get('ignite') ?? 0);
    // Rainbow Corona (searingAura) — a standing ring of flame around the
    // player, a pet-owned second aura slot merged with any champion aura.
    this.combat.petAura = this.petBonuses.uniques.get('searingAura')
      ? { radius: 95, damagePerSecond: 25 * this.petBonuses.uniques.get('searingAura') }
      : null;
    // Rainbow Lance (piercingGale) — every player shot pierces +1/+2 enemies.
    this.combat.pool.bonusHits = Math.round(this.petBonuses.uniques.get('piercingGale') ?? 0);
    // Rainbow Nemesis (retribution) — taking a hit blasts nearby enemies. Rides
    // player:damaged, which only fires on a hit that actually landed (past
    // i-frames/dodge/plates) and is throttled by the post-hit mercy window, so
    // a crowded tick can't chain-detonate it.
    const retributionMult = this.petBonuses.uniques.get('retribution') ?? 0;
    if (retributionMult > 0) {
      this.eventBus.on('player:damaged', () => {
        this.combat.applyAreaDamage({
          x: this.player.x, y: this.player.y, radius: 110, damage: 25 * retributionMult, player: this.player,
        });
      });
    }
    // --- level-up cards that need an event, not just a flag ---------------
    // All three are dormant until their card is picked (the guard inside each
    // listener), so they cost one no-op comparison per event otherwise. They
    // hang off the same events the pet uniques above use, deliberately.
    //
    // Reprisal: the card-scale Retribution. Same player:damaged reasoning —
    // only fires on a hit that actually landed, throttled by the mercy window.
    this.eventBus.on('player:damaged', () => {
      if (!(this.player.reprisalDamage > 0)) return;
      this.combat.applyAreaDamage({
        x: this.player.x, y: this.player.y, radius: 90, damage: this.player.reprisalDamage, player: this.player,
      });
    });
    // Cascade: a killed minion detonates. `_cascading` stops a detonation
    // that kills something else from detonating again — the chain would be
    // fun exactly once and unbounded every time after.
    this._cascading = false;
    this.eventBus.on('minion:died', ({ x, y }) => {
      if (!(this.player.killBlastDamage > 0) || this._cascading) return;
      this._cascading = true;
      this.combat.applyAreaDamage({
        x, y, radius: CASCADE_RADIUS, damage: this.player.killBlastDamage, player: this.player,
      });
      this.eventBus.emit('ultimate:shockwave', { x, y, radius: CASCADE_RADIUS, color: '#ff9f5a' });
      this._cascading = false;
    });
    // A boss phase break bursts motes instead of the boss paying one lump at
    // death (BULLET-HEAVEN.md §H1). This is the one place a boss fight becomes
    // a harvest, and it's deliberately the moment it already pauses to change
    // gear: the player gets a few seconds where sweeping the floor is the
    // right play, then the new phase's pattern starts. It costs nothing in
    // difficulty — no telegraph changes, no HP moves — and it gives the fight
    // an in-run reward beat it did not have.
    //
    // Since BULLET-HELL.md §D2 the burst is *paid out of the bullets that were
    // on screen* — every live bullet is cancelled and converted (see
    // `_cancelBullets`), so breaking a phase inside a dense pattern is worth
    // more than breaking it in a lull, and the board is clear when the new
    // phase's vocabulary is introduced. The fixed ring below survives only as
    // a floor for the break that lands on a near-empty screen.
    this.eventBus.on('boss:phase-change', ({ boss }) => {
      if (!boss || boss.bossDef?.isMiniboss) return; // heralds are a duel, not a harvest
      const converted = this._cancelBullets(boss);
      const { min, max, xpEach, spread } = MOTES.bossPhaseBurst;
      const rolled = min + Math.floor(Math.random() * (max - min + 1));
      const count = Math.max(0, rolled - converted);
      for (let i = 0; i < count; i++) {
        // Scattered on a ring around the boss rather than dropped on it: the
        // point is to pull the player through the space the fight just
        // vacated, not to reward standing on the thing that's about to fire.
        const angle = (i / count) * Math.PI * 2 + Math.random() * 0.4;
        const dist = spread * (0.55 + Math.random() * 0.45);
        this.eventBus.emit('mote:dropped', {
          x: Math.min(Math.max(boss.x + Math.cos(angle) * dist, ARENA.x + 4), ARENA.x + ARENA.width - 4),
          y: Math.min(Math.max(boss.y + Math.sin(angle) * dist, ARENA.y + 4), ARENA.y + ARENA.height - 4),
          xp: xpEach * this.progression.xpGainMultiplier,
          tier: 'large',
        });
      }
    });

    // Card HP break (BOSS-REWORK.md §B6). Damage the player lands while a
    // breakable card is running is credited to that card, and a card whose
    // budget is met ends early and counts as a capture — so dodging well and
    // hitting hard stop being two unrelated activities sharing a clock.
    //
    // Only the primary boss's scheduler: `boss:damaged` is only ever the
    // gauntlet boss (heralds and the Echo ghost route through minion:damaged),
    // and a herald pair is a duel rather than a card-break fight.
    this.eventBus.on('boss:damaged', ({ amount }) => {
      this.spawning?.creditDamage(amount);
    });

    // §B9 — an answerable bullet the player shot down pays out through the
    // same conversion a phase break uses. One bullet, one mote: the payoff is
    // the point, but the card's whole board should not be worth more broken
    // than dodged.
    this.eventBus.on('bullet:destroyed', ({ bullet, x, y }) => {
      this.eventBus.emit('mote:dropped', {
        x, y, tier: bulletCancelTier(bullet),
        xp: bulletCancelXp(bullet) * this.progression.xpGainMultiplier * this.grazeChain.multiplier,
      });
      this.particles.burst(x, y, {
        count: 4, color: '#8ad1ff', life: 0.3, speedMin: 30, speedMax: 110, size: 1.3, additive: true,
      });
    });

    // The ultimate is the other half of §D2: firing it clears the screen and
    // converts the curtain, which is what makes it a danmaku *bomb* rather
    // than a damage window with an invulnerability rider. It costs a full
    // meter, so unlike the phase break it is a decision — and the better the
    // moment you pick, the more it pays, which is the only kind of skill
    // expression a bomb button can have.
    this.eventBus.on('ultimate:activated', () => {
      this._cancelBullets(this.player);
    });

    // A captured spell card pays out here (BULLET-HELL.md §D1). CaptureTracker
    // decided that it happened and priced it; this is the part that needs the
    // run's actual systems. The Glimmer half is already banked on the tracker
    // and lands at run end with the graze payout.
    this.eventBus.on('boss:pattern-captured', ({ charge, moteCount, moteXp, desperation }) => {
      this._runCaptures++;
      this.ultimate.addCharge(charge);
      if (moteCount <= 0 || moteXp <= 0) return;
      // Around the PLAYER, not the boss — unlike the phase-break harvest, the
      // point of this burst isn't to pull the player anywhere. They just earned
      // it exactly where they were standing, and a card can be captured off a
      // herald halfway across the arena.
      const xpEach = (moteXp / moteCount) * this.progression.xpGainMultiplier;
      for (let i = 0; i < moteCount; i++) {
        const angle = (i / moteCount) * Math.PI * 2 + Math.random() * 0.5;
        const dist = 12 + Math.random() * 16;
        this.eventBus.emit('mote:dropped', {
          x: Math.min(Math.max(this.player.x + Math.cos(angle) * dist, ARENA.x + 4), ARENA.x + ARENA.width - 4),
          y: Math.min(Math.max(this.player.y + Math.sin(angle) * dist, ARENA.y + 4), ARENA.y + ARENA.height - 4),
          xp: xpEach,
          tier: desperation ? 'large' : 'medium',
        });
      }
    });

    // The wave stage's payout, at the stage clear rather than per wave, so it
    // lands right before a boss fight.
    this.eventBus.on('wave:cleared', () => {
      // Nothing stranded by a stage transition, and — not incidentally — the
      // genre's signature image: the whole floor streaming in at once, firing
      // back-to-back level-ups right as the boss walks on.
      this.pickups.vacuumAll();

      // Spoils of War (the level-up card) still pays its own heal on top —
      // that's a card the player spent a pick on, not the stage's baseline.
      const percent = this.player.waveClearHealPercent ?? 0;
      if (percent > 0 && this.player.hp > 0) {
        const before = this.player.hp;
        this.player.hp = Math.min(this.player.maxHp, this.player.hp + this.player.maxHp * percent);
        this.eventBus.emit('player:healed', { amount: this.player.hp - before, source: 'waveClear' });
      }

      // No chest here any more (BULLET-HEAVEN.md §H3): the Spoils chest is
      // now a physical drop the BOSS leaves behind — see _dropSpoilsChest.
      // The stage clear keeps the vacuum and the heal, and the torrent of
      // motes now streams in immediately (nothing pauses over it) as the
      // boss walks on, which is the beat that image was always for.
    });

    this.combat.dashAttack = character.dashAttack ?? null;
    // Champion signature halves that resolve at hit time (see
    // data/characters.js) — the player-state halves went in through
    // _playerBonuses above. Both null for champions without them.
    this.combat.aura = character.signature?.aura ?? null;
    this.combat.dashChain = character.signature?.dashChain ?? null;
    // Charged Space button, unique per champion (data/ultimates.js). Needs no
    // perk unlock — it's core to the champion, not a meta purchase. Firing it
    // also grants the generic combat surge that used to be Awakening's.
    // Graze chain (BULLET-HELL.md §D5). Built BEFORE the ultimate on purpose:
    // both listen to `bullet:grazed`, and the chain has to have counted the
    // graze before the meter prices it, or every graze would be paid at the
    // multiplier the PREVIOUS one earned.
    this.grazeChain = new GrazeChain(this.eventBus);
    // Resonance (BULLET-HELL.md §D7) — the polarity payoff. Same ordering
    // argument as the chain above: it turns `bullet:absorbed` into a priced
    // `polarity:absorbed` that the meter listens for, so it has to exist first.
    this.resonance = new Resonance(this.eventBus);
    // Tine's Attunement (BULLET-HELL.md §D7's champion promotion) — the run-level
    // half of the signature. `_toneCaster` is stamped onto every SpawningSystem's
    // pool as it is built (_attachToneCaster), and her shorter fuse is handed to
    // the chain here so the readout and the release agree from the first bullet.
    this._attuneConfig = character.signature?.attune ?? null;
    this._toneCaster = this._attuneConfig ? createToneCaster(this._attuneConfig) : null;
    if (this._attuneConfig?.stacksPerRelease) {
      this.resonance.stacksPerRelease = this._attuneConfig.stacksPerRelease;
    }
    this.eventBus.on('polarity:release', ({ x, y }) => this._firePolarityRelease(x, y));
    this.ultimate = new UltimateSystem(this.eventBus, {
      player: this.player,
      combat: this.combat,
      ultimate: character.ultimate ?? null,
      grazeChain: this.grazeChain,
    });
    // Deathbomb (BULLET-HELL.md §D3). The two halves the Player can't know
    // on its own: how long a hit hangs, and whether there's a full bar to
    // spend on it. A champion with no ultimate def never has one, so
    // isReady is permanently false and the mechanic quietly doesn't exist
    // for them — which is correct, since there'd be nothing to pay with.
    this.player.deathbombWindow = !this.save.deathbomb ? 0
      // reduceShake, NOT reduceFlash: windowReduced exists to give back the
      // reaction time that damping slow-mo takes away (config/constants.js).
      : (this.save.reduceShake ? DEATHBOMB.windowReduced : DEATHBOMB.window);
    this.player.canDeathbomb = () => this.ultimate.isReady;

    // Spell-card capture (BULLET-HELL.md §D1) — one tracker for the whole run,
    // covering every SpawningSystem it will ever build (boss, second boss,
    // both heralds) because it works purely off the event bus. Owns the
    // records and the banked Glimmer; the payout wiring is below.
    this.captureTracker = new CaptureTracker(this.eventBus);
    // BOSS-REWORK.md §B10 — the player's lifetime per-card capture record,
    // snapshotted once at run start rather than read live: a variant unlocking
    // *during* the fight that unlocked it would change a card the player is
    // in the middle of learning, which is the failure mode §B10 exists to
    // avoid. Every SpawningSystem the run builds is handed this same snapshot.
    this._patternRecords = this.save?.data?.statistics?.patternRecords ?? null;

    // The run fights all of BOSSES in sequence, each preceded by a wave-
    // defense stage (see _startBossStage/_onBossDied) — gauntletIndex/
    // bossDef track which one is currently live/upcoming, `stage` + the
    // presence of `boss`/`spawning` vs. `minionWaves` track which half of
    // that cycle is active. Transitions between the two are instant (no
    // click-through overlay), so `stage` and `boss`/`spawning`/`minionWaves`
    // always change together, atomically, within a single _update() tick.
    this.isChallenge = challengeBossIndex != null;
    // Card practice rides Boss Challenge's rails: same roster index, same
    // catch-up level, same rewardless run:ended branch. All it adds is the
    // one-card library and the unkillable boss.
    this.practicePattern = this.isChallenge ? practicePattern : null;
    this.isCardPractice = this.practicePattern != null;
    // The Tide has no gauntlet to index — it pins straight to its reaper, so
    // every place that reads `bossDef` (the HUD's "X INCOMING" over the
    // twenty-minute bar, the music theme, the arena theme) is already naming
    // the thing at the end of the clock from the first frame. That is the
    // intent: the player should be able to see what is coming for the whole
    // run and spend twenty minutes preparing for it.
    this.gauntletIndex = this.isTide
      ? TIDE.reaperBossIndex
      : (this.isChallenge ? challengeBossIndex : this.startBossIndex);
    // Challenge mode indexes CHALLENGE_ROSTER (the gauntlet bosses plus
    // the challenge-exclusive ones), NOT _bossDefForIndex's
    // BOSSES[i % BOSSES.length] wraparound — indices past the gauntlet
    // would otherwise alias back onto gauntlet bosses. Safe because a
    // challenge run never advances the gauntlet:
    // _onBossDied ends it before the index++/_bossDefForIndex path, waves/
    // endless never run, so this is the only bossDef lookup it ever does.
    this.bossDef = this.isChallenge
      ? this._ascensionScaledDef(CHALLENGE_ROSTER[challengeBossIndex])
      : this._bossDefForIndex(this.gauntletIndex);
    if (this.isTide) {
      // The reaper meets a player who has had twenty uninterrupted minutes to
      // snowball, not one who arrived through a gauntlet that cost them
      // resources — so it comes in harder than the same boss does there.
      this.bossDef = { ...this.bossDef, maxHp: this.bossDef.maxHp * TIDE.reaperHpMultiplier };
    }
    this.player.incomingDamageMultiplier =
      this.ascensionRules.enemyDamageMultiplier * this.worldEnemyMultiplier * this._incomingDamageLoopMultiplier();
    this.stage = 'waves';
    this.boss = null;
    this.spawning = null;
    // Second slot used only during the herald gauntlet (see
    // _spawnMinibossFromQueue) — two heralds now fight at once instead of
    // one at a time. Always both null outside that stage.
    this._secondaryBoss = null;
    this._secondarySpawning = null;
    // Radius of the bullet the focus ring is currently sized for — 0 when no
    // bullet is near enough to matter. See _updateFocusRing.
    this._focusRingBulletRadius = 0;
    // The first MinionWaveSystem is constructed at the very END of this
    // constructor (see below) rather than here: its constructor spawns wave
    // 1 immediately, which emits wave:started — and that needs the audio
    // bindings and JuiceDirector wiring (both registered further down) to
    // already be listening, or the run's first wave starts silently.
    this.minionWaves = null;
    // Owned for the whole run (not recreated per wave/boss stage like
    // minionWaves/spawning are) — pods can appear during either.
    this.pickups = new PickupSystem(this.eventBus);
    // Also run-owned; only populated during boss fights (see _startBossStage).
    this.cover = new CoverSystem(this.eventBus, {
      getPlayer: () => this.player,
      getBoss: () => this.boss,
    });
    // Act III's Tollkeeper eats XP motes inside its radius and pays them back
    // when it dies (see the minion:died handler below). A getter rather than a
    // list, because the live minion set changes every wave.
    this.pickups.getAbsorbers = () => this.combat.minions.filter((m) => m.type === 'tollkeeper' && !m.isDead);
    // Graze as the mote magnet (BULLET-HELL.md §D5) — a live chain widens the
    // pickup radius, so threading the curtain and harvesting the floor become
    // one act instead of two competing ones. The single most valuable property
    // BULLET-HEAVEN.md identifies is an input that negotiates greed against
    // safety; this makes the graze band that input.
    this.pickups.getMagnetBonus = () => this.grazeChain.magnetBonus;
    this.pickups.ascensionHealMultiplier = this.ascensionRules.pickupHealMultiplier;
    this.pickups.healMultiplier = this.petBonuses.podHealMultiplier
      * (1 + 1.0 * (this.petBonuses.uniques.get('regrowth') ?? 0));
    // Rainbow Riptide (riptide) — draws health pods toward the player from
    // anywhere in the arena; 0 leaves pods stationary (body-contact only).
    this.pickups.magnetSpeed = 220 * (this.petBonuses.uniques.get('riptide') ?? 0);
    // Riptide's pod magnet also widens the mote magnet — it already reads as
    // "things on the floor come to you", and BULLET-HEAVEN.md §H1 calls out
    // that pet as the proof the code path works. Motes then give the stat
    // somewhere permanently useful to go, which is the point of introducing
    // `magnetRadius` as a real axis rather than a constant: passives, pets and
    // meta upgrades now all have a lane the game previously had no room for.
    this.player.magnetRadius = (this.player.magnetRadius ?? 0)
      + 26 * (this.petBonuses.uniques.get('riptide') ?? 0);

    // Mythic Sage (fastStudy) — +25%/+50% in-run XP, distinct from Echoes.
    // Zeroed for the tutorial: XP would open a level-up pick screen partway
    // through a lesson about movement, which is exactly the wrong second
    // thing to explain. It's the only lever needed — every XP path
    // (survival, kills, damage) runs through this multiplier.
    const xpGainMultiplier = (this.isTutorial ? 0 : 1)
      * (1 + META_UPGRADES.xpGain.perLevel * this._getUpgradeLevel('xpGain'))
      * (1 + 0.25 * (this.petBonuses.uniques.get('fastStudy') ?? 0));
    this.progression = new ProgressionSystem(this.eventBus, {
      combat: this.combat,
      player: this.player,
      isPerkUnlocked: (id) => this._isPerkUnlocked(id),
      xpGainMultiplier,
      // Ascension IV removes one base choice; Lucky Star is applied after
      // that penalty, so the combination correctly gives three choices.
      levelUpChoices: (this.ascensionRules.levelUpChoices ?? 3)
        + (this.petBonuses.uniques.has('luckyStar') || this.petBonuses.uniques.has('radiantInsight') ? 1 : 0)
        + this.worldBonuses.extraLevelUpChoices, // The Loom (Second Crown)
      rng: this.rng.stream('levelup'), // BULLET-HELL.md §D8
    });
    // Catch-up build for the two modes that don't start at the beginning.
    // MUST stay after `this.progression` and `this.bossDef` exist: this block
    // used to sit further up the constructor, where `this.isChallenge` and
    // `this.progression` were both still undefined, so it silently never ran
    // and every Boss Challenge started at level 1 with a starter weapon
    // against a late-gauntlet boss.
    if (this.isChallenge) {
      this.challengeStartLevel = challengeLevelForBoss(this.bossDef);
      this.progression.prepareChallenge(this.challengeStartLevel);
    } else if (this.startBossIndex > 0) {
      // Any act-select start hands over the build a player would plausibly
      // arrive with, by the same route (data/acts.js's actStartLevel) — the
      // rewarded Act III branch included, whose whole HP staircase is tuned
      // against exactly this arrival state (ACT3-BIBLE §9).
      this.actStartLevel = actStartLevel(this.act);
      this.progression.prepareChallenge(this.actStartLevel);
    }
    this.echoGainMultiplier =
      (1 + META_UPGRADES.echoGain.perLevel * this._getUpgradeLevel('echoGain'))
      * this.petBonuses.echoGainMultiplier
      * this.worldBonuses.echoGainMultiplier;
    this.particles = new ParticleSystem();
    // Death recap (ARCHIVE.md P4) — a pure player:damaged listener, so it has
    // to exist before anything can hurt the player, but needs nothing wired
    // into it afterwards.
    this.damageLedger = new DamageLedger(this.eventBus);
    audio.bindRunEvents(this.eventBus);

    const uiRoot = document.getElementById('ui-root');
    this.levelUpPicker = new LevelUpPicker(this.eventBus, this.progression, uiRoot);
    // Act III's two in-fight prompts. Both are pure presentation over an event
    // pair, the same contract LevelUpPicker has with ProgressionSystem, so the
    // systems that own the rules never touch the DOM.
    this.levyPrompt = new LevyPrompt(this.eventBus, uiRoot);
    this.versePicker = new VersePicker(this.eventBus, uiRoot);
    // The Spoils chest (BULLET-HEAVEN.md §H3). Takes its build context through
    // a getter rather than by value: it's constructed once per run but opens
    // four-plus times, and what it can pay out depends on what the run looks
    // like at that moment, not at construction.
    this.spoilsChest = new SpoilsChest(this.eventBus, uiRoot, {
      save: this.save,
      rng: this.rng.stream('spoils'), // BULLET-HELL.md §D8

      getContext: () => ({
        combat: this.combat,
        player: this.player,
        progression: this.progression,
      }),
    });
    this.runEndOverlay = new RunEndOverlay(this.eventBus, uiRoot, {
      onReturnToMenu,
      // Endless loops ACT I specifically — it advances gauntletIndex and walks
      // BOSSES on repeat, which is a continuation only for the act that ends
      // where the loop begins. Withheld from the Tide, whose whole shape is
      // "twenty minutes, then a reaper" and which has no gauntlet to advance;
      // and from Acts II and III, where it would drop an act-II/III build into
      // Act I's court at whatever roster index the run happened to stop on.
      // Null makes RunEndOverlay omit the button entirely.
      onContinueEndless: (this.isTide || this.isStandaloneAct) ? null : () => this._continueEndless(),
      save: this.save,
    });
    this.pauseOverlay = new PauseOverlay(this.eventBus, uiRoot, {
      onResume: () => this._setUserPause(false),
      // onReturnToMenu (main.js's showMainMenu) destroys this Game and
      // clears all run overlays, including the pause screen itself.
      onExit: () => onReturnToMenu(),
    });
    this.finalWarningOverlay = new FinalWarningOverlay(this.eventBus, uiRoot, { onContinue: () => this._beginFinalWaveStage() });
    this.actOpeningOverlay = new ActOpeningOverlay(this.eventBus, uiRoot, {
      onContinue: () => { this._autoPause = false; this._syncPaused(); },
    });
    this.surviveOverlay = new SurviveOverlay(this.eventBus, uiRoot);
    // No-op (mounts nothing) unless isMobileModeActive(save) — see core/deviceMode.js.
    this.touchControls = new TouchControls(this.input, uiRoot, { save });

    // Simulation pauses (but rendering keeps running) during a level-up
    // pick, while the player-triggered pause is active, and permanently
    // once the run ends — see _update() and _syncPaused().
    this.paused = false;
    this.runEnded = false;
    this._autoPause = false;
    this._userPause = false;

    // Registered before the stage-transition listener below so a boss
    // death's particle burst (which reads this.boss.x/y via getBoss) always
    // runs while this.boss is still the just-killed boss, not the null
    // _onBossDied leaves behind once it moves the gauntlet on to the next
    // wave stage. JuiceDirector owns every cosmetic event reaction (shake/
    // hit-stop/slow-mo/particles/numbers) plus the ephemeral state the
    // _draw* methods read — rendering itself stays in this file.
    this.juice = new JuiceDirector(this.eventBus, {
      camera: this.camera,
      save: this.save,
      particles: this.particles,
      getPlayer: () => this.player,
      getBoss: () => this.boss,
      // Equipped FX cosmetic (data/cosmetics.js) recolors the level-up and
      // boss-kill bursts; null keeps the default per-event colors.
      fxColor: this._cosmetics.fx?.color ?? null,
    });

    // Gameplay halves of two events whose cosmetic halves live in
    // JuiceDirector: a Bomber's death burst damages the player, and a
    // Splitter's death spawns its children. Both need live Game references
    // (player / combat), which is why they're here rather than inside the
    // entity or the wave system.
    this.eventBus.on('minion:exploded', ({ x, y, damage, radius }) => {
      const dist = Math.hypot(this.player.x - x, this.player.y - y);
      if (dist < radius + this.player.radius) this.player.takeDamage(damage, 'bomberBlast');
    });
    this.eventBus.on('minion:split', ({ children }) => {
      for (const config of children) this.combat.addMinion(new Skirmisher(this.eventBus, config));
    });

    this.eventBus.on('xp:levelup:open', () => { this._autoPause = true; this._syncPaused(); });
    this.eventBus.on('xp:levelup:closed', () => { this._autoPause = false; this._syncPaused(); });
    // The Spoils chest rides the same autopause flag as the level-up pick
    // (BULLET-HEAVEN.md §H3) — same contract, same single unpause event. The
    // two can never overlap even though a boss kill now grants a guaranteed
    // level AND leaves a chest on the floor: the chest is only collected by
    // the player (or its magnet) moving, and neither runs while a pick screen
    // has the simulation paused.
    this.eventBus.on('spoils:opened', () => { this._autoPause = true; this._syncPaused(); });
    this.eventBus.on('spoils:closed', () => { this._autoPause = false; this._syncPaused(); });
    // …and only then does a queued second chest go up. Registered after the
    // unpause listener above so the reopen's own spoils:opened wins the flag.
    this.eventBus.on('spoils:closed', () => this._openNextSpoils());
    // The Verse's card hand (Act III) rides the same flag for the same reason:
    // it is a pick screen, and picking which way a boss escalates while it is
    // still shooting at you would be a reflex test rather than a decision.
    // The Tithe's levy prompt deliberately does NOT pause — paying is meant to
    // cost you position and attention, and its Arrears timer is a real clock.
    this.eventBus.on('verse:offer', () => { this._autoPause = true; this._syncPaused(); });
    this.eventBus.on('verse:picked', () => { this._autoPause = false; this._syncPaused(); });
    // Tollkeeper (Act III): everything it swallowed is paid back where it died,
    // as real motes, so the run's economy is delayed rather than destroyed.
    this.eventBus.on('minion:died', ({ x, y, minion }) => {
      if (!(minion?.bankedXp > 0)) return;
      this.pickups.dropMote({ x, y, xp: minion.bankedXp, tier: 'large' });
      minion.bankedXp = 0;
    });
    this.eventBus.on('boss:spawn', ({ name }) => this._showBossTitle(name));
    // Heralds don't get the big title-card treatment — with two of them
    // up at once (see _spawnMinibossFromQueue) a title card per spawn would
    // mean two competing banners, and their HP bars/names are already
    // visible in the HUD. The boss:pattern-start telegraph callout (now
    // handled in JuiceDirector) still fires for either, so a herald's
    // attack callout keeps working for free.
    this.eventBus.on('boss:died', () => this._onBossDied());
    this.eventBus.on('miniboss:died', ({ boss }) => this._onMinibossDied(boss));
    this.eventBus.on('player:died', () => this._onPlayerDied());
    this.eventBus.on('bullet:grazed', () => { this._runGrazes++; });

    // --- achievement counters (BULLET-HEAVEN.md §H6) -----------------------
    // Pure tallies over events that already exist. Deliberately here rather
    // than inside the systems that emit them: none of these change behaviour,
    // and a system that starts counting things for a menu has acquired a
    // second job.
    this._runMotes = 0;
    this._runBestMoteBurst = 0;
    this._runFusionIds = [];
    this._runPodsCollected = 0;
    this._runDamageTaken = 0;
    this._runChestsOpened = 0;
    this._runBestLateChest = 0;
    this._runCaptures = 0; // spell cards captured this run (BULLET-HELL.md §D1)
    this.eventBus.on('mote:collected', ({ count }) => {
      this._runMotes += count;
      this._runBestMoteBurst = Math.max(this._runBestMoteBurst, count);
    });
    this.eventBus.on('weapon:fused', ({ name }) => {
      const def = FUSION_DEFS.find((d) => d.name === name);
      if (def) this._runFusionIds.push(def.id);
    });
    this.eventBus.on('pod:collected', () => { this._runPodsCollected++; });
    this.eventBus.on('player:damaged', ({ amount }) => { this._runDamageTaken += amount ?? 0; });
    this.eventBus.on('spoils:opened', ({ count }) => {
      this._runChestsOpened++;
      // Only chests past the hardcoded generous opening three count toward
      // the Jackpot achievement — the opening 5 is scripted, so rewarding it
      // would be rewarding the player for reaching the third stage.
      if (this._runChestsOpened > OPENING_CHEST_ROLLS.length) {
        this._runBestLateChest = Math.max(this._runBestLateChest, count);
      }
    });

    this._accumulator = 0;
    this._lastTime = performance.now();
    this._running = false;
    this._rafId = null;
    this._shardsEarnedThisRun = 0;
    this._shardRewardRemainder = 0;
    this._runGrazes = 0;
    // Per-run (not persisted) — the Second Wind perk (see shardUnlocks.js)
    // only revives once per run, regardless of how many times it's owned.
    this._extraLifeUsed = false;
    this._extraLifeGraceRemaining = 0;
    // Echo Duel (data/echoBoss.js): the opt-in rift pickup and its ghost.
    // The set gates one rift offer per eligible gauntlet index, so endless
    // loops re-offer once per loop rather than every wave stage.
    this._echoRift = null;
    this._echoDuelsWon = 0;
    this._echoRiftSeenIndices = new Set();
    // The Spoils chest a boss left on the floor (BULLET-HEAVEN.md §H3), and
    // the stage indices of chests collected but not yet shown. The queue is
    // only ever 0 or 1 deep in practice — see _dropSpoilsChest — but it is
    // what makes "a collected chest is always paid" true by construction
    // rather than by argument.
    this._spoilsDrop = null;
    this._spoilsQueue = [];
    this._bossStageTime = 0;
    this._arenaPulseTimer = 12;
    this._bossEnraged = false;
    // True only while the final stage's herald gauntlet (see data/bosses.js's
    // MINIBOSSES) is up — this.stage stays 'boss' the whole time (it reuses
    // that stage's entire render/update path via this.boss/this.spawning),
    // this flag is just what tells _onMinibossDied apart from a real
    // boss:died handoff. _minibossQueueIndex tracks which of the four
    // heralds is currently up (see _startMinibossStage/_spawnMinibossFromQueue).
    this._isMinibossStage = false;
    this._minibossQueueIndex = 0;

    // Last, so wave 1's wave:started emission is heard by the audio/juice
    // listeners registered above — see the comment at the null init.
    // Boss Challenge skips the wave-defense stage entirely and drops
    // straight into the fight instead.
    if (this.isTutorial) {
      // Its own stage: no waves to clear, no boss to reach, and no _endRun
      // path — the tutorial finishes by handing control straight back to the
      // caller (main.js), which is what keeps it out of the reward, stats,
      // and leaderboard machinery entirely.
      this.stage = 'tutorial';
      this.tutorial = new TutorialDirector(this.eventBus, this.combat, { player: this.player });
      this.tutorialOverlay = new TutorialOverlay(uiRoot, { onSkip: () => this.skipTutorial() });
      this.eventBus.on('tutorial:complete', () => {
        this.save.markTutorialSeen();
        this._onTutorialComplete?.();
      });
    } else if (this.isChallenge) {
      this._startBossStage();
    } else {
      this.minionWaves = new MinionWaveSystem(this.eventBus, this.combat, this._waveConfig(), { rng: this._waveRng });
      // Acts II and III open on their prologue, held paused until the player
      // clicks through. Act II's used to arrive mid-run on the descent
      // interstitial, because the King's fall was literally its doorway; now
      // that both acts are entered from the menu, neither prologue would ever
      // be read without a screen of its own (ACT2-BIBLE §1, ACT3-BIBLE §2).
      const opening = ACT_OPENINGS[this.act.id];
      if (this.isStandaloneAct && opening) {
        this.stage = 'waves';
        this._autoPause = true;
        this._syncPaused();
        this.eventBus.emit('act:opening', opening);
      }
    }
  }

  /**
   * Called by the tutorial's Skip button — same terminal state as playing it
   * through, because the question the flag answers is "has this player been
   * offered the controls", not "did they complete a drill".
   */
  skipTutorial() {
    this.save.markTutorialSeen();
    this._onTutorialComplete?.();
  }

  /**
   * Clears the field at a stage transition, paying out first.
   *
   * Removing a minion is not killing it, so no kill bonus is owed — but the
   * damage-XP already banked on it (BULLET-HEAVEN.md §H1) is XP the player
   * genuinely earned, and since the ramp ends on a clock rather than on a
   * clear (§H2) a stage now routinely expires with a full field of
   * half-damaged bodies. Dropping their banked XP as motes is what keeps
   * "ends on the clock" from quietly meaning "loses you the last ten seconds
   * of work". The motes land into the vacuum wave:cleared already triggered.
   */
  _sweepMinions() {
    this.progression?.flushBankedXp(this.combat.minions);
    for (const m of [...this.combat.minions]) this.combat.removeMinion(m);
  }

  _waveConfig() {
    // The Tide is one stage that lasts the whole run, so its curve comes from
    // data/tide.js rather than from a boss's waveConfig. Everything below
    // (ascension reinforcement, the random type roll, the per-act vocabulary
    // merges) is gauntlet shaping that the Tide deliberately does not want:
    // its type list is authored, and its ramp is the mode.
    if (this.isTide) {
      return tideWaveConfig({ loopMultiplier: this._endlessLoopMultiplier() });
    }
    const config = { ...this.bossDef.waveConfig };
    if (this.ascensionRules.reinforcedWaves) {
      config.minionsPerWave *= 2;
    }
    // Hard cap regardless of endless-loop dampening or reinforcedWaves —
    // keeps a single wave's minion count from becoming a performance/
    // readability problem at high loop counts.
    config.minionsPerWave = Math.min(config.minionsPerWave, 40);
    // Rerolled per wave stage (called once per boss/miniboss transition) —
    // stays fixed across all of that stage's waves, then reshuffles for the
    // next one, so the mix varies run to run instead of matching a curated
    // per-boss list.
    config.minionTypes = randomMinionTypeSet(this._waveRng);
    // Act II and Act III bosses foreshadow themselves: each declares the
    // creature that echoes its own texture (ACT2-BIBLE.md §8, ACT3-BIBLE.md
    // §8), merged ON TOP of the random roll rather than replacing it — so the
    // act keeps its wave-to-wave variety and still teaches the boss's rule
    // before the boss asks for it.
    //
    // One loop over ACT_MINION_TYPE_FIELDS rather than a branch per act. The
    // fields stay separate, which is the isolation they were split for; what
    // this removes is the copy-paste, so Act IV's vocabulary is a name in that
    // list instead of a third identical branch.
    for (const field of ACT_MINION_TYPE_FIELDS) {
      const declared = this.bossDef.waveConfig[field];
      if (declared) config.minionTypes = [...config.minionTypes, ...declared];
    }
    // Wave-stage shape (BULLET-HEAVEN.md §H2) — read fresh here, per stage, so
    // flipping the setting mid-run takes effect at the next wave stage rather
    // than reshaping the one already in progress.
    //
    // Never in the tutorial: that stage is scripted around a known number of
    // bodies arriving in a known order, and a spawn curve would fight it.
    config.rampMode = this.save.waveRamp && !this.isTutorial;
    // Suicide bullets (BULLET-HELL.md §D6) — Act II onward only. Act I is the
    // on-ramp and the only act that has to teach "walk into the crowd and
    // shoot"; a wave half that answers a kill with bullets is a second rule on
    // top of that one, and it belongs where the player already has a dash, an
    // ultimate and a bullet-cancel to spend on it. Never in the tutorial, for
    // the same reason rampMode isn't: that stage is scripted.
    //
    // The Tide sets its own config above and deliberately opts out: its whole
    // premise is a screen kept full at a density where clear rate is already
    // the only variable, and adding a per-death bullet tax there would be a
    // flat difficulty increase rather than the self-balancing pressure this is.
    config.suicideBullets = this.act.id !== 'act1' && !this.isTutorial;
    return config;
  }

  // The shared per-loop rate for every enemy's own HP — boss/miniboss
  // maxHp below. Minion HP no longer has its own per-loop term (see
  // _bossDefForIndex) — only the within-stage wave ramp (MinionWaveSystem's
  // _waveScale) and each minion type's fixed multiplier vary it now.
  // player.incomingDamageMultiplier has its own separate rate — see
  // _incomingDamageLoopMultiplier.
  _endlessLoopMultiplier(index = this.gauntletIndex) {
    const loop = this.endless ? Math.floor(index / BOSSES.length) : 0;
    return Math.pow(1.2, loop);
  }

  // player.incomingDamageMultiplier's per-loop rate (constructor/
  // _continueEndless) — scales ALL damage the player takes, boss/miniboss/
  // minion alike. Deliberately its own rate, distinct from the HP-side
  // _endlessLoopMultiplier above.
  _incomingDamageLoopMultiplier(index = this.gauntletIndex) {
    const loop = this.endless ? Math.floor(index / BOSSES.length) : 0;
    return Math.pow(1.1, loop);
  }

  // Ascension's universal enemy-stat climb (+20%/level, up to 3x at A10 —
  // see data/ascensions.js). Scales enemy HP: boss/miniboss maxHp and each
  // wave's minionHp. Enemy damage rides its own path via
  // player.incomingDamageMultiplier (enemyDamageMultiplier below). Applied on
  // top of any endless-loop scaling, and independent of it.
  _ascensionScaledDef(def) {
    // Reprise counter-pressure rides the same enemy-HP path as Ascension —
    // one combined multiplier so boss/miniboss/minion HP all pick it up.
    const m = this.ascensionRules.enemyStatMultiplier * this.worldEnemyMultiplier;
    if (m === 1) return def;
    const scaled = { ...def, maxHp: def.maxHp * m };
    if (def.waveConfig?.minionHp != null) {
      scaled.waveConfig = { ...def.waveConfig, minionHp: def.waveConfig.minionHp * m };
    }
    return scaled;
  }

  _bossDefForIndex(index) {
    // Endless loops Act I only (BOSSES, wrapping); it never has a loop
    // multiplier > 1 off that path, so a non-endless run always takes the
    // simple branch just below.
    // FULL_ROSTER is every act's slots end to end (data/acts.js). Each run
    // walks only from its own act's startIndex to its own endIndex (see
    // campaignFlow.js), so no act ever indexes into another's bosses.
    const base = this.endless
      ? BOSSES[index % BOSSES.length]
      : FULL_ROSTER[Math.min(index, FULL_ROSTER.length - 1)];
    const multiplier = this._endlessLoopMultiplier(index);
    if (multiplier === 1) return this._ascensionScaledDef(base);
    // minionHp is left unscaled here (no dedicated per-loop multiplier) —
    // its base value already carries the flat 0.75 balance compression
    // (see data/bosses.js). minionContactDamage/minionShotDamage are also
    // left unscaled — both still ramp per loop for free via
    // player.incomingDamageMultiplier above, same mechanism boss/miniboss
    // pattern damage relies on. Speed, fire rate, and wave size stay on the
    // loop-based/dampened curve (endless-only) — otherwise a dozen endless
    // loops turns every minion into something too fast to react to on top
    // of already hitting harder.
    const dampen = (rate) => 1 + (multiplier - 1) * rate;
    return this._ascensionScaledDef({
      ...base,
      maxHp: base.maxHp * multiplier,
      waveConfig: {
        ...base.waveConfig,
        minionMoveSpeed: base.waveConfig.minionMoveSpeed * dampen(0.4),
        minionShotSpeed: base.waveConfig.minionShotSpeed * dampen(0.4),
        minionFireInterval: base.waveConfig.minionFireInterval / dampen(0.3),
        minionsPerWave: Math.round(base.waveConfig.minionsPerWave * dampen(0.5)),
      },
    });
  }

  /**
   * Same endless-loop HP scaling as _bossDefForIndex, so a herald doesn't
   * trivialize in later endless loops. Takes the herald QUEUE index (0..
   * MINIBOSSES.length-1) and applies the CURRENT loop's multiplier (the
   * herald stage runs during the final boss's own stage, so
   * this.gauntletIndex is always in the right loop) — deliberately not
   * derived from gauntletIndex arithmetic, since MINIBOSSES and BOSSES are
   * different lengths now (four founding-court heralds vs. seven gauntlet
   * bosses — see data/bosses.js).
   */
  /**
   * The guard set standing in front of the current boss, or null if it has
   * none. Act I's Unmade King uses the default four (data/bosses.js
   * MINIBOSSES); the Undersong declares its own three Heralds of the Deep on
   * its def (`minibosses`, see data/act2Bosses.js). Everything downstream
   * reads the roster through here, so a roster of any length works.
   */
  _minibossRoster() {
    if (this.bossDef?.minibosses) return this.bossDef.minibosses;
    return this.bossDef?.isFinalBoss ? MINIBOSSES : null;
  }

  _minibossDefForIndex(queueIndex) {
    const roster = this._minibossRoster();
    const base = roster[queueIndex % roster.length];
    const multiplier = this._endlessLoopMultiplier();
    if (multiplier === 1) return this._ascensionScaledDef(base);
    return this._ascensionScaledDef({ ...base, maxHp: base.maxHp * multiplier });
  }

  /** Fires on player HP hitting 0 — consumes Second Wind if available, otherwise ends the run as a loss. */
  _onPlayerDied() {
    if (this.ascensionRules.reviveEnabled && this._isPerkUnlocked('extraLife') && !this._extraLifeUsed) {
      this._extraLifeUsed = true;
      this.player.hp = this.player.maxHp;
      this.player.isInvincible = true;
      this._extraLifeGraceRemaining = 2; // seconds of grace i-frames after reviving
      this.eventBus.emit('player:revived');
      return;
    }
    this._endRun(false);
  }

  /** Combines the chosen character's passive with permanent meta upgrades (Echoes shop), Shard perks, and owned-pet bonuses (data/pets.js). */
  _playerBonuses(character) {
    const pets = this.petBonuses;
    // Meta shop maxHp is a flat bonus; earth pets are now a PERCENTAGE of max
    // HP, folded into the multiplier alongside Equinox.
    const maxHpBonus = META_UPGRADES.maxHp.perLevel * this._getUpgradeLevel('maxHp')
      + character.passive.maxHpBonus;
    const moveSpeedMultiplier =
      (1 + META_UPGRADES.moveSpeed.perLevel * this._getUpgradeLevel('moveSpeed'))
      * character.passive.moveSpeedMultiplier
      * pets.moveSpeedMultiplier
      * this.worldBonuses.moveSpeedMultiplier; // Second Crown
    // Meta shop's dash-cooldown is a flat second reduction; frost pets +
    // Permafrost are a PERCENTAGE of the base cooldown (Player applies the pct
    // to PLAYER.dashCooldown first, then subtracts this flat meta reduction).
    const dashCooldownReduction = META_UPGRADES.dashCooldown.perLevel * this._getUpgradeLevel('dashCooldown');
    const dashCooldownPct = pets.dashCooldownPct + 0.30 * (pets.uniques.get('permafrost') ?? 0);
    const dashRangeMultiplier = character.passive.dashRangeMultiplier;
    const maxDashCharges = this._isPerkUnlocked('doubleDash') ? 2 : 1;
    const dodgeChance = this._isPerkUnlocked('dodgeChance') ? SHARD_UNLOCKS.dodgeChance.value : 0;
    // Meta shop's HP regen is a flat HP/sec; water pets are a PERCENTAGE of max
    // HP per second (Player sums both).
    // Long Table (Aqueduct/Almshouse) adds flat HP/sec on top of the meta shop's.
    const hpRegenPerSecond = META_UPGRADES.hpRegen.perLevel * this._getUpgradeLevel('hpRegen')
      + this.worldBonuses.hpRegenPerSecond;
    return {
      maxHpBonus,
      // Granary/Kitchens (Long Table) fold into the max-HP multiplier bracket.
      maxHpMultiplier: this._equinoxMultiplier() * pets.maxHpMultiplier * this.worldBonuses.maxHpMultiplier,
      moveSpeedMultiplier,
      dashCooldownReduction,
      dashCooldownPct,
      dashRangeMultiplier,
      maxDashCharges,
      dodgeChance,
      hpRegenPerSecond,
      hpRegenPercent: pets.hpRegenPercent,
      fireRateMultiplier: pets.fireRateMultiplier
        * (1 + 0.20 * (pets.uniques.get('overclock') ?? 0))
        * this.worldBonuses.fireRateMultiplier, // The Measure
      lifestealPercent: 0.10 * (pets.uniques.get('bloodmoon') ?? 0),
      // Stoneskin (earth legendary) is now percent damage reduction (stacks
      // with Bulwark's, capped at 0.9 in takeDamage), not a flat subtraction.
      damageReduction: 0.12 * (pets.uniques.get('stoneskin') ?? 0),
      // Legendary/mythical pet uniques mapped onto Player fields (data/pets.js):
      // Slipstream (post-dash move + dash-range burst, wind legendary),
      // Swarmbane (non-boss dmg), Giant Slayer (boss dmg), Keen Edge (crit),
      // Overdrive (ultimate charge rate; multiplies with Crescendo), Judgment
      // (execute low-HP non-boss enemies), Wellspring (clutch heal, water
      // legendary). Each `?? 0` term is a no-op when that pet isn't equipped.
      postDashMoveBonus: 0.40 * (pets.uniques.get('slipstream') ?? 0),
      postDashDashRangeBonus: 0.50 * (pets.uniques.get('slipstream') ?? 0),
      minionDamageMultiplier: 1 + 0.25 * (pets.uniques.get('swarmbane') ?? 0),
      bossDamageMultiplier: 1 + 0.25 * (pets.uniques.get('giantSlayer') ?? 0),
      petCritChance: 0.12 * (pets.uniques.get('keenEdge') ?? 0),
      petCritDamageBonus: 0.40 * (pets.uniques.get('keenEdge') ?? 0),
      ultimateChargeGainMultiplier: 1 + 0.30 * (pets.uniques.get('overdrive') ?? 0),
      executeThreshold: 0.15 * (pets.uniques.get('judgment') ?? 0),
      wellspringHealPct: 0.08 * (pets.uniques.get('wellspring') ?? 0),
      // Champion signature (data/characters.js) — Player picks out the
      // pieces it owns (Cadence, Aegis plates, crit scaling) and ignores the
      // rest, which CombatSystem takes instead.
      signature: character.signature ?? null,
    };
  }

  _equinoxMultiplier() {
    return 1 + META_UPGRADES.equinox.perLevel * this._getUpgradeLevel('equinox');
  }

  /** Neutral (0/false) when disableEchoUpgrades/disableShardPerks is set for this run — see the constructor. */
  _getUpgradeLevel(id) {
    return this.disableEchoUpgrades ? 0 : this.save.getUpgradeLevel(id);
  }

  _isPerkUnlocked(id) {
    return this.disableShardPerks ? false : this.save.isPerkUnlocked(id);
  }

  _syncPaused() {
    this.paused = this._autoPause || this._userPause || this.runEnded;
  }

  /** Only reachable outside the level-up screen and after the run has ended (see _tick's pause-key poll). */
  _setUserPause(paused) {
    this._userPause = paused;
    this._syncPaused();
    // Snapshot of the run for the pause screen's context strip (ui/PauseOverlay.js).
    this.eventBus.emit(paused ? 'game:paused' : 'game:resumed', paused ? {
      characterName: this.character.name,
      characterColor: this.character.color,
      level: this.progression.level,
      survivalTime: this.progression.survivalTime,
      // What the run has actually been built into — weapons at their live
      // levels, plus every passive/pact picked (see PauseOverlay).
      build: this.progression.buildSummary,
      // Endless counts within the looping Act I gauntlet; a campaign run counts
      // across the whole Act I + Act II sequence (CAMPAIGN_BOSSES).
      bossNumber: this.endless ? (this.gauntletIndex % BOSSES.length) + 1 : this.gauntletIndex + 1,
      bossCount: this.endless ? BOSSES.length : CAMPAIGN_BOSSES.length,
      mode: this.endless
        ? `Endless · Loop ${Math.floor(this.gauntletIndex / BOSSES.length) + 1}`
        : (this.ascension.level ? `Ascension ${this.ascension.level}` : 'Normal'),
    } : undefined);
  }

  /** Fires on every boss death — ends the run on the last boss, otherwise moves on to the next wave stage immediately. */
  _onBossDied() {
    // Everything the phase breaks scattered streams in on the kill (see the
    // boss:phase-change handler in the constructor). A boss fight's harvest
    // should never be left behind by the stage advancing, and arriving all at
    // once on the kill is the right beat for it anyway.
    this.pickups.vacuumAll();

    // Record the kill for the Archive codex's progressive unlock (see
    // ui/LoreMenu.js) — before the challenge early-return below so both
    // gauntlet and challenge-exclusive boss lore unlock on defeat.
    this.save.recordBossDefeated(this.bossDef.spriteKey);
    // The fight's own rules end with the fight — a levy ring still collecting
    // or a card hand still on offer during the next wave stage would be a bug
    // with a UI attached. Safe on every path below, including the ones that end
    // the run outright.
    this._disposeAct3Systems();

    // Deliberately NOT force-resolving the pending pick here: boss:died
    // itself now always grants a guaranteed level (ProgressionSystem's own
    // boss:died handler), so a pick is pending on essentially every boss
    // kill — force-resolving unconditionally would auto-apply it and never
    // let the player actually choose. _endRun() (below, on the run-ending
    // paths) still force-resolves for itself, since there's no time left to
    // show a picker once the run is over.

    // Boss Challenge: one boss, one kill, no gauntlet march — end the run
    // right here regardless of which BOSSES[] index was picked, and skip
    // the Echo/Shard payout below (see the constructor comment on
    // challengeBossIndex for why no reward).
    if (this.isChallenge) {
      this._endRun(true);
      return;
    }

    // The Tide's reaper. Killing it is the run — there is no next stage to
    // advance to, and surviving something that arrives only after twenty
    // minutes of escalation is exactly the win condition the mode is for.
    if (this.isTide) {
      this._endRun(true);
      return;
    }

    // Banked immediately, on top of the cumulative calcEchoes() payout at
    // true run end — every boss kill pays out, not just the run-ending one.
    // Every act pays now that none of them is a shortcut through another
    // (data/acts.js); `isPractice` is left in place because Boss Challenge and
    // any future rewardless route still answer to it.
    if (!this.isPractice) {
      const bossIndex = this.gauntletIndex % BOSSES.length;
      // Three reward curves, picked by where in the roster the kill happened —
      // the slot, not the act the run chose, so this is unchanged by the split:
      // Act III's slots pay from ACT3_*, Act II's from ACT2_*, and Act I plus
      // every endless loop keep the original tables.
      const inActThree = !this.endless && this.gauntletIndex >= ACT3_START_INDEX;
      const act3Index = this.gauntletIndex - ACT3_START_INDEX;
      const inActTwo = !this.endless && !inActThree && this.gauntletIndex >= BOSSES.length;
      const act2Index = this.gauntletIndex - BOSSES.length;
      const echoBonus = inActThree
        ? (ACT3_ECHO_BONUS[act3Index] ?? 0)
        : inActTwo ? (ACT2_ECHO_BONUS[act2Index] ?? 0) : (ECHO_BONUS_PER_BOSS[bossIndex] ?? 0);
      this.save.addEchoes(
        echoBonus
        * this.echoGainMultiplier
        * (this.player.echoPactMultiplier ?? 1)
        * this.rewardMultiplier
        * (this.endless ? getEndlessEchoMultiplier(Math.floor(this.gauntletIndex / BOSSES.length)) : 1),
      );
      // King's Ransom (Shard perk) doubles every boss's Shard drop.
      const shardMultiplier = this._isPerkUnlocked('doubleShards') ? 2 : 1;
      const baseShards = this.endless
        ? 5
        : inActThree ? (ACT3_SHARDS[act3Index] ?? 0)
          : inActTwo ? (ACT2_SHARDS[act2Index] ?? 0) : (SHARDS_PER_BOSS[bossIndex] ?? 0);
      const shardRewardWithRemainder =
        baseShards * shardMultiplier * this.rewardMultiplier + this._shardRewardRemainder;
      const shardsThisKill = Math.floor(shardRewardWithRemainder);
      this._shardRewardRemainder = shardRewardWithRemainder - shardsThisKill;
      this.save.addShards(shardsThisKill);
      this._shardsEarnedThisRun += shardsThisKill;
    }

    // Every boss kill is a clean reset point, not just a checkpoint for
    // chip damage to carry through — full heal before the next stage.
    if (this.ascensionRules.healBetweenBosses) this.player.hp = this.player.maxHp;

    // Endless ends (and offers Continue) at every Act I final-boss loop
    // boundary; every other run ends on its own act's last boss — the King for
    // Act I, the Undersong for Act II, the Anthem for Act III (campaignFlow.js).
    const outcome = bossDeathOutcome({
      endless: this.endless,
      index: this.gauntletIndex,
      actOneLen: BOSSES.length,
      endIndex: this.act.endIndex,
    });
    if (outcome === 'end') {
      this._endRun(true);
      return;
    }

    // The kill's Spoils chest (BULLET-HEAVEN.md §H3), dropped where the boss
    // fell. Below the run-ending branch on purpose — a chest nobody can walk
    // to, behind the results overlay, is just a reward that visibly didn't
    // happen — and above the final-warning branch, which pauses but does not
    // end: those chests sit on the floor through the interstitial and are
    // collected the moment the player is moving again.
    //
    // gauntletIndex has not advanced yet, so the tier is the stage that was
    // actually cleared rather than the next one.
    this._dropSpoilsChest();

    // player/progression carry over untouched; the boss and its
    // SpawningSystem are discarded either way.
    const defeatedName = this.bossDef.name;
    this.gauntletIndex++;
    this.bossDef = this._bossDefForIndex(this.gauntletIndex);
    this.boss = null;
    this.combat.setBoss(null);
    this.spawning = null;
    // Always already null here (the herald pair only exists mid-way through
    // the final boss's own wave stage, well after this point) — reset
    // anyway, same belt-and-braces reasoning as the primary slot above.
    this._secondaryBoss = null;
    this._secondarySpawning = null;
    // The Famished Court's Feast can leave courtiers mid-march if the boss
    // dies while the pattern is live (SpawningSystem stops updating running
    // patterns the moment isDying flips, so the pattern's own onEnd sweep
    // never fires) — clear them here so nothing frozen-but-shootable
    // carries into the next wave stage and stalls isStageCleared.
    this._sweepMinions();

    // Crossfade to the next boss's theme, dropped back to wave intensity —
    // also runs under the final-warning screen, where the Unmade King's
    // theme starting early is exactly the right kind of ominous.
    audio.playMusic(this.bossDef.musicTheme, 0);

    if (this.bossDef.isFinalBoss) {
      // The one deliberate exception to every other transition being
      // instant — see FinalWarningOverlay/_beginFinalWaveStage. Paused like
      // the level-up screen until the player clicks through.
      // The previous boss's spawn title can still be fading (holds 2s +
      // 0.6s fade) since there's no wave stage between it and this warning
      // screen to let it clear on its own — clear it out so it can't overlap.
      document.querySelectorAll('.boss-title').forEach((el) => el.remove());
      this.stage = 'warning';
      this._autoPause = true;
      this._syncPaused();
      this.eventBus.emit('gauntlet:final-warning', { defeatedName, name: this.bossDef.name });
    } else {
      this.stage = 'waves';
      this.minionWaves = new MinionWaveSystem(this.eventBus, this.combat, this._waveConfig(), { rng: this._waveRng });
      this._maybeSpawnEchoRift();
    }
  }

  /**
   * Echo Duel: offers the rift during the fourth gauntlet slot's wave stage
   * (right after the Riven Choir, while The Famished Court looms) whenever
   * a previous run's build snapshot exists — the same mid-run beat it sat
   * at in the four-boss gauntlet. Touching it is opt-in (_updateEchoRift);
   * ignoring it costs nothing and it despawns when the boss fight starts.
   */
  _maybeSpawnEchoRift() {
    if (!this.save.data.lastEchoSnapshot) return;
    // Echo Duel is an Act I beat; don't re-offer it in the Act II continuation.
    if (!this.endless && this.gauntletIndex >= BOSSES.length) return;
    if (this.gauntletIndex % BOSSES.length !== 3) return;
    if (this._echoRiftSeenIndices.has(this.gauntletIndex)) return;
    this._echoRiftSeenIndices.add(this.gauntletIndex);
    this._echoRift = { x: ARENA.x + ARENA.width * 0.5, y: ARENA.y + ARENA.height * 0.78, radius: 7 };
    this.eventBus.emit('echo-rift:spawned', { x: this._echoRift.x, y: this._echoRift.y });
  }

  /** Touch check for the rift — summons the ghost through the same secondary-boss slot the herald pairs use. */
  _updateEchoRift() {
    const rift = this._echoRift;
    if (Math.hypot(rift.x - this.player.x, rift.y - this.player.y) >= rift.radius + this.player.radius) return;
    this._echoRift = null;
    const snapshot = this.save.data.lastEchoSnapshot;
    if (!snapshot || this._secondaryBoss) return;
    const def = buildEchoBossDef(snapshot, {
      hpMultiplier: this._endlessLoopMultiplier() * this.ascensionRules.enemyStatMultiplier,
    });
    this._secondaryBoss = new Boss(this.eventBus, def);
    this._secondaryBoss.x = rift.x;
    this._secondaryBoss.y = Math.max(ARENA.y + 40, rift.y - 60);
    // As a minion (not the boss slot), same as a herald pair's second half —
    // isBossTier keeps full boss-grade hit treatment; being in minions also
    // holds MinionWaveSystem.isStageCleared false, so the wave stage waits
    // for the duel to resolve before handing off to the real boss.
    this.combat.addMinion(this._secondaryBoss);
    this._secondarySpawning = this._attachToneCaster(new SpawningSystem(this.eventBus, def, this.combat, { rng: this._cardRng, patternRecords: this._patternRecords }));
    this._showBossTitle(def.name);
    this.eventBus.emit('echo-duel:accepted', { name: def.name });
  }

  /**
   * Leaves the dead boss's Spoils chest on the floor (BULLET-HEAVEN.md §H3).
   *
   * Called from _onBossDied while this.boss is still the corpse, so the chest
   * lands where the fight ended rather than at some neutral spot — the drop is
   * the kill's payout made physical, and it should read as having come off the
   * thing that just died.
   *
   * Skipped in the tutorial (a slot machine is the wrong second thing to
   * explain) and in Boss Challenge, whose single kill ends the run outright.
   */
  _dropSpoilsChest() {
    if (this.isTutorial || this.isChallenge || this.runEnded) return;
    const source = this.boss ?? this.player;
    // Clamped well inside the walls: a boss killed against the edge would
    // otherwise drop its chest half-buried in one, where the sprite is
    // clipped and the pickup circle is partly unreachable.
    const margin = 24;
    const x = Math.min(Math.max(source.x, ARENA.x + margin), ARENA.x + ARENA.width - margin);
    const y = Math.min(Math.max(source.y, ARENA.y + margin), ARENA.y + ARENA.height - margin);

    // Belt-and-braces: a chest still on the floor when the next one drops is
    // paid out rather than overwritten. Unreachable in a normal run (the
    // magnet collects within seconds and the next boss is a whole wave stage
    // away), but the alternative to handling it is a silently eaten reward.
    if (this._spoilsDrop) this._collectSpoilsDrop(this._spoilsDrop);

    this._spoilsDrop = new SpoilsChestDrop({
      x,
      y,
      tier: chestTier(this.gauntletIndex),
      stageIndex: this.gauntletIndex,
      animate: !this.save.skipAnimations,
    });
    this.eventBus.emit('spoils:dropped', { x, y, tier: this._spoilsDrop.tier });
  }

  /** Touch/magnet check for the chest on the floor — opens the reel on contact. */
  _updateSpoilsChestDrop(dt) {
    const drop = this._spoilsDrop;
    if (!drop) return;
    if (drop.update(dt, this.player)) this._collectSpoilsDrop(drop);
  }

  /** Banks a chest and shows it (or queues it, if one is somehow already up). */
  _collectSpoilsDrop(drop) {
    this._spoilsDrop = null;
    drop.active = false;
    if (!this.save.skipAnimations) {
      this.particles.burst(drop.x, drop.y, {
        count: 14, color: SPOILS_CHEST_TRIM[drop.tier] ?? SPOILS_CHEST_TRIM.plain,
        life: 0.5, speedMin: 30, speedMax: 120, size: 1.5, additive: true, gravity: 90,
      });
    }
    this._spoilsQueue.push(drop.stageIndex);
    this._openNextSpoils();
  }

  /**
   * Shows the next collected-but-unshown chest. The one place the overlay is
   * opened from, so "collected" and "shown" can never drift apart: anything
   * that can't be shown right now (an overlay already up, a run that ended in
   * the same tick) stays queued rather than being dropped on the floor.
   */
  _openNextSpoils() {
    if (this._spoilsQueue.length === 0 || this.spoilsChest.isOpen || this.runEnded) return;
    this.spoilsChest.open({ stageIndex: this._spoilsQueue.shift() });
  }

  /** Called by FinalWarningOverlay's Continue button — starts the final boss's wave stage. */
  _beginFinalWaveStage() {
    this._autoPause = false;
    this._syncPaused();
    this.stage = 'waves';
    this.minionWaves = new MinionWaveSystem(this.eventBus, this.combat, this._waveConfig(), { rng: this._waveRng });
  }

  /**
   * Called once MinionWaveSystem.isStageCleared. All four heralds now fight
   * in pairs right before the true final boss (see MINIBOSSES in
   * data/bosses.js) — two at once instead of one at a time — rather than
   * one per gauntlet stage; every other stage's wave-clear goes straight
   * into its real boss.
   */
  _startMinibossStage() {
    // The Tide's clock running out summons its reaper directly. No heralds:
    // the reaper IS the mode's ending, and a herald pair in front of it would
    // turn a single decisive confrontation into a gauntlet, which is the exact
    // structure this mode exists to be an alternative to.
    if (this.isTide) {
      this._startBossStage();
      return;
    }
    if (!this._minibossRoster()) {
      this._startBossStage();
      return;
    }
    this._sweepMinions();
    this.stage = 'boss'; // reuses the exact same render/update path as a real boss fight
    this._isMinibossStage = true;
    this._minibossQueueIndex = 0;
    this._spawnMinibossFromQueue();
    audio.setMusicIntensity(1);
  }

  /**
   * Spawns MINIBOSSES[_minibossQueueIndex] and, if one exists, the next one
   * in the queue right alongside it — all four share the current loop's
   * endless multiplier (see _minibossDefForIndex). The primary goes through the same
   * this.boss/this.spawning slot a real boss fight uses; the secondary
   * (this._secondaryBoss/_secondarySpawning) is added to CombatSystem as a
   * minion instead of a second `boss` slot — Boss.isBossTier is what keeps
   * it getting full boss-tier hit-detection treatment (no
   * minionDamageMultiplier, no knockback) despite that.
   */
  _spawnMinibossFromQueue() {
    const primaryDef = this._minibossDefForIndex(this._minibossQueueIndex);
    this.boss = new Boss(this.eventBus, primaryDef);
    this.combat.setBoss(this.boss);
    this.spawning = this._attachToneCaster(new SpawningSystem(this.eventBus, primaryDef, this.combat, { rng: this._cardRng, patternRecords: this._patternRecords }));

    const secondaryIndex = this._minibossQueueIndex + 1;
    // An odd-length roster (Act II's three wardens) simply brings the last one
    // up alone — the pair math never assumed an even count.
    if (secondaryIndex < this._minibossRoster().length) {
      const secondaryDef = this._minibossDefForIndex(secondaryIndex);
      this._secondaryBoss = new Boss(this.eventBus, secondaryDef);
      // Spawns on top of the primary otherwise (both default to arena
      // center) — nudge it aside so the pair reads as two fights from the
      // first frame instead of popping apart once physics separation
      // (OverlapResolver, run from CombatSystem.update) catches up.
      this._secondaryBoss.x -= 70;
      this.combat.addMinion(this._secondaryBoss);
      this._secondarySpawning = this._attachToneCaster(new SpawningSystem(this.eventBus, secondaryDef, this.combat, { rng: this._cardRng, patternRecords: this._patternRecords }));
    } else {
      this._secondaryBoss = null;
      this._secondarySpawning = null;
    }

    this._bossStageTime = 0;
    this._bossEnraged = false;
  }

  /** Fires on a herald's death — once both of the current pair are down, advances to the next pair (or hands off to the real boss once all four are down). this.bossDef untouched throughout. */
  _onMinibossDied(deadBoss) {
    // Echo Duel ghost: routes out before the herald-queue logic below —
    // advancing _minibossQueueIndex mid-wave-stage would spawn heralds into
    // a stage that isn't theirs.
    if (deadBoss?.bossDef?.isEchoGhost) {
      this.combat.removeMinion(deadBoss);
      if (deadBoss === this._secondaryBoss) {
        this._secondaryBoss = null;
        this._secondarySpawning = null;
      }
      const reward = Math.round(150 * this._endlessLoopMultiplier());
      this.save.addEchoes(reward);
      this._echoDuelsWon++;
      this.eventBus.emit('echo-duel:won', { reward, x: deadBoss.x, y: deadBoss.y });
      return;
    }
    // A real herald (not the Echo ghost, which returned above): unlocks the
    // shared "heralds" Archive entry, keyed by that id rather than the
    // herald's inherited boss spriteKey (see data/lore.js BOSS_LORE.heralds).
    this.save.recordBossDefeated(this.bossDef.minibossLoreKey ?? 'heralds');
    if (deadBoss === this.boss) {
      this.combat.setBoss(null);
      this.boss = null;
      this.spawning = null;
    } else if (deadBoss === this._secondaryBoss) {
      this.combat.removeMinion(this._secondaryBoss);
      this._secondaryBoss = null;
      this._secondarySpawning = null;
    }
    if (this.boss || this._secondaryBoss) return; // the other half of the pair is still up

    this._minibossQueueIndex += 2;
    if (this._minibossQueueIndex < this._minibossRoster().length) {
      this._spawnMinibossFromQueue();
      return;
    }
    this._isMinibossStage = false;
    this._startBossStage();
  }

  /** Called once the herald is dead (or immediately from the wave stage if ascension/mode ever disables heralds) — spawns the real boss this wave stage was building up to. */
  _startBossStage() {
    // An unaccepted Echo Rift despawns when its wave stage ends — the offer
    // is per wave stage, not something that lingers into the boss fight.
    this._echoRift = null;
    // Safety net: isStageCleared already requires zero minions, but a
    // straggler (e.g. a Charger that wandered off) shouldn't ever carry
    // into the boss fight.
    this._sweepMinions();
    this.stage = 'boss';
    const stageBossDef = this.ascensionRules.extraBossLayer
      ? { ...this.bossDef, layerFromPhase: Math.max(0, this.bossDef.layerFromPhase - 1) }
      : this.bossDef;
    this._startAct3Systems(stageBossDef);
    this.boss = new Boss(this.eventBus, stageBossDef);
    // §D8 — the drill ends when the player quits, not when the boss dies. The
    // player's weapons still fire and still do nothing, which also keeps the
    // XP curve still: Boss.takeDamage returns before emitting, so no damage
    // event means no XP means no level-up picker interrupting the card.
    this.boss.practiceLock = this.isCardPractice;
    this.combat.setBoss(this.boss);
    this.spawning = this._attachToneCaster(new SpawningSystem(this.eventBus, stageBossDef, this.combat, {
      practicePattern: this.practicePattern,
      rng: this._cardRng, // BULLET-HELL.md §D8
      patternRecords: this._patternRecords, // BOSS-REWORK.md §B10
    }));
    this._bossStageTime = 0;
    this._bossEnraged = false;
    this.eventBus.emit('boss:spawn', { name: this.bossDef.name });
    // Fresh cover for every boss; pillar HP rides the same endless per-loop
    // curve as enemy HP so blocking stays meaningful at depth.
    this.cover.beginBossFight({
      hpMultiplier: this._endlessLoopMultiplier() * this.ascensionRules.enemyStatMultiplier,
    });
    audio.setMusicIntensity(1);
  }

  /**
   * Act III's three fight-long textures (ACT3-BIBLE §3): the Tithe's levy ring,
   * the Manifold's second body, and the Verse's card hand. Declared on the boss
   * def and owned by small systems under systems/act3/, exactly as Act II
   * declared `gloaming` / `ballast` / `polarity` and let Game drive them — a
   * rule that has to hold for a whole fight can't be a pattern the scheduler
   * picks now and then.
   *
   * The Anthem carries all three at once, each gated by its own `minPhase`
   * (its Chorus recapitulation), so these are built up front and simply stay
   * dormant until the boss reaches the phase that turns them on.
   */
  _startAct3Systems(bossDef) {
    this._disposeAct3Systems();
    if (bossDef.levy) {
      this._levy = new LevySystem(this.eventBus, bossDef.levy, {
        player: this.player, progression: this.progression, ultimate: this.ultimate,
      });
    }
    if (bossDef.manifold) {
      this._manifold = new ManifoldSystem(this.eventBus, bossDef.manifold, {
        player: this.player,
        combat: this.combat,
        // The accessibility assist the bible makes a shipping requirement:
        // mirrored control is exactly the mechanic that hurts most under motor
        // or spatial-processing difficulty, so the second body can be switched
        // to following automatically at reduced damage (see SettingsMenu).
        assist: this.save.manifoldAssist,
      });
    }
    if (bossDef.verse) {
      this._verse = new VerseSystem(this.eventBus, bossDef.verse, { player: this.player });
    }
  }

  _disposeAct3Systems() {
    this._levy?.dispose();
    this._manifold?.dispose();
    this._verse?.dispose();
    this._levy = null;
    this._manifold = null;
    this._verse = null;
  }

  /** Ticks whichever Act III textures this fight declared, respecting their phase gates. */
  _updateAct3Systems(dt, boss) {
    // `minPhase` is how the Anthem layers its four textures in one at a time.
    // Absent on the bosses that introduce them, which is "live from the start".
    const live = (cfg) => boss.phase >= (cfg.minPhase ?? 0);
    if (this._levy && live(this._levy.config)) this._levy.update(dt, boss);
    if (this._manifold && live(this._manifold.config)) this._manifold.update(dt, boss, this.spawning?.pool);
    if (this._verse && live(this._verse.config)) this._verse.update(dt, boss);
  }

  _showBossTitle(name) {
    const isFinal = this.bossDef.isFinalBoss;
    const el = document.createElement('div');
    el.className = isFinal ? 'boss-title boss-title-final' : 'boss-title';
    const nameEl = document.createElement('div');
    nameEl.textContent = name.toUpperCase();
    el.appendChild(nameEl);
    // One-line story beat under the name (data/lore.js). Heralds share the
    // 'heralds' epithet rather than their inherited boss's; a boss without a
    // lore entry simply shows no epithet.
    // Heralds share their act's guard-set epithet rather than the boss whose
    // sprite they borrow. `minibossLoreKey` is declared by the acts that have
    // their own guard set (Act II's deep wardens, Act III's song heralds);
    // Act I's four fall through to the default.
    const lore = this.bossDef.isMiniboss
      ? (BOSS_LORE[this.bossDef.minibossLoreKey] ?? BOSS_LORE.heralds)
      : BOSS_LORE[this.bossDef.spriteKey];
    if (lore?.epithet) {
      const epithetEl = document.createElement('div');
      epithetEl.className = 'boss-title-epithet';
      epithetEl.textContent = lore.epithet;
      el.appendChild(epithetEl);
    }
    document.getElementById('ui-root').appendChild(el);
    requestAnimationFrame(() => el.classList.add('boss-title-visible'));
    setTimeout(() => {
      el.classList.remove('boss-title-visible');
      el.classList.add('boss-title-fade');
      setTimeout(() => el.remove(), 600);
    }, isFinal ? 3500 : 2000);
  }

  _endRun(won) {
    if (this.runEnded) return;
    // Dying inside the desperation window (boss at 0 HP, finale still firing —
    // see SpawningSystem._updateDesperation) is a loss, not a win: the window
    // is the last thing that has to be survived, so a death in it fails the
    // run exactly like any other death.
    // Close any open ultimate window so its def's onEnd runs — a run that
    // ends mid-Zero-Hour must not leave the enemy time scale (or any other
    // flag an ultimate flipped) applied to whatever comes next.
    this.ultimate.cancel();
    // Drop any hit still hanging in the deathbomb window (§D3) — the run is
    // over, and it must not resolve into the next one.
    this.player.clearPendingHit();
    this.juice.deathbombPrompt = null;
    // Same reasoning as the boss-death path: an Act III texture left running
    // under the results overlay would keep collecting, mirroring or offering.
    this._disposeAct3Systems();
    // Covers the player-death path (a fatal hit and an XP-threshold crossing
    // can land in the same tick, before the level-up pause takes effect —
    // see the comment on _onBossDied). Harmless no-op if nothing's pending.
    this.progression.forceResolvePending();
    // An uncollected chest dies with the run — there is no arena left to walk
    // to it in, and SpoilsChest itself closes on run:ended, so anything still
    // queued has nowhere to be shown either.
    this._spoilsDrop = null;
    this._spoilsQueue.length = 0;
    this.runEnded = true;
    this._syncPaused();
    // Calm the mix back down under the results overlay — the victory/
    // defeat stinger (run:ended, AudioSystem) plays over the quieter bed.
    audio.setMusicIntensity(0);

    // Spell-card records (BULLET-HELL.md §D1) — written for EVERY mode,
    // deliberately above the rewardless early return below. Boss Challenge and
    // act practice pay nothing, but "which cards can I capture" is the exact
    // question those modes exist to answer, and a practice capture that didn't
    // count would make the record useless as a practice list (§D8).
    this.save.recordPatternResults(this.captureTracker.records);

    // Boss Challenge: skip every reward/progression side effect below
    // (Echoes, Shards, leaderboard score, run statistics, Echo Duel
    // snapshot) — a practice fight against one boss shouldn't be a faster
    // way to farm currency than actually clearing the gauntlet.
    // `isPractice` (data/acts.js) rides the same early return. No act sets it
    // any more — every act is a standalone rewarded run since the split — but
    // the branch is the contract for a rewardless route, not a fact about
    // Act II, so it stays.
    if (this.isChallenge || this.isPractice) {
      this.eventBus.emit('run:ended', {
        runId: this.runId,
        seed: this.seed,
        won,
        echoes: 0,
        shards: 0,
        glimmer: 0,
        survivalTime: this.progression.survivalTime,
        totalDamageDealt: this.progression.totalDamageDealt,
        level: this.progression.level,
        gauntletIndex: this.gauntletIndex,
        bossName: this.bossDef.name,
        characterName: this.character.name,
        leaderboardScore: 0,
        scoreBreakdown: null,
        ascensionLevel: this.ascension.level,
        endless: false,
        challenge: this.isChallenge,
        practice: this.isPractice,
        // Both rewardless modes skip every payout, but the recap is a lesson
        // rather than a payout — it's the whole reason to practise.
        damageRecap: this.damageLedger.getRecap(),
        captures: this.captureTracker.captures,
        captureAttempts: this.captureTracker.attempts,
      });
      return;
    }
    // bossDef.maxHp (not this.boss.maxHp) since the player can die mid wave
    // stage, before a Boss instance for this gauntlet slot even exists —
    // bossDef is always the boss this stage was building up to or fighting.
    const endlessEchoMultiplier = this.endless
      ? getEndlessEchoMultiplier(Math.floor(this.gauntletIndex / BOSSES.length)) : 1;
    // The Tide pays on the clock instead of against a boss HP pool
    // (data/tide.js). calcEchoes' whole shape is damage-relative-to-the-boss,
    // which is meaningless in a mode where the boss appears in the last
    // minute — a Tide run that never reaches the reaper would otherwise earn
    // nearly nothing for twenty minutes of play. Per-minute income is also
    // where BULLET-HEAVEN.md §H7 puts the uncapped Echo economy ARCHIVE.md gap 1
    // asks for: it is a mode with no milestone payouts to cap.
    const baseEchoes = this.isTide
      ? tideEchoes(this.progression.survivalTime)
      : calcEchoes({
        totalDamageDealt: this.progression.totalDamageDealt,
        bossMaxHp: this.bossDef.maxHp,
        survivalSeconds: this.progression.survivalTime,
        minionDamageDealt: this.progression.minionDamageDealt,
      });
    const echoes = Math.floor(baseEchoes
      * this.echoGainMultiplier * (this.player.echoPactMultiplier ?? 1) * this.rewardMultiplier * endlessEchoMultiplier);
    this.save.addEchoes(echoes);
    // Shards are whole-number currency. Preserve fractional Ascension
    // rewards between boss kills, then round the remainder up at the end of
    // every round—including an Endless clear before Continue is chosen.
    if (this._shardRewardRemainder > 0) {
      const remainderBonus = Math.ceil(this._shardRewardRemainder);
      this.save.addShards(remainderBonus);
      this._shardsEarnedThisRun += remainderBonus;
      this._shardRewardRemainder = 0;
    }
    // The Tide pins `gauntletIndex` to its reaper's roster slot so every
    // bossDef read (HUD, music, arena theme) names the thing at the end of the
    // clock from the first frame. That index is a LOOKUP, not progress — a
    // Tide run that died at three minutes has defeated nothing, and reporting
    // it as `gauntletIndex + won` would credit it with the whole gauntlet,
    // inflating lifetime statistics, the Deepest-Gauntlet record, every
    // boss-count achievement, and the run's Reprise contribution alike.
    const reportedGauntletIndex = this.isTide ? -1 : this.gauntletIndex;
    const reportedBossesDefeated = this.isTide
      ? (won ? 1 : 0) // the reaper, and only if it actually fell
      : this.gauntletIndex + (won ? 1 : 0);
    // Glimmer (cosmetic currency) — flat vanity payout for the run, no
    // reward/pact/endless multipliers applied (it's deliberately outside the
    // combat economy). Boss Challenge never reaches here (early return above).
    const glimmer = calcGlimmer({
      gauntletIndex: reportedGauntletIndex,
      grazes: this._runGrazes,
      // Chain-weighted rather than a raw count (BULLET-HELL.md §D5) — same
      // grazes, more Glimmer when they were chained.
      grazeValue: this.grazeChain.value,
      won,
      captureGlimmer: this.captureTracker.glimmer,
    });
    this.save.addGlimmer(glimmer);
    // Shards are already banked per-boss-kill in _onBossDied (1/2/3, not a
    // lump sum here) — this is just the run-end readout of that total.
    // One breakdown, and the total read off it — the two used to be computed
    // by separate calls with duplicated argument lists that could drift apart.
    const scoreBreakdown = calcLeaderboardScoreBreakdown({
      // The Tide scores on minutes survived, not gauntlet depth — its own
      // board (see the run:ended payload) ranks it against other Tide runs.
      gauntletIndex: reportedGauntletIndex,
      survivalSeconds: this.progression.survivalTime,
      won,
      ascensionLevel: this.ascension.level,
      endless: this.endless,
      // Each act scores against itself: an Act II run begins at boss eight and
      // an Act III run at boss fourteen, so counting progress from zero would
      // credit fights that never happened. Clear bonus and par come from the
      // act for the same reason — see data/acts.js and Currency.js. The Tide
      // starts at zero and keeps the pre-split constants so its own board's
      // existing rows stay comparable (data/tide.js).
      startIndex: this.isTide ? 0 : this.act.startIndex,
      clearBonus: this.isTide ? TIDE_CLEAR_BONUS : this.act.scoring.clearBonus,
      parSeconds: this.isTide ? TIDE_PAR_SECONDS : this.act.scoring.parSeconds,
      // Computed here rather than inside Currency.js so that module stays
      // free of data/ imports — same floor() the HP/damage curves use.
      endlessLoop: this.endless ? Math.floor(this.gauntletIndex / BOSSES.length) : 0,
      noEchoUpgrades: this.disableEchoUpgrades,
      noShardPerks: this.disableShardPerks,
      noPetBonuses: this.disablePetBonuses,
    });
    const leaderboardScore = scoreBreakdown.total;
    // The act unlock gate (data/acts.js). Deliberately down HERE rather than
    // beside recordBossDefeated in _onBossDied: everything above the rewardless
    // early return runs for Boss Challenge too, and both the Unmade King and
    // the Undersong are in CHALLENGE_ROSTER — so recording a clear from a boss
    // death would let one practice fight open a fully rewarded act.
    //
    // The Tide is excluded for the same reason it scores on its own board: it
    // begins at index 0 and so resolves to Act I, but twenty minutes against a
    // clock is not a clear of the Court. Endless is not excluded — it wins by
    // completing an Act I loop, which is exactly what the gate asks for.
    if (won && !this.isTide) this.save.recordActCleared(this.act.id);
    // Record stats BEFORE the milestone check + emit below, so a milestone
    // cosmetic whose threshold this very run crossed (e.g. a first clear ->
    // Sovereign Crown) unlocks now and shows up on the same run-end screen.
    this.save.recordRunStatistics({
      won,
      endless: this.endless,
      ascensionLevel: this.ascension.level,
      gauntletIndex: reportedGauntletIndex,
      bossesDefeated: reportedBossesDefeated,
      totalDamageDealt: this.progression.totalDamageDealt,
      survivalTime: this.progression.survivalTime,
      leaderboardScore,
      grazes: this._runGrazes,
      bestGrazeChain: this.grazeChain.peak,
      bulletsAbsorbed: this.resonance.total,
      bestResonance: this.resonance.peak,
      echoDuelsWon: this._echoDuelsWon,
      motesCollected: this._runMotes,
      bestMoteBurst: this._runBestMoteBurst,
      fusions: this._runFusionIds.length,
      fusionIds: this._runFusionIds,
      pactsTaken: this.progression.buildSummary.pacts.length,
      level: this.progression.level,
      chestsOpened: this._runChestsOpened,
      bestLateChest: this._runBestLateChest,
      characterId: this.character.id,
    });

    // Achievements (BULLET-HEAVEN.md §H6). Evaluated AFTER the statistics
    // write above, so a lifetime threshold crossed by this very run unlocks
    // now rather than at the end of the next one — the whole mechanism is
    // "there is always a named next thing", and a reward that arrives a run
    // late reads as a bug.
    const newlyUnlockedAchievements = this.save.evaluateAchievements({
      won,
      died: !won,
      endless: this.endless,
      grazes: this._runGrazes,
      damageTaken: this._runDamageTaken,
      podsCollected: this._runPodsCollected,
      bossesDefeated: reportedBossesDefeated,
      pactsTaken: this.progression.buildSummary.pacts.length,
      characterId: this.character.id,
    });
    // Auto-unlock any milestone cosmetic (data/cosmetics.js) whose lifetime
    // stat threshold is now met and isn't already owned — the run-end payload
    // surfaces the names so the player sees what they earned.
    const newlyUnlockedCosmetics = [];
    for (const def of Object.values(COSMETICS)) {
      if (def.milestone && !this.save.ownsCosmetic(def.id) && cosmeticMilestoneMet(def, this.save.statistics)) {
        this.save.unlockCosmetic(def.id);
        newlyUnlockedCosmetics.push(def.name);
      }
    }
    this.eventBus.emit('run:ended', {
      runId: this.runId,
      // BULLET-HELL.md §D8 — null on an ordinary run. The overlay shows it and
      // the leaderboard files the row under it, which is the whole point: a
      // seeded row is comparable to the other rows that share its seed.
      seed: this.seed,
      won,
      echoes,
      shards: this._shardsEarnedThisRun,
      glimmer,
      newlyUnlockedCosmetics,
      newlyUnlockedAchievements,
      survivalTime: this.progression.survivalTime,
      totalDamageDealt: this.progression.totalDamageDealt,
      level: this.progression.level,
      gauntletIndex: reportedGauntletIndex,
      bossName: this.bossDef.name,
      characterName: this.character.name,
      leaderboardScore,
      scoreBreakdown,
      ascensionLevel: this.ascension.level,
      endless: this.endless,
      // Which act's ending, ladder and Reprise rate this run answers to. The
      // overlay uses it to pick the epilogue, the board to submit against, and
      // the run-worth to contribute at (data/acts.js, ACT3-BIBLE §1).
      actId: this.act.id,
      // BULLET-HEAVEN.md §H7 is explicit that the Tide must not share the
      // gauntlet's board: it is a different game measured on a different
      // axis (minutes survived against a curve, not bosses felled), and one
      // ladder holding both would rank neither.
      board: this.isTide ? TIDE.id : this.act.board,
      // Purity: already priced into leaderboardScore as a multiplier, but
      // carried through separately so the board can show/filter WHY a run
      // scored what it did rather than only the number.
      noEchoUpgrades: this.disableEchoUpgrades,
      noShardPerks: this.disableShardPerks,
      noPetBonuses: this.disablePetBonuses,
      // What actually hurt this run, biggest source first, plus the killing
      // blow (systems/DamageLedger.js). Null on a flawless run.
      damageRecap: this.damageLedger.getRecap(),
      captures: this.captureTracker.captures,
      captureAttempts: this.captureTracker.attempts,
    });
    // Echo Duel: record this run's build so the NEXT run can duel its
    // ghost. Only runs that reached at least the second boss leave an echo
    // — a first-boss wipe isn't a build worth mirroring.
    if (this.gauntletIndex >= 1) {
      this.save.setLastEchoSnapshot({
        profileName: this.save.data.profileName || this.save.accountUsername || 'a Survivor',
        characterId: this.character.id,
        weapons: this.combat.weapons.map((w) => ({ name: w.name, level: w.level })),
        passives: [...this.progression._ownedNonStackablePassives],
        level: this.progression.level,
        // A Tide build is a real build worth duelling, but it was not reached
        // at any gauntlet depth — see reportedGauntletIndex above.
        gauntletIndex: reportedGauntletIndex,
        ascension: this.ascension.level,
        recordedAt: new Date().toISOString(),
      });
    }
  }

  _continueEndless() {
    if (!this.runEnded) return;
    this.endless = true;
    this.runEnded = false;
    this._autoPause = false;
    this._userPause = false;
    this._syncPaused();
    this.gauntletIndex += 1;
    this.bossDef = this._bossDefForIndex(this.gauntletIndex);
    this.player.incomingDamageMultiplier =
      this.ascensionRules.enemyDamageMultiplier * this.worldEnemyMultiplier * this._incomingDamageLoopMultiplier();
    this.boss = null;
    this.combat.setBoss(null);
    this.spawning = null;
    this._secondaryBoss = null;
    this._secondarySpawning = null;
    this.stage = 'waves';
    audio.playMusic(this.bossDef.musicTheme, 0);
    this.minionWaves = new MinionWaveSystem(this.eventBus, this.combat, this._waveConfig(), { rng: this._waveRng });
    this._maybeSpawnEchoRift();
  }

  start() {
    if (this._running) return;
    this._running = true;
    this._lastTime = performance.now();
    this._rafId = requestAnimationFrame(this._tick);
    // Each stage's whole arc (waves -> boss) runs on the upcoming boss's
    // theme, thickening with intensity as the fight escalates — see
    // MusicDirector/musicThemes.js.
    audio.playMusic(this.bossDef.musicTheme, this.stage === 'boss' ? 1 : 0);
  }

  stop() {
    this._running = false;
    if (this._rafId !== null) cancelAnimationFrame(this._rafId);
    audio.stopMusic();
  }

  destroy() {
    this.stop();
    // A practice session has no end of its own — the boss can't die, so the
    // only way out is the pause menu's Exit, which tears the Game down without
    // ever reaching _endRun. Flush the cards drilled here on the way out, or
    // the mode that exists to fill in the capture list would be the one mode
    // that never writes to it. Cleared afterwards so a second destroy (or an
    // _endRun on the player's death, which flushes the same tally) can't
    // double-count them.
    if (this.isCardPractice && !this.runEnded) {
      this.save.recordPatternResults(this.captureTracker.records);
      this.captureTracker.records = {};
    }
    this.touchControls.destroy();
    this.pauseOverlay.destroy();
    this.tutorialOverlay?.destroy();
    this.tutorial?.destroy();
    this.pickups.destroy();
    this.spoilsChest.destroy();
    const uiRoot = document.getElementById('ui-root');
    uiRoot.querySelectorAll('.boss-title, .runend-overlay, .levelup-overlay, .spoils-overlay, .survive-overlay, .final-warning-overlay, .tutorial-overlay, .levy-prompt').forEach((el) => el.remove());
    // Each run creates its own Renderer (a new text-overlay canvas + a
    // window resize listener, see Renderer.js) — without this, every past
    // run's HUD canvas (dash pips, ultimate meter, health bar, boss name)
    // stayed in the DOM frozen on its last-drawn frame, stacking on top of
    // the new run's live HUD and making it look like the old one "stuck".
    this.renderer.destroy();
  }

  // The arena is bigger than the camera's view (Camera.js), and most boss
  // attacks are telegraphed/fired from the boss's own position — if the
  // camera only ever follows the player, a boss that wanders or dashes away
  // can attack from entirely off-screen, making an otherwise-correctly-
  // telegraphed hit look like it came from nowhere. Following the midpoint
  // between player and boss during the boss stage keeps both in frame far
  // more often, at the cost of the view feeling a little less player-locked.
  // The midpoint alone isn't sufficient once they're far enough apart
  // though (one of them ends up outside the frame even though the midpoint
  // is dead center) — spreadX/spreadY tell Camera.update() how far out it
  // needs to zoom to actually keep both in view, not just centered on.
  _cameraFollowTarget() {
    if (this.stage === 'boss' && this.boss) {
      // Midpoint of both heralds when a pair is up (see
      // _spawnMinibossFromQueue), so the camera keeps whichever one wanders
      // further away in frame too, not just this.boss.
      const bossX = this._secondaryBoss ? (this.boss.x + this._secondaryBoss.x) / 2 : this.boss.x;
      const bossY = this._secondaryBoss ? (this.boss.y + this._secondaryBoss.y) / 2 : this.boss.y;
      const midX = (this.player.x + bossX) / 2;
      const midY = (this.player.y + bossY) / 2;
      let spreadX = Math.max(Math.abs(this.player.x - midX), Math.abs(bossX - midX));
      let spreadY = Math.max(Math.abs(this.player.y - midY), Math.abs(bossY - midY));
      if (this._secondaryBoss) {
        spreadX = Math.max(spreadX, Math.abs(this.boss.x - midX), Math.abs(this._secondaryBoss.x - midX));
        spreadY = Math.max(spreadY, Math.abs(this.boss.y - midY), Math.abs(this._secondaryBoss.y - midY));
      }
      return { x: midX, y: midY, spreadX, spreadY };
    }
    return this.player;
  }

  _tick = (now) => {
    if (!this._running) return;

    const frameTime = Math.min((now - this._lastTime) / 1000, MAX_FRAME_TIME);
    this._lastTime = now;

    // Polled every real frame (not gated by the sim accumulator) so pause
    // responds instantly and can toggle the sim back on. Blocked during the
    // level-up pick and after the run ends — those have their own overlays.
    if (this.input.consumePausePressed() && !this._autoPause && !this.runEnded) {
      this._setUserPause(!this._userPause);
    }

    // JuiceDirector unifies hit-stop and slow-mo into one scale applied to
    // real frame time: 0 freezes the sim outright for a beat (the
    // accumulator deliberately isn't advanced, so simulation time never
    // "catches up" with a burst once the freeze ends), fractional values
    // run the whole sim — entities, bullets, cosmetic decays — in slow
    // motion together, and 1 is normal play.
    const timeScale = this.juice.tickTimeScale(frameTime);
    if (timeScale > 0) {
      this._accumulator += frameTime * timeScale;
      while (this._accumulator >= SIM_DT) {
        this._update(SIM_DT);
        this._accumulator -= SIM_DT;
      }
    }
    // Rolling frame-time average for the dev perf readout — see _drawPerf.
    this._perfFrameMs = (this._perfFrameMs ?? frameTime * 1000) * 0.9 + frameTime * 1000 * 0.1;

    this._render();
    this._rafId = requestAnimationFrame(this._tick);
  };

  _update(dt) {
    this.camera.update(dt, this._cameraFollowTarget());
    this.particles.update(dt);
    // Ages all ephemeral juice state (afterimages, damage numbers, pulses,
    // flashes, callouts). Runs even while paused, same reasoning as
    // particles.update above — e.g. a level-up's own "LEVEL UP!" text
    // should keep animating through its own pick screen, not freeze until
    // the player dismisses it.
    this.juice.update(dt);

    if (this.paused) return; // level-up pick open, or run has ended

    const moveVec = this.input.getMoveVector();
    const dashHeld = this.input.isDashHeld();
    this.player.update(dt, moveVec, dashHeld, this.input.isFocusHeld());
    this._updateFocusRing(dt);
    if (this.input.consumeUltimatePressed()) this.ultimate.activate();
    // Fugue's Countersubject (Act III). A no-op for every other champion —
    // swapStance() bails when the run has no stance config.
    // One champion-verb key (KEYBINDS.stanceSwap). Both calls are no-ops for a
    // champion without that signature, and no champion has both.
    if (this.input.consumeStanceSwapPressed()) {
      this.player.swapStance();
      this.player.flipAttunement();
    }
    // Ticked on real dt, deliberately: Zero Hour slows the enemies, not the
    // clock on its own window.
    this.ultimate.update(dt);
    // Decays the graze chain (BULLET-HELL.md §D5). Ticked on real dt like the
    // ultimate above: Zero Hour slows the bullets, and a chain window that
    // slowed with them would make the mode quietly better at chaining than
    // skill is.
    this.grazeChain.update(dt);
    // Resonance decays on real dt for the same reason (BULLET-HELL.md §D7).
    this.resonance.update(dt);

    // Zero Hour (Iris's ultimate) slows everything hostile — bosses, minions,
    // and every bullet already in flight, all of which are stepped by the
    // systems below — while the player's own movement, dash, and fire rate
    // stay on real dt. 1 whenever no time-scaling ultimate is active.
    //
    // `save.enemySpeed` (the accessibility assist) multiplies in at the same
    // point rather than writing to `player.enemyTimeScale`, which Zero Hour
    // owns and resets to a literal 1 when it ends (data/ultimates.js) — it
    // would clobber the setting on every expiry. Multiplying here composes
    // the two correctly: the assist holds while the ultimate deepens it.
    const enemyDt = dt * this.player.enemyTimeScale * this.save.enemySpeed;

    if (this._extraLifeGraceRemaining > 0) {
      this._extraLifeGraceRemaining -= dt;
      // Don't clobber a genuine dash's own i-frames if one happens to be
      // running right as the grace window ends — its own update() will
      // clear isInvincible on schedule regardless.
      if (this._extraLifeGraceRemaining <= 0 && !this.player.isDashing) {
        this.player.isInvincible = false;
      }
    }

    if (this.stage === 'tutorial') {
      this.tutorial.update(dt);
    } else if (this.stage === 'waves') {
      this.minionWaves.update(enemyDt, this.player);
      if (this._echoRift) this._updateEchoRift();
      // The Echo Duel ghost fights inside the wave stage through the same
      // secondary slot the herald pairs use in the boss stage.
      if (this._secondaryBoss) {
        this._secondaryBoss.update(enemyDt, this.player);
        this._secondarySpawning.update(enemyDt, this._secondaryBoss, this.player);
      }
      if (this.minionWaves.isStageCleared) this._startMinibossStage();
    } else if (this.stage === 'boss') {
      this._bossStageTime += dt;
      if (this.ascensionRules.bossEnrage && !this._bossEnraged && this._bossStageTime >= 90) {
        this._bossEnraged = true;
        this.player.incomingDamageMultiplier *= 1.35;
        this.eventBus.emit('ascension:enrage');
      }
      // During the herald-pair miniboss stage, one of the pair can die
      // (this.boss set to null by _onMinibossDied) while the other is still
      // up — see its "other half of the pair is still up" early return —
      // so both slots need their own null guard here, not just the
      // secondary's.
      if (this.boss) {
        // SpawningSystem can emit boss:died synchronously. The death handler
        // advances the stage and clears this.boss before this block returns,
        // so retain the tick's reference for post-update presentation logic.
        const activeBoss = this.boss;
        activeBoss.update(enemyDt, this.player);
        this.spawning.update(enemyDt, activeBoss, this.player);
        // After the spawning system, so the bullets a texture reacts to (the
        // Manifold's second body taking hits) are this frame's, not last's.
        this._updateAct3Systems(enemyDt, activeBoss);
        // Act II's adaptive themes respond to the rule being played: the
        // Swell gets fuller as the player's recent output rises, while Hush
        // drops back to its sparse layer while the player is muted.
        if (activeBoss.bossDef.spriteKey === 'swell' && this.boss === activeBoss) {
          const loudness = Math.min(1, (activeBoss.recentDamage ?? 0) / 2600);
          audio.setMusicIntensity(loudness > 0.66 ? 2 : loudness > 0.2 ? 1 : 0);
        } else if (activeBoss.bossDef.spriteKey === 'greatHush' && this.boss === activeBoss && this.player.muted) {
          audio.setMusicIntensity(0);
        }
      }
      if (this._secondaryBoss) {
        this._secondaryBoss.update(enemyDt, this.player);
        this._secondarySpawning.update(enemyDt, this._secondaryBoss, this.player);
      }
    }
    // 'warning' stage: nothing to update — this.paused is already true the
    // whole time (see _onBossDied), so this is really just belt-and-braces
    // against a null this.boss rather than load-bearing on its own.

    this.pickups.update(dt, this.player);
    // Outside the stage branches above: a chest dropped by a boss is collected
    // during the NEXT wave stage as often as during the boss stage it fell in,
    // and it survives the handoff between them for the same reason a mote does.
    this._updateSpoilsChestDrop(dt);
    this.cover.update(dt);
    if (this.ascensionRules.arenaPulses) {
      this._arenaPulseTimer -= dt;
      if (this._arenaPulseTimer <= 0) {
        this._arenaPulseTimer = 12;
        this.player.takeDamage(10, 'arenaPulse');
        this.eventBus.emit('ascension:arena-pulse');
      }
    }
    this.combat.update(dt, this.player, this.camera);
    // The Ballast: bend every projectile in flight toward the well. After both
    // pools have integrated this frame (the nudge lands on the next), and it
    // reaches across both — boss bullets AND the player's own shots.
    this._applyBallast(dt);
    this._applySinkweights(dt);
    this.progression.update(dt);

    // Orbit Ward shards delete boss bullets they overlap. Done here (not in
    // either system) because the player projectile pool (this.combat) and the
    // boss bullet pool (this.spawning) are owned by different systems and
    // never otherwise interact — Game.js is the one place that holds both.
    if (this.spawning) this._blockBossBullets();
    // The Countersong turns boss bullets around. Same reason it lives here as
    // the block pass above: the two pools are owned by different systems and
    // this file is the only place that holds both.
    this._convertBossBullets();
  }

  /**
   * Countersong (Act III, the game's first reflect): a boss bullet caught
   * inside the arc is consumed and sent back as a player projectile. One per
   * `reflectInterval` per arc, so a dense wall pays out steadily instead of
   * being turned into a single screen-clearing burst.
   *
   * Deliberately not a "delete the bullet" defensive tool — Orbit Ward gave
   * that up on purpose. The bullet is *given back*: what you get out is damage,
   * and what you pay is standing where the fire is.
   */
  _convertBossBullets() {
    const arcs = this.combat.weapons.filter((w) => typeof w.covers === 'function' && w.reflectReady && w.arcCenter);
    if (!arcs.length) return;
    const convertFrom = (spawning) => {
      if (!spawning) return;
      for (const arc of arcs) {
        if (!arc.reflectReady) continue;
        let caught = null;
        spawning.forEachBullet((bullet) => {
          if (caught || !bullet.active || !arc.covers(bullet.x, bullet.y)) return;
          caught = bullet;
        });
        if (!caught) continue;
        caught.active = false;
        arc.noteReflect();
        // Straight back the way it came. Aiming it at the boss instead would be
        // strictly better and would make positioning irrelevant; sending it
        // back along its own line means the reflect is worth what your angle
        // was worth.
        const speed = Math.hypot(caught.vx, caught.vy) || 1;
        this.combat.pool.fire({
          x: caught.x,
          y: caught.y,
          vx: (-caught.vx / speed) * arc.config.reflectSpeed,
          vy: (-caught.vy / speed) * arc.config.reflectSpeed,
          radius: caught.radius + 1,
          damage: arc.reflectDamage,
          hits: 1,
          lifetime: 1.6,
          color: arc.config.color,
        });
        this.eventBus.emit('countersong:reflected', { x: caught.x, y: caught.y, color: arc.config.color });
      }
    };
    convertFrom(this.spawning);
    convertFrom(this._secondarySpawning);
    // Minion fire counts too — the arc doesn't care who sang it.
    convertFrom(this.minionWaves);
  }

  /**
   * Bullet cancel (BULLET-HELL.md §D2). Deletes every live bullet in the run —
   * both bosses' pools and the minions' — and drops the motes they convert
   * into where they were standing. Returns how many motes were dropped, so the
   * phase-break handler knows how much of its fixed floor is still owed.
   *
   * Called from the two handlers in the constructor rather than from `Boss` or
   * `UltimateSystem`: this file is the only place that holds every pool and
   * the progression multiplier at once, and neither of those systems should
   * learn what a mote is to fire an event they already fire.
   */
  _cancelBullets(origin) {
    const { drops, cancelled } = cancelBullets(
      [this.spawning, this._secondarySpawning, this.minionWaves],
      {
        cap: CANCEL.maxMotes,
        // The chain multiplies the cancel's XP (BULLET-HELL.md §D5's "mote
        // value"). Applied HERE, at drop time, rather than at pickup: motes
        // carry banked XP and PickupSystem's conservation property — every
        // point banked is eventually paid, none created or lost — is the thing
        // that makes the mote field auditable. A multiplier at the pickup end
        // would break it. Bombing out of a curtain you were grazing is exactly
        // the moment §D2 wants to pay the most, so this is the right drop.
        xpMultiplier: this.progression.xpGainMultiplier * this.grazeChain.multiplier,
      },
    );
    if (!cancelled) return 0;
    for (const drop of drops) this.eventBus.emit('mote:dropped', drop);
    // The image the fight builds toward: the curtain visibly *becomes* the
    // reward rather than blinking out. Bursts only at the motes that dropped,
    // so a capped 200-bullet cancel doesn't also spawn 200 particle bursts.
    for (const drop of drops) {
      this.particles.burst(drop.x, drop.y, {
        count: 2, color: '#8ad1ff', life: 0.25, speedMin: 20, speedMax: 90, size: 1.2, additive: true,
      });
    }
    this.eventBus.emit('bullets:cancelled', {
      count: cancelled, x: origin?.x ?? this.player.x, y: origin?.y ?? this.player.y,
    });
    return drops.length;
  }

  /**
   * Hands a freshly built SpawningSystem Tine's tone stamp (BULLET-HELL.md
   * §D7's champion promotion), so every bullet it fires comes out toned and
   * recoloured. Returns the system so the four construction sites stay one
   * line each.
   *
   * Per-pool, not one shared caster: the stamp alternates tones, and a herald
   * pair firing through one alternator would let either boss's cadence decide
   * the other's checkerboard. Null for every champion but Tine, in which case
   * this is the identity function and no pool pays anything.
   *
   * Minion fire is deliberately NOT toned. It comes out of MinionWaveSystem's
   * own pool with its own collision path and no telegraph contract, and the
   * wave half is not where polarity is a read — toning it would turn a chase
   * into a colour-matching chore and hand out absorb income for standing in a
   * crowd. Tine's signature is how she fights BOSSES, in every act.
   */
  _attachToneCaster(spawning) {
    if (this._toneCaster && spawning?.pool) spawning.pool.toneCaster = this._toneCaster;
    // Every SpawningSystem the run builds passes through here, which makes it
    // the one place §B9's shootable-bullet pass can be guaranteed to see all
    // of them. The schedulers this replaces are dropped first so the list
    // never grows one entry per boss across a run — the ones still live are
    // whatever the two slots point at, and a moment later this one.
    for (const stale of [...(this.combat?.hostilePools ?? [])]) {
      if (stale !== this.spawning && stale !== this._secondarySpawning) {
        this.combat.removeHostilePool(stale);
      }
    }
    if (spawning) this.combat?.addHostilePool(spawning);
    return spawning;
  }

  /**
   * The resonance release (BULLET-HELL.md §D7). Every
   * POLARITY.stacksPerRelease absorbed bullets, the stored fire is handed back
   * as a fan of homing shots (geometry in polarityRelease.js).
   *
   * The three things only this file knows: which pool, what is nearest, and
   * what the build's damage multiplier is. Same division of labour as
   * `_cancelBullets` — a pure listener that counts absorbs should not have to
   * learn any of them.
   */
  _firePolarityRelease(x, y) {
    const player = this.player;
    if (!player || player.isDead) return;
    const target = this.combat.getNearestTarget(player);
    if (!target) {
      // Nothing to shoot: hand the stacks back rather than spending a release on
      // an empty room, so the bank stays full until an enemy is on screen.
      this.resonance.stacks = this.resonance.stacksPerRelease - 1;
      return;
    }
    const shots = fireRelease({
      player,
      target,
      pool: this.combat.pool,
      // The soft-capped damage read WITHOUT the weapon-count penalty: a release
      // is not a weapon and shouldn't be taxed for owning several. Scaling it at
      // all is what keeps a release worth earning in a late-run build.
      damageMultiplier: this.combat.effectiveDamageMultiplier,
    });
    if (!shots) return;
    this.eventBus.emit('polarity:released', { x: x ?? player.x, y: y ?? player.y, shots });
  }

  _blockBossBullets() {
    const blockAgainst = (spawning) => {
      if (!spawning) return;
      // Resonant Pillars eat bullets first (and pay HP for each one) —
      // beams/zones/charges never pass through here, which is exactly why
      // cover doesn't trivialize beam-heavy kits.
      this.cover.forEachAlive((pillar) => {
        spawning.forEachBullet((bullet) => {
          if (!bullet.active || !pillar.alive) return;
          const dist = Math.hypot(bullet.x - pillar.x, bullet.y - pillar.y);
          if (dist < bullet.radius + pillar.radius) {
            bullet.active = false;
            this.cover.damagePillar(pillar, bullet.damage);
            this.particles.burst(bullet.x, bullet.y, { count: 3, color: '#8ad1ff', life: 0.2, speedMin: 20, speedMax: 70, size: 1 });
          }
        });
      });
      this.combat.forEachBlockingProjectile((shard) => {
        spawning.forEachBullet((bullet) => {
          if (!bullet.active) return;
          const dist = Math.hypot(bullet.x - shard.x, bullet.y - shard.y);
          if (dist < bullet.radius + shard.radius) {
            bullet.active = false;
            this.particles.burst(bullet.x, bullet.y, { count: 3, color: shard.color, life: 0.2, speedMin: 20, speedMax: 70, size: 1 });
          }
        });
      });
    };
    blockAgainst(this.spawning);
    blockAgainst(this._secondarySpawning);
  }

  _render() {
    const { renderer, camera, player, combat } = this;
    renderer.beginFrame();
    const ctx = renderer.ctx;
    const tc = renderer.textCtx;

    this._drawArena(ctx, camera);
    this._drawPickups(ctx, camera);
    this._drawCover(ctx, camera);
    if (this.stage === 'boss') {
      // Minions can exist during a boss fight now — The Famished Court's
      // courtiers (see patterns/TheFeast.js). Empty for every other boss,
      // so this is a no-op loop outside that fight.
      this._drawMinions(ctx, camera);
      // this.boss can be null mid-frame here too (see the update-loop guard
      // above) — one herald of a pair dying before the other leaves the
      // primary slot empty while the stage is still 'boss'.
      if (this.boss) {
        this._drawAttackVisuals(ctx, camera, this.boss, this.spawning);
        this._drawBoss(ctx, camera, this.boss);
        this._drawBossName(tc, renderer, this.boss);
      }
      if (this._secondaryBoss) {
        this._drawAttackVisuals(ctx, camera, this._secondaryBoss, this._secondarySpawning);
        this._drawBoss(ctx, camera, this._secondaryBoss);
        this._drawBossName(tc, renderer, this._secondaryBoss);
      }
    } else if (this.stage === 'tutorial') {
      this._drawMinions(ctx, camera);
    } else if (this.stage === 'waves') {
      this._drawEchoRift(ctx, camera);
      // Under the minions: these are areas of effect, not the bodies casting
      // them, so the bodies must sit on top of their own zones.
      this._drawSinkweights(ctx, camera);
      this._drawMufflerZones(ctx, camera);
      this._drawMinions(ctx, camera);
      this._drawWardenLinks(ctx, camera);
      // Echo Duel ghost mid-wave-stage: full boss-grade rendering.
      // (_drawMinions skips isBossTier entries, so it isn't double-drawn
      // as a skirmisher sprite.)
      if (this._secondaryBoss) {
        this._drawAttackVisuals(ctx, camera, this._secondaryBoss, this._secondarySpawning);
        this._drawBoss(ctx, camera, this._secondaryBoss);
        this._drawBossName(tc, renderer, this._secondaryBoss);
      }
    }
    // 'warning' stage: nothing else to draw yet — boss/minionWaves don't
    // exist until FinalWarningOverlay's Continue button fires.
    this._drawUltimateField(ctx, camera);
    this._drawParticles(ctx, camera);
    this._drawProjectiles(ctx, camera, combat, this._playerFireAlpha());
    this._drawAura(ctx, camera, player, combat);
    // Quill's Inscription (Act III) — under the player, over the projectiles:
    // the glyphs are ground, not fire.
    this._drawGlyphs(ctx, camera, player);
    this._drawPlayer(ctx, camera, player);
    this._drawWeaponIcons(ctx, camera, player, combat);
    // BULLET-HELL.md §D0 — hostile bullets are the top world layer, above the
    // player's own effects and above the player itself. They used to be drawn
    // inside the stage branches above, which put every player-side effect
    // (ultimate field, particles, projectiles, aura, weapon icons) on top of
    // the one class of object the player must never lose track of — and the
    // occlusion got worse the stronger the build got, i.e. the screen was
    // least readable exactly when it was most dangerous.
    //
    // Still *below* screen effects and the Gloam: those are deliberate
    // whole-world treatments, and the Gloam hiding bullets is its mechanic.
    this._drawHostileBullets(ctx, camera);
    // …and the hull is the layer above even that (BULLET-HELL.md §D0). Focus
    // mode's dot IS the collision circle, so nothing in the world may cover
    // it — least of all the bullets it exists to be measured against. It used
    // to be drawn from inside _drawPlayer, which put it under the curtain.
    this._drawFocusHitbox(ctx, camera, player);
    // Last draw on the low-res world buffer, before any HUD text: flashes/
    // vignette tint the world without washing out the crisp text overlay —
    // that separation is the whole reason Renderer keeps two canvases.
    this._drawScreenEffects(ctx);
    // Act II — The Gloam: collapse the world to a lit disc around the player.
    // After the scene + screen effects (so it darkens everything drawn on the
    // world buffer) but before HUD text (which must stay readable).
    this._drawGloaming(ctx, camera);
    // Act II — The Antiphon: a ring in the player's current attunement tone.
    this._drawAttunement(ctx, camera);
    // Act II — The Ballast: the well's reach, as faint concentric rings.
    this._drawBallast(ctx, camera);
    // Act III — the three fight-long textures: the Tithe's collection ring,
    // the Manifold's second body, and the Verse's shrunken floor.
    this._drawAct3(ctx, camera);
    this._drawFloatingTexts(tc, renderer, camera);
    this._drawDamageNumbers(tc, renderer, camera);
    this._drawDeathbombLabel(tc, renderer, camera, player);
    this._drawPlayerHpArc(tc, renderer, player);
    this._drawPlayerDashPips(tc, renderer, player);
    this._drawCadenceMeter(tc, renderer, player);
    this._drawDebtMeter(tc, renderer, player);
    this._drawArmorPlates(tc, renderer, player);
    this._drawUltimateMeter(tc, renderer, player);
    this._drawGrazeChain(tc, renderer, player);
    this._drawResonance(tc, renderer, player);
    if (this.stage === 'boss') {
      // Card practice has no HP bar: the boss can't be hurt, so a bar pinned at
      // full for the whole session is a lie about what the player is doing. The
      // session's capture tally goes in its slot instead — that IS the progress
      // bar of a practice session.
      if (this.isCardPractice) this._drawPracticeBanner(tc, renderer);
      else if (this.boss) this._drawBossHpBar(tc, renderer, this.boss, 0);
      if (this._secondaryBoss) this._drawBossHpBar(tc, renderer, this._secondaryBoss, 1);
    } else if (this.stage === 'waves') {
      this._drawWaveHud(tc, renderer);
      if (this._secondaryBoss) this._drawBossHpBar(tc, renderer, this._secondaryBoss, 1);
    }
    this._drawHudGraphics(tc, renderer, player);
    this._drawHud(tc, renderer, player);
    this._drawTelegraphCallout(tc, renderer);
    this._drawCaptureBanner(tc, renderer);
    this._drawXpBar(tc, renderer, this.progression);
    this._drawControlsHint(tc, renderer);
    if (this.stage === 'tutorial') this._drawTutorialPrompt(tc, renderer);
    // Dev-only frame/particle readout — toggle from the console with
    // `__showPerf = true` (window.__game is already exposed in dev builds).
    if (typeof window !== 'undefined' && window.__showPerf) this._drawPerf(tc, renderer);

    renderer.present();
  }

  /**
   * How bright the player's own fire is allowed to be this frame
   * (BULLET-HELL.md §D0). Dimmed exactly when a hostile curtain is on screen —
   * the same stage test _drawHostileBullets uses, minus the wave-minion case,
   * whose bullets are sparse enough that hiding the player's damage would cost
   * more than it buys.
   */
  _playerFireAlpha() {
    const curtain = this.stage === 'boss' || this._secondaryBoss != null;
    return curtain ? RENDER.bossProjectileAlpha : 1;
  }

  /**
   * Every bullet that can hurt the player, drawn as one pass on top of the
   * whole world (see the call site in _render, and BULLET-HELL.md §D0).
   *
   * The stage conditions mirror the stage branches in _render exactly — this
   * is only a change of *when* these are drawn, never of which ones are.
   */
  _drawHostileBullets(ctx, camera) {
    if (this.stage === 'boss') {
      if (this.boss) this._drawBossBullets(ctx, camera, this.spawning);
      if (this._secondaryBoss) this._drawBossBullets(ctx, camera, this._secondarySpawning);
    } else if (this.stage === 'tutorial') {
      this._drawBossBullets(ctx, camera, this.tutorial);
    } else if (this.stage === 'waves') {
      this._drawMinionBullets(ctx, camera);
      if (this._secondaryBoss) this._drawBossBullets(ctx, camera, this._secondarySpawning);
    }
  }

  /**
   * Every bullet that can hurt the player, as data — the same set
   * _drawHostileBullets draws, by the same stage conditions. Split out because
   * the focus ring needs to *measure* the curtain, not paint it.
   */
  _forEachHostileBullet(fn) {
    if (this.stage === 'boss') {
      if (this.boss) this.spawning.forEachBullet(fn);
      if (this._secondaryBoss) this._secondarySpawning.forEachBullet(fn);
    } else if (this.stage === 'tutorial') {
      this.tutorial.forEachBullet(fn);
    } else if (this.stage === 'waves') {
      this.minionWaves.forEachBullet(fn);
      if (this._secondaryBoss) this._secondarySpawning.forEachBullet(fn);
    }
  }

  /**
   * Tracks the bullet radius the focus ring should be drawn for.
   *
   * The graze test is `dist < player.radius + bullet.radius + GRAZE.band`, so
   * the true near-miss boundary is not one circle — it grows with whatever is
   * being threaded. Drawing it at `PLAYER.radius + GRAZE.band` taught a ring
   * that was correct only for a zero-radius bullet, i.e. never: every real
   * bullet grazes further out than the ring showed, by its own radius.
   *
   * The ring is therefore sized for the bullet with the smallest *gap*
   * (surface-to-surface), which is precisely the bullet the boundary is about
   * to matter for. Eased at a fixed rate rather than snapped, because the
   * nearest bullet changes identity constantly inside a curtain and a ring
   * that popped between radii would read as noise instead of as a measurement.
   * With nothing in reach it relaxes to 0 — an honest "no bullet to be exact
   * about", and the old drawing.
   */
  _updateFocusRing(dt) {
    const player = this.player;
    let nearestGap = Infinity;
    let target = 0;
    if (player.focusReveal > 0) {
      this._forEachHostileBullet((bullet) => {
        const gap = Math.hypot(bullet.x - player.x, bullet.y - player.y)
          - bullet.radius - player.radius;
        if (gap < nearestGap) {
          nearestGap = gap;
          target = bullet.radius;
        }
      });
      // Out past the band by more than the band again, the bullet is not what
      // the player is reading, so it stops sizing the ring.
      if (nearestGap > GRAZE.band * 2) target = 0;
    }
    const step = dt / FOCUS.ringEaseDuration;
    const current = this._focusRingBulletRadius;
    this._focusRingBulletRadius = target > current
      ? Math.min(target, current + step * FOCUS.ringEaseSpan)
      : Math.max(target, current - step * FOCUS.ringEaseSpan);
  }

  /**
   * The tutorial's current instruction, centred over the arena: headline plus
   * the one sentence under it. Sits where the boss title card and telegraph
   * callout do, deliberately — it's teaching the player where to look for the
   * game's own callouts later.
   */
  _drawTutorialPrompt(tc, renderer) {
    const { title, body } = this.tutorial.getPrompt();
    const s = renderer.scale;
    const ox = renderer.offsetX;
    const oy = renderer.offsetY;
    const view = renderer.visibleVirtualBounds();
    tc.textAlign = 'center';
    tc.fillStyle = this.save.highContrast ? this._arenaInk : '#ffd166';
    tc.font = `bold ${Math.round(6 * s)}px ${UI_FONT}`;
    tc.fillText(title, ox + view.centerX * s, oy + (view.top + 14) * s);
    tc.fillStyle = '#a8a8c0';
    tc.font = `${Math.round(3.2 * s)}px ${UI_FONT}`;
    tc.fillText(body, ox + view.centerX * s, oy + (view.top + 20) * s);
  }

  /**
   * The opening controls reminder (ui/controlsHint.js), along the bottom of
   * the arena for the first few seconds of a run and then faded out. Driven
   * off progression.survivalTime rather than wall-clock so it doesn't burn
   * down behind a pause or a level-up pick.
   */
  _drawControlsHint(tc, renderer) {
    const alpha = controlsHintAlpha(this.progression.survivalTime);
    if (alpha <= 0) return;
    const s = renderer.scale;
    const view = renderer.visibleVirtualBounds();
    tc.save();
    tc.globalAlpha = alpha;
    tc.fillStyle = this.save.highContrast ? this._arenaInk : '#a8a8c0';
    tc.font = `${Math.round(3 * s)}px ${UI_FONT}`;
    tc.textAlign = 'center';
    // One line above the run timer, not on top of it. Both are centred on the
    // bottom edge, and at `bottom - 5` against the timer's `bottom - 4` they
    // overprinted for the whole time the hint was visible — which is the
    // opening seconds of every run, i.e. the only moments either of them is
    // being read. The HP meter occupies bottom-14..bottom-9 but is anchored
    // left, so the centre of this row is clear.
    tc.fillText(
      this._controlsHintText ??= controlsHintText(this.save),
      renderer.offsetX + view.centerX * s,
      renderer.offsetY + (view.bottom - 11) * s,
    );
    tc.restore();
  }

  _drawPerf(tc, renderer) {
    const s = renderer.scale;
    const view = renderer.visibleVirtualBounds();
    tc.fillStyle = '#4ade80';
    tc.font = `${Math.round(3 * s)}px ${UI_FONT}`;
    tc.textAlign = 'left';
    const fps = this._perfFrameMs > 0 ? (1000 / this._perfFrameMs).toFixed(0) : '—';
    tc.fillText(
      `${fps} fps · ${this._perfFrameMs?.toFixed(1) ?? '—'}ms · ${this.particles.activeCount}p · ${this.combat.pool.activeCount}proj`,
      renderer.offsetX + (view.left + 4) * s,
      renderer.offsetY + (view.top + 4) * s,
    );
  }

  _drawPickups(ctx, camera) {
    this._drawMotes(ctx, camera);
    this.pickups.forEachActive((pod) => {
      const p = camera.worldToScreen(pod.x, pod.y);
      drawSprite(ctx, HEALTH_POD_SPRITE, p.x, p.y);
    });
    this._drawSpoilsChestDrop(ctx, camera);
  }

  /**
   * The chest a boss left behind (BULLET-HEAVEN.md §H3): a cast shadow that
   * stays put, the sprite riding its drop arc (then bobbing) above it, and a
   * tier-colored glow. The shadow is what sells the arc as height rather than
   * as the chest sliding up the arena, and it doubles as the honest marker of
   * where the pickup circle actually is while the sprite is in the air.
   */
  _drawSpoilsChestDrop(ctx, camera) {
    const drop = this._spoilsDrop;
    if (!drop) return;
    const trim = SPOILS_CHEST_TRIM[drop.tier] ?? SPOILS_CHEST_TRIM.plain;
    const t = this.save.skipAnimations ? 0 : performance.now() / 1000;
    const lift = drop.liftAt(t);
    const ground = camera.worldToScreen(drop.x, drop.y);
    const p = camera.worldToScreen(drop.x, drop.y - lift);

    ctx.save();
    // Shrinks as the chest rises — the one cue that reads as height without
    // a second sprite.
    const shadowScale = 1 - Math.min(0.5, lift / 44);
    ctx.globalAlpha = 0.32 * shadowScale;
    ctx.fillStyle = '#000000';
    ctx.beginPath();
    ctx.ellipse(ground.x, ground.y + 8, 10 * shadowScale, 3.5 * shadowScale, 0, 0, Math.PI * 2);
    ctx.fill();

    // Glow: steady once landed, brighter and pulsing once the magnet has it,
    // so "it's coming to you" is visible rather than something the player has
    // to infer from the chest suddenly moving.
    ctx.globalAlpha = drop.magnetizing ? 0.5 + 0.25 * Math.sin(t * 9) : 0.28;
    ctx.fillStyle = trim;
    ctx.beginPath();
    ctx.arc(p.x, p.y, 14, 0, Math.PI * 2);
    ctx.fill();
    ctx.restore();

    drawSprite(ctx, SPOILS_CHEST_SPRITE, p.x, p.y, { paletteOverride: { M: trim } });
  }

  /**
   * XP motes (BULLET-HEAVEN.md §H1) — drawn under everything else in the
   * arena layer, since a late-run floor can hold hundreds and they must never
   * obscure a bullet or a telegraph.
   *
   * **Motes must not be mistakable for enemies.** They used to be small
   * filled diamonds, which was chosen to separate them from the round bullets
   * and did — but not from the *minions*, which are the things actually
   * sharing the floor with them. Both were small saturated shapes on a dark
   * grid, and MOTES.tiers made it worse by picking colours the minion
   * palettes already owned outright. A player scanning a crowded frame had no
   * one-glance rule for "walk into that" vs. "do not walk into that".
   *
   * So a mote is drawn with two things no creature in the game has:
   *
   *  - **A four-point sparkle**, not a body. Minion silhouettes come from
   *    buildSilhouette/finishCreature and are all convex spiky blobs; thin
   *    crossed arms are a shape the enemy vocabulary cannot produce.
   *  - **Emission** — a white core and an additive halo. Every creature is
   *    matte with a dark outline. Nothing that glows can hurt you.
   *
   * That pair reads at 2px, where hue does not, and it holds regardless of
   * what colour a future minion is given. MOTES.tiers' hue separation (see
   * moteLegibility.test.js) is the backstop behind it, not the mechanism.
   *
   * Static under skipAnimations: the bob is the only motion here and it
   * carries no information. Under reduceFlash the halo is dropped — the
   * sparkle and the white core still carry the whole distinction, so the
   * accessibility path loses luminance, never legibility.
   */
  _drawMotes(ctx, camera) {
    const bobbing = !this.save.skipAnimations;
    const halos = !this.save.reduceFlash;
    const t = bobbing ? performance.now() / 1000 : 0;
    ctx.save();

    // Halos first, in one additive batch. Two passes over the mote list beats
    // flipping globalCompositeOperation per mote — a late-run floor can hold
    // MOTES.maxActive of these, and the composite switch is the expensive
    // part, not the arc.
    if (halos) {
      ctx.globalCompositeOperation = 'lighter';
      ctx.globalAlpha = MOTES.haloAlpha;
      this.pickups.forEachMote((mote) => {
        const bob = bobbing ? Math.sin(t * 3 + mote.bobPhase) * 0.6 : 0;
        const p = camera.worldToScreen(mote.x, mote.y + bob);
        ctx.fillStyle = mote.color;
        ctx.beginPath();
        ctx.arc(p.x, p.y, mote.radius * MOTES.haloScale, 0, Math.PI * 2);
        ctx.fill();
      });
      ctx.globalCompositeOperation = 'source-over';
    }

    this.pickups.forEachMote((mote) => {
      const bob = bobbing ? Math.sin(t * 3 + mote.bobPhase) * 0.6 : 0;
      const p = camera.worldToScreen(mote.x, mote.y + bob);
      const r = mote.radius;
      const w = r * MOTES.sparkleSpread;
      // A mote about to stream in dims first — the field visibly "goes quiet"
      // a moment before it all rushes the player, which makes the vacuum read
      // as a consequence rather than as motes teleporting.
      ctx.globalAlpha = mote.vacuuming ? 0.75 : 1;

      // Four arms from a pinched waist: up/right/down/left, each tapering to
      // a point. One path, so it fills as a single star rather than as two
      // overlapping bars.
      ctx.fillStyle = mote.color;
      ctx.beginPath();
      ctx.moveTo(p.x, p.y - r);
      ctx.lineTo(p.x + w, p.y - w);
      ctx.lineTo(p.x + r, p.y);
      ctx.lineTo(p.x + w, p.y + w);
      ctx.lineTo(p.x, p.y + r);
      ctx.lineTo(p.x - w, p.y + w);
      ctx.lineTo(p.x - r, p.y);
      ctx.lineTo(p.x - w, p.y - w);
      ctx.closePath();
      ctx.fill();

      // The white core. Survives every accessibility path on purpose: it is
      // the cheapest, highest-contrast "this is a pickup" signal there is,
      // and it is the one thing that still separates a mote from a minion
      // for a player who cannot use the hue difference at all.
      ctx.fillStyle = '#ffffff';
      ctx.beginPath();
      ctx.arc(p.x, p.y, Math.max(0.7, r * 0.32), 0, Math.PI * 2);
      ctx.fill();
    });
    ctx.restore();
  }

  _drawEchoRift(ctx, camera) {
    if (!this._echoRift) return;
    const p = camera.worldToScreen(this._echoRift.x, this._echoRift.y);
    const t = performance.now() / 1000;
    const pulse = 1 + Math.sin(t * 3) * 0.15;
    const r = this._echoRift.radius * pulse;
    ctx.save();
    ctx.strokeStyle = '#c084fc';
    ctx.fillStyle = 'rgba(192, 132, 252, 0.35)';
    ctx.lineWidth = 1;
    ctx.beginPath();
    ctx.moveTo(p.x, p.y - r);
    ctx.lineTo(p.x + r * 0.65, p.y);
    ctx.lineTo(p.x, p.y + r);
    ctx.lineTo(p.x - r * 0.65, p.y);
    ctx.closePath();
    ctx.fill();
    ctx.stroke();
    ctx.globalAlpha = 0.4 + Math.sin(t * 3) * 0.2;
    ctx.beginPath();
    ctx.arc(p.x, p.y, r + 3, 0, Math.PI * 2);
    ctx.stroke();
    ctx.restore();
  }

  _drawCover(ctx, camera) {
    this.cover.forEachAlive((pillar) => {
      const p = camera.worldToScreen(pillar.x, pillar.y);
      const r = pillar.radius;
      const stage = pillar.crackStage;
      ctx.save();
      // Fades as it crumbles — HP is legible from across the arena without
      // needing a bar over a piece of scenery.
      ctx.globalAlpha = stage === 2 ? 0.55 : stage === 1 ? 0.75 : 0.9;
      ctx.fillStyle = pillar.hitFlashTimer > 0 ? '#ffffff' : '#8ad1ff';
      ctx.strokeStyle = '#d6ecff';
      ctx.lineWidth = 1;
      ctx.beginPath();
      ctx.moveTo(p.x, p.y - r);
      ctx.lineTo(p.x + r * 0.7, p.y);
      ctx.lineTo(p.x, p.y + r);
      ctx.lineTo(p.x - r * 0.7, p.y);
      ctx.closePath();
      ctx.fill();
      ctx.stroke();
      if (stage >= 1) {
        ctx.strokeStyle = '#2b4a66';
        ctx.beginPath();
        ctx.moveTo(p.x - r * 0.3, p.y - r * 0.4);
        ctx.lineTo(p.x + r * 0.1, p.y);
        ctx.lineTo(p.x - r * 0.2, p.y + r * 0.4);
        ctx.stroke();
      }
      if (stage >= 2) {
        ctx.strokeStyle = '#2b4a66';
        ctx.beginPath();
        ctx.moveTo(p.x + r * 0.35, p.y - r * 0.3);
        ctx.lineTo(p.x + r * 0.1, p.y + r * 0.2);
        ctx.stroke();
      }
      ctx.restore();
    });
  }

  _drawMinions(ctx, camera) {
    for (const minion of this.combat.minions) {
      // Boss-tier entries in the minion registry (the Echo Duel ghost, a
      // herald pair's second half) get real boss rendering elsewhere — the
      // sprite fallback below would draw them as skirmishers.
      if (minion.isBossTier) continue;
      const p = camera.worldToScreen(minion.x, minion.y);
      // The Anglerling (Act II) shows only its lure until it's close enough
      // to reveal — never truly invisible, just not yet a body. The lure is
      // drawn AT its position, so what you're tracking is honest.
      if (minion.revealed === false) {
        const pulse = this.save.reduceFlash ? 0.75 : 0.5 + 0.35 * Math.sin(performance.now() / 260 + minion.lurePhase);
        ctx.save();
        ctx.globalAlpha = pulse;
        ctx.fillStyle = '#6ea8ff';
        ctx.beginPath();
        ctx.arc(p.x, p.y, 1.4 * camera.zoom, 0, Math.PI * 2);
        ctx.fill();
        ctx.restore();
        continue;
      }
      const sprite = MINION_SPRITES[minion.type] ?? MINION_SPRITES.skirmisher;
      // Chargers/Casters/Trappers flash white during their telegraph window —
      // same "about to hurt you" tell as every other telegraphed attack in
      // this game. hitFlashTimer (set in each minion's takeDamage) is the
      // inverse cue: "you just hurt it." A shielded Warden gets its own
      // tint (lower priority than the transient flashes above) so its
      // invulnerability reads at a glance instead of only being discoverable
      // by watching a hit whiff.
      // The Mirror-Shade's tone is the single fact that decides whether your
      // fire does anything at all, so it colors the whole body rather than
      // being a small accent — same two hues the Antiphon fight uses, and the
      // same hues the player's own attunement ring shows.
      const toneTint = minion.tone ? (minion.tone === 'cold' ? '#4dc9ff' : '#ff8a4d') : null;
      const paletteOverride = (minion.hitFlashTimer > 0 || minion.isTelegraphing)
        ? { R: '#ffffff' }
        : minion.invulnerable ? { R: '#5ad1e6' }
          : toneTint ? { R: toneTint } : null;
      // Spawn flourish — purely cosmetic. The minion is fully live (moving,
      // firing, hittable) from its first frame exactly as before (see
      // CombatSystem.addMinion's spawnedAt stamp); this just materializes it
      // with a converging ring + scale-in instead of an instant pop.
      const sinceSpawn = minion.spawnedAt !== undefined ? performance.now() - minion.spawnedAt : Infinity;
      if (sinceSpawn < 300) {
        const t = sinceSpawn / 300;
        ctx.save();
        ctx.globalAlpha = 0.7 * (1 - t);
        ctx.strokeStyle = '#ffffff';
        ctx.lineWidth = 1;
        ctx.beginPath();
        ctx.arc(p.x, p.y, minion.radius + 9 * (1 - t), 0, Math.PI * 2);
        ctx.stroke();
        ctx.restore();
        ctx.save();
        const scaleIn = 0.6 + 0.4 * t;
        ctx.translate(p.x, p.y);
        ctx.scale(scaleIn, scaleIn);
        ctx.translate(-p.x, -p.y);
        drawSprite(ctx, sprite, p.x, p.y, { paletteOverride });
        ctx.restore();
        continue;
      }
      drawSprite(ctx, sprite, p.x, p.y, { paletteOverride });
    }
  }

  // A shielded Warden was previously only distinguishable by its own cyan
  // tint — the actual kill-order decision (which sibling to shoot first)
  // meant hovering over every minion to guess, or just whiffing on the
  // Warden to learn it the hard way. A dashed tether plus a matching ring
  // around linkedMinion makes "shoot that one first" readable at a glance.
  // Drawn after _drawMinions (not before) so the ring sits on top of the
  // sprite, same as the boss's vulnerable-ring convention.
  _drawWardenLinks(ctx, camera) {
    const pulse = 0.55 + Math.sin(performance.now() / 220) * 0.2;
    for (const minion of this.combat.minions) {
      if (minion.type !== 'warden' || !minion.invulnerable) continue;
      const link = minion.linkedMinion;
      if (!link || link.isDead) continue;
      const a = camera.worldToScreen(minion.x, minion.y);
      const b = camera.worldToScreen(link.x, link.y);
      ctx.save();
      ctx.strokeStyle = '#5ad1e6';
      ctx.globalAlpha = pulse;
      ctx.lineWidth = 1.5;
      ctx.setLineDash([3, 3]);
      ctx.beginPath();
      ctx.moveTo(a.x, a.y);
      ctx.lineTo(b.x, b.y);
      ctx.stroke();
      ctx.setLineDash([]);
      ctx.lineWidth = 2;
      ctx.globalAlpha = Math.min(1, pulse + 0.2);
      ctx.beginPath();
      ctx.arc(b.x, b.y, link.radius + 4, 0, Math.PI * 2);
      ctx.stroke();
      ctx.restore();
    }
  }

  _drawMinionBullets(ctx, camera) {
    this.minionWaves.forEachBullet((bullet) => {
      const p = camera.worldToScreen(bullet.x, bullet.y);
      this._drawHostileBulletBody(ctx, p, bullet);
      if (bullet.tone) this._drawToneSigil(ctx, p, bullet.tone, bullet.radius + 2);
    });
  }

  /**
   * The colour in-arena text and High Contrast overlays are drawn in.
   *
   * Everything in this file used to hard-code `#ffffff` for both, which was
   * correct for eighteen of the nineteen arenas — they are all near-black.
   * The Anthem ends Act III in full daylight (`floor: '#f2f0ea'`), where white
   * text is invisible and High Contrast, of all settings, is the worst
   * offender. Keyed off the floor's own lightness rather than a boss check, so
   * any future light arena inherits the fix. Cached because it is read dozens
   * of times a frame and the floor only changes between fights.
   */
  get _arenaInk() {
    const floor = this.bossDef?.arenaTheme?.floor ?? '#15151f';
    if (floor !== this._arenaInkFloor) {
      this._arenaInkFloor = floor;
      this._arenaInkColor = hexLuminance(floor) > 0.55 ? '#141118' : '#ffffff';
    }
    return this._arenaInkColor;
  }

  /**
   * Whether additive ('lighter') blending actually reads in this arena.
   *
   * Additive light is how the player's own shots are told apart from hostile
   * fire (see _drawProjectiles) — but it only works over a dark floor. On The
   * Anthem's near-white arena everything additive saturates straight to white
   * and the player's shots would vanish into the ground, which is precisely
   * the arena where losing track of them matters most. Derived from the same
   * cached floor-lightness check as _arenaInk, so there is one place that
   * decides what "light arena" means and it cannot drift.
   */
  get _additiveSafe() {
    return this._arenaInk === '#ffffff';
  }

  /**
   * The wave-stage banner: how much of this stage is left before the boss.
   *
   * It used to read `THE HOLLOW SOVEREIGN INCOMING` over a bar in the boss's
   * own accent colour, in the exact screen position the boss fight then puts
   * its HP bar. For the whole hundred seconds of a wave stage that is a boss
   * name above a filling bar — which is a boss health bar in every game the
   * player has ever seen. It is not one: it is stage progress, and it fills
   * as the *player* survives rather than as the boss is damaged, so the one
   * inference a player naturally draws from it is exactly backwards.
   *
   * Two changes, both about saying what it is:
   *   - The countdown is stated in seconds and the verb is `IN`, not
   *     `INCOMING`. A number that visibly ticks down cannot be mistaken for
   *     a health total.
   *   - The bar EMPTIES as the stage runs out, and is drawn in neutral HUD
   *     ink rather than the boss's accent. A depleting neutral bar and a
   *     depleting red boss bar are different objects at a glance.
   */
  _drawWaveHud(tc, renderer) {
    const s = renderer.scale;
    const ox = renderer.offsetX;
    const oy = renderer.offsetY;
    const view = renderer.visibleVirtualBounds();
    tc.fillStyle = this._arenaInk;
    tc.font = `${Math.round(4 * s)}px ${UI_FONT}`;
    tc.textAlign = 'center';

    // Ramp mode has no waves to count (BULLET-HEAVEN.md §H2) — the stage is
    // one continuous rise on a clock. A wave counter there would be a number
    // describing something the player cannot see happening.
    const remaining = this.minionWaves.rampMode
      ? Math.max(0, 1 - this.minionWaves.rampProgress)
      : Math.max(0, 1 - Math.min(1, this.minionWaves.waveIndex / this.minionWaves.waveConfig.waveCount));
    const secondsLeft = this.minionWaves.rampMode
      ? Math.ceil(remaining * this.minionWaves.stageDuration)
      : null;

    const name = this.bossDef.name.toUpperCase();
    const label = secondsLeft === null
      ? `WAVE ${Math.min(this.minionWaves.waveIndex, this.minionWaves.waveConfig.waveCount)}/${this.minionWaves.waveConfig.waveCount} — ${name} NEXT`
      : `${name} IN ${secondsLeft}s`;
    tc.fillText(label, ox + view.centerX * s, oy + (view.top + 10) * s);

    // Emptying, and in HUD ink rather than the boss's accent — see the header.
    tc.fillStyle = '#2a2a3a';
    tc.fillRect(ox + (view.centerX - 36) * s, oy + (view.top + 13) * s, 72 * s, 2 * s);
    tc.fillStyle = this._hudInk;
    tc.fillRect(ox + (view.centerX - 36) * s, oy + (view.top + 13) * s, 72 * s * remaining, 2 * s);
  }

  // Two passes: normal alpha-blended particles first, then every additive
  // particle under a single 'lighter' composite switch — at most two
  // composite-mode changes per frame no matter how many particles are live.
  _drawParticles(ctx, camera) {
    this._drawParticlePass(ctx, camera, false);
    ctx.save();
    ctx.globalCompositeOperation = 'lighter';
    this._drawParticlePass(ctx, camera, true);
    ctx.restore();
  }

  _drawParticlePass(ctx, camera, additive) {
    this.particles.forEach((p) => {
      if (p.additive !== additive) return;
      const s = camera.worldToScreen(p.x, p.y);
      const lifeFrac = Math.max(0, p.life / p.maxLife);
      ctx.globalAlpha = lifeFrac;
      if (p.shape === 'spark') {
        // Motion-stretched streak along the particle's velocity — reads as
        // a hot spark for the cost of one stroked segment.
        ctx.strokeStyle = p.color;
        ctx.lineWidth = Math.max(0.6, p.size * 0.6);
        ctx.beginPath();
        ctx.moveTo(s.x, s.y);
        ctx.lineTo(s.x - p.vx * 0.035, s.y - p.vy * 0.035);
        ctx.stroke();
      } else if (p.shape === 'square') {
        const half = p.size * (0.5 + 0.5 * lifeFrac);
        ctx.fillStyle = p.color;
        ctx.fillRect(s.x - half, s.y - half, half * 2, half * 2);
      } else {
        // Circles shrink slightly as they fade — burning out, not blinking off.
        ctx.fillStyle = p.color;
        ctx.beginPath();
        ctx.arc(s.x, s.y, p.size * (0.55 + 0.45 * lifeFrac), 0, Math.PI * 2);
        ctx.fill();
      }
    });
    ctx.globalAlpha = 1;
  }

  // Full-screen tints on the low-res world buffer. Flash state only exists
  // when reduceFlash is off (JuiceDirector.flashScreen skips creating it).
  // The low-HP vignette is skipped under highContrast (it lowers edge
  // contrast by design) and is dimmer + static under reduceFlash.
  _drawScreenEffects(ctx) {
    const flash = this.juice.screenFlash;
    if (flash) {
      ctx.save();
      ctx.globalAlpha = Math.max(0, flash.life / flash.maxLife) * flash.strength;
      ctx.fillStyle = flash.color;
      ctx.fillRect(0, 0, VIRTUAL_WIDTH, VIRTUAL_HEIGHT);
      ctx.restore();
    }
    const hpFraction = this.player.hp / this.player.maxHp;
    if (hpFraction <= 0.3 && this.player.hp > 0 && !this.save.highContrast) {
      const urgency = 1 - hpFraction / 0.3; // 0 at the 30% threshold -> 1 at death's door
      const pulse = this.save.reduceFlash ? 1 : 0.8 + 0.2 * Math.sin(performance.now() / 260);
      const alpha = (0.12 + 0.16 * urgency) * pulse * (this.save.reduceFlash ? 0.6 : 1);
      const cx = VIRTUAL_WIDTH / 2;
      const cy = VIRTUAL_HEIGHT / 2;
      const g = ctx.createRadialGradient(cx, cy, Math.min(cx, cy) * 0.8, cx, cy, Math.hypot(cx, cy));
      g.addColorStop(0, 'rgba(229, 72, 77, 0)');
      g.addColorStop(1, `rgba(229, 72, 77, ${alpha.toFixed(3)})`);
      ctx.fillStyle = g;
      ctx.fillRect(0, 0, VIRTUAL_WIDTH, VIRTUAL_HEIGHT);
    }
  }

  // Act II — The Gloam's signature (data/act2Bosses.js `gloaming`). The arena
  // renders near-black except a lit disc around the player: transparent within
  // `radius`, fading to `darkness` opacity across `fadeBand`, flat dark beyond
  // (a radial gradient filled over the whole buffer clamps to its outer stop).
  // The radius shrinks per boss phase and is briefly snuffed by Faltering
  // Lantern via `boss._gloamRadiusMult` (unset -> 1). Only The Gloam sets
  // `bossDef.gloaming`, so every other fight skips this entirely. It's a core
  // mechanic, not juice, so it survives reduceFlash — but it softens under
  // highContrast so low-vision players keep more of the field.
  /**
   * Widest light any equipped weapon carries (Umbral Lantern, Halo, Lumen
   * Lance — see ACT2-BIBLE.md §7). 0 for every other build, which is the only
   * reason a darkness fight is dark: the counter is a pick, not a setting.
   */
  _carriedLightRadius() {
    let widest = 0;
    for (const weapon of this.combat.weapons) {
      if (weapon.lightRadius > widest) widest = weapon.lightRadius;
    }
    return widest;
  }

  _drawGloaming(ctx, camera) {
    if (this.stage !== 'boss' || !this.boss) return;
    const g = this.boss.bossDef?.gloaming;
    if (!g) return;
    const phase = this.boss.phase ?? 0;
    // Optional per-phase gate — lets the Undersong finale have the dark "creep
    // in" at a later phase. The Gloam itself omits minPhase (defaults 0 = on).
    if (phase < (g.minPhase ?? 0)) return;
    const base = Math.max(g.minRadius ?? 50, (g.visionRadius ?? 90) - phase * (g.radiusPerPhase ?? 12));
    // Act II — the Umbral Lantern (and its fusions) carry their own light, so
    // a build that packs one genuinely sees more of a darkness fight. The
    // largest light wins rather than summing them: two lanterns shouldn't
    // erase the mechanic outright.
    const worldRadius = Math.max(base, this._carriedLightRadius());
    const mult = this.boss._gloamRadiusMult ?? 1;
    let radius = worldRadius * mult * camera.zoom;
    let darkness = g.darkness ?? 0.95;
    if (this.save.highContrast) {
      radius *= 1.35;
      darkness = Math.min(darkness, 0.7);
    }
    const fade = (g.fadeBand ?? 24) * camera.zoom;
    const p = camera.worldToScreen(this.player.x, this.player.y);
    const grad = ctx.createRadialGradient(p.x, p.y, Math.max(1, radius), p.x, p.y, radius + fade);
    grad.addColorStop(0, 'rgba(3, 4, 8, 0)');
    grad.addColorStop(1, `rgba(3, 4, 8, ${darkness.toFixed(3)})`);
    ctx.save();
    ctx.fillStyle = grad;
    ctx.fillRect(0, 0, VIRTUAL_WIDTH, VIRTUAL_HEIGHT);
    ctx.restore();
    this._drawGloamTelegraphGlow(ctx, camera);
  }

  _drawGloamTelegraphGlow(ctx, camera) {
    if (!this.spawning) return;
    for (const visual of this.spawning.getVisuals(this.boss)) {
      const points = visual.zones ?? (visual.x !== undefined ? [visual] : []);
      for (const point of points) {
        if (point.x === undefined) continue;
        const p = camera.worldToScreen(point.x, point.y);
        ctx.save();
        ctx.globalAlpha = 0.8;
        ctx.strokeStyle = '#e0e6ff';
        ctx.lineWidth = 1.5;
        ctx.setLineDash([2, 2]);
        ctx.beginPath();
        ctx.arc(p.x, p.y, point.radius ?? (this.boss.radius + 8), 0, Math.PI * 2);
        ctx.stroke();
        ctx.setLineDash([]);
        ctx.restore();
      }
    }
  }

  // Act II — The Antiphon's signature (data/act2Bosses.js `polarity`). A thin
  // ring around the player in their current attunement tone (flipped by dash,
  // Player.attunement) — the readout that tells you which tone is currently
  // passing through you and which is biting. Only the Antiphon sets
  // `bossDef.polarity`, so no other fight draws it.
  _drawAttunement(ctx, camera) {
    if (this.stage !== 'boss' || !this.boss) return;
    // Either boss: heralds fight in pairs, so the Antiphon-Echo warden (which
    // inherits `polarity` from its parent def) can be the SECONDARY body. Its
    // toned bullets obey the tone rule from whichever pool they came out of, so
    // the readout has to appear either way — before §D7 gave absorbing a payoff
    // this only cost the player a hint; now it would hide an income stream.
    // Tine (BULLET-HELL.md §D7's champion promotion) tones every bullet in
    // every fight, so she carries the readout into fights whose boss has no
    // `polarity` of its own — without it her whole run would be played blind to
    // the one fact her signature is about. Her signature's hues are the
    // Antiphon's, so the ring means exactly what it means in Act II.
    const pol = this.boss.bossDef?.polarity ?? this._secondaryBoss?.bossDef?.polarity ?? this._attuneConfig;
    if (!pol) return;
    const color = this.player.attunement === 'cold' ? pol.coldColor : pol.warmColor;
    const p = camera.worldToScreen(this.player.x, this.player.y);
    const r = (this.player.radius + 3.5) * camera.zoom;
    ctx.save();
    // Tine's flip cooldown, read as the ring dimming and filling back in: the
    // key she plays the run on is read off the same glyph as the tone it
    // changes, rather than as a pip somewhere else on the HUD. Full brightness
    // for every other champion, whose ring never has a cooldown to show.
    ctx.globalAlpha = this._attuneConfig ? 0.3 + 0.6 * this.player.attuneFlipFraction : 0.9;
    ctx.strokeStyle = color;
    ctx.lineWidth = 1.5;
    ctx.beginPath();
    ctx.arc(p.x, p.y, r, 0, Math.PI * 2);
    ctx.stroke();
    // Resonance banked toward the next release (BULLET-HELL.md §D7), drawn as a
    // violet arc creeping around the same ring from the top. Deliberately the
    // ring the player is already watching to read their tone: the two facts
    // ("which tone passes through me" and "how close is my release") are read
    // at the same instant and are about the same act, so they share one glyph
    // instead of competing for attention with the bullets.
    const fill = this.resonance.releaseFraction;
    if (fill > 0) {
      ctx.globalAlpha = 1;
      ctx.strokeStyle = this.save.highContrast ? this._arenaInk : POLARITY.color;
      ctx.lineWidth = 2;
      ctx.beginPath();
      ctx.arc(p.x, p.y, r + 1.5 * camera.zoom, -Math.PI / 2, -Math.PI / 2 + fill * Math.PI * 2);
      ctx.stroke();
    }
    ctx.restore();
  }

  // The Ballast (Act II) — bends every projectile in flight toward the well at
  // the boss's heart, across BOTH pools (boss bullets and the player's own
  // shots). Pure physics lives in gravityWell.js; this just feeds it each
  // pool. Only the Ballast sets `bossDef.ballast`, so no other fight pays for
  // the per-projectile pass.
  _applyBallast(dt) {
    if (this.stage !== 'boss' || !this.boss) return;
    const cfg = this.boss.bossDef?.ballast;
    if (!cfg) return;
    // Optional per-phase gate — the Undersong's well "forms" a phase in.
    if ((this.boss.phase ?? 0) < (cfg.minPhase ?? 0)) return;
    const t = performance.now() / 1000;
    const x = this.boss.x + Math.sin(t * (cfg.driftSpeed ?? 0.35)) * (cfg.driftRadius ?? 0);
    const y = this.boss.y + Math.cos(t * (cfg.driftSpeed ?? 0.35)) * (cfg.driftRadius ?? 0);
    const inverted = this.boss.phase >= (cfg.invertPhase ?? Infinity) && Math.sin(t * 3) > 0;
    const strength = inverted ? -cfg.wellStrength : cfg.wellStrength;
    this._ballastWell = { x, y, radius: cfg.wellRadius, inverted };
    this.spawning.pool.forEachActive((b) => applyGravityWell(b, x, y, strength, cfg.wellRadius, dt));
    if (cfg.affectsPlayerFire !== false) {
      this.combat.pool.forEachActive((p) => applyGravityWell(p, x, y, strength, cfg.wellRadius, dt));
    }
  }

  /**
   * The Sinkweight (Act II, entities/Sinkweight.js) bends the player's own
   * fire toward it, through the same `applyGravityWell` the Ballast fight uses
   * — one implementation, so a wave stage teaches exactly the lead the boss
   * will demand. The entity applies its own drag on the *player*; only the
   * projectile half needs the pool, which is why it lives here.
   *
   * Costs nothing when no Sinkweight is on the field: the loop simply doesn't
   * run. Only player shots bend — minion bullets curving toward their own
   * sibling would read as a bug rather than a mechanic.
   */
  _applySinkweights(dt) {
    if (this.stage !== 'waves') return;
    for (const minion of this.combat.minions) {
      if (!minion.well || minion.isDead) continue;
      const { x, y, well } = minion;
      this.combat.pool.forEachActive((p) => applyGravityWell(p, x, y, well.strength, well.radius, dt));
    }
  }

  // The Sinkweight's reach, drawn the same way the Ballast's is — the pull has
  // to be visible or it just feels like the weapon is broken.
  _drawSinkweights(ctx, camera) {
    for (const minion of this.combat.minions) {
      if (!minion.well) continue;
      const p = camera.worldToScreen(minion.x, minion.y);
      ctx.save();
      ctx.strokeStyle = '#b98cff';
      ctx.globalAlpha = 0.16;
      ctx.lineWidth = 1;
      ctx.beginPath();
      ctx.arc(p.x, p.y, minion.well.radius * camera.zoom, 0, Math.PI * 2);
      ctx.stroke();
      ctx.restore();
    }
  }

  // The Muffler's bubble of silence — the one minion tell the player must be
  // able to see the EDGE of, since the whole counterplay is stepping outside
  // it. Brightens while it's actually muting you.
  _drawMufflerZones(ctx, camera) {
    for (const minion of this.combat.minions) {
      if (minion.hushRadius === undefined) continue;
      const p = camera.worldToScreen(minion.x, minion.y);
      ctx.save();
      ctx.strokeStyle = '#9fb0a8';
      ctx.globalAlpha = minion.hushing ? 0.55 : 0.22;
      ctx.lineWidth = 1;
      ctx.setLineDash([3, 3]);
      ctx.beginPath();
      ctx.arc(p.x, p.y, minion.hushRadius * camera.zoom, 0, Math.PI * 2);
      ctx.stroke();
      ctx.setLineDash([]);
      ctx.restore();
    }
  }

  // The Ballast's visual tell — faint concentric rings marking the well's
  // reach so the pull is legible, not mysterious. Static (flash-safe).
  _drawBallast(ctx, camera) {
    if (this.stage !== 'boss' || !this.boss) return;
    const cfg = this.boss.bossDef?.ballast;
    if (!cfg) return;
    const well = this._ballastWell ?? { x: this.boss.x, y: this.boss.y, radius: cfg.wellRadius, inverted: false };
    const p = camera.worldToScreen(well.x, well.y);
    const R = well.radius * camera.zoom;
    ctx.save();
    ctx.strokeStyle = well.inverted ? '#ffb86b' : (this.boss.bossDef.arenaTheme?.accent ?? '#b98cff');
    ctx.globalAlpha = 0.16;
    ctx.lineWidth = 1;
    for (let i = 1; i <= 3; i++) {
      ctx.beginPath();
      ctx.arc(p.x, p.y, R * (i / 3), 0, Math.PI * 2);
      ctx.stroke();
    }
    ctx.restore();
  }

  /**
   * Act III's fight-long textures (systems/act3/). Each is drawn only when the
   * fight declared it and its phase gate is open, so every other fight in the
   * game pays three null checks and nothing else.
   *
   * All three are readability-critical rather than decorative: the levy ring is
   * the only tell that the boss is immune and what to do about it; the mirror
   * bodies are a health bar you can't see any other way; the narrowed floor is
   * a hazard boundary. High contrast is honoured throughout for that reason.
   */
  _drawAct3(ctx, camera) {
    if (this.stage !== 'boss') return;
    const highContrast = this.save.highContrast;
    const accent = this.boss?.bossDef?.arenaTheme?.accent ?? '#ffd166';

    // --- The Verse: the singable floor, when `narrow` has been written -------
    if (this._verse) {
      const v = this._verse.getVisual();
      if (v.narrowed) {
        const p = camera.worldToScreen(v.x, v.y);
        ctx.save();
        ctx.strokeStyle = highContrast ? this._arenaInk : accent;
        ctx.globalAlpha = 0.5;
        ctx.setLineDash([5, 4]);
        ctx.lineWidth = 1.5;
        ctx.beginPath();
        ctx.arc(p.x, p.y, v.radius * camera.zoom, 0, Math.PI * 2);
        ctx.stroke();
        ctx.restore();
      }
    }

    // --- The Tithe: the collection ring -------------------------------------
    if (this._levy) {
      const v = this._levy.getVisual();
      const p = camera.worldToScreen(v.x, v.y);
      const r = v.radius * camera.zoom;
      ctx.save();
      if (v.state === 'open') {
        // Paid: the window is open and the boss is hittable. Solid and bright —
        // this is the only stretch of the fight where damage means anything.
        ctx.strokeStyle = highContrast ? this._arenaInk : '#7fe0a0';
        ctx.globalAlpha = 0.75;
        ctx.lineWidth = 2;
        ctx.beginPath();
        ctx.arc(p.x, p.y, r, 0, Math.PI * 2);
        ctx.stroke();
      } else if (v.state === 'collecting') {
        // Unpaid: dashed, and tightening as Arrears approaches, so the clock is
        // visible on the ring itself rather than only in the prompt.
        ctx.strokeStyle = highContrast ? this._arenaInk : '#d9a441';
        ctx.globalAlpha = 0.35 + v.urgency * 0.5;
        ctx.setLineDash([4, 3]);
        ctx.lineWidth = 1 + v.urgency;
        ctx.beginPath();
        ctx.arc(p.x, p.y, r * (1 - v.urgency * 0.25), 0, Math.PI * 2);
        ctx.stroke();
        ctx.setLineDash([]);
      } else {
        ctx.strokeStyle = highContrast ? this._arenaInk : '#d9a441';
        ctx.globalAlpha = 0.14;
        ctx.lineWidth = 1;
        ctx.beginPath();
        ctx.arc(p.x, p.y, r, 0, Math.PI * 2);
        ctx.stroke();
      }
      ctx.restore();
    }

    // --- The Manifold: the second body (and the mirror line) ----------------
    if (this._manifold) {
      const v = this._manifold.getVisual();
      if (v.bodies.length) {
        ctx.save();
        for (const body of v.bodies) {
          const p = camera.worldToScreen(body.x, body.y);
          const r = v.radius * camera.zoom;
          // Drawn as an outline rather than a filled body: it must read as
          // *yours* and as *not the one you are looking through*, at a glance,
          // in a screen full of bullets.
          ctx.strokeStyle = highContrast ? this._arenaInk : '#66d9ff';
          ctx.globalAlpha = 0.9;
          ctx.lineWidth = 1.5;
          ctx.beginPath();
          ctx.arc(p.x, p.y, r, 0, Math.PI * 2);
          ctx.stroke();
          ctx.globalAlpha = 0.2;
          ctx.fillStyle = highContrast ? this._arenaInk : '#66d9ff';
          ctx.fill();
          // The tether back to the real body — the shared HP pool, made visible.
          const me = camera.worldToScreen(this.player.x, this.player.y);
          ctx.globalAlpha = v.assist ? 0.1 : 0.18;
          ctx.lineWidth = 1;
          ctx.setLineDash([3, 4]);
          ctx.beginPath();
          ctx.moveTo(me.x, me.y);
          ctx.lineTo(p.x, p.y);
          ctx.stroke();
          ctx.setLineDash([]);
        }
        ctx.restore();
      }
    }
  }

  // Drawn on the high-res text-overlay canvas (like _drawHudGraphics/_drawXpBar)
  // rather than the low-res virtual buffer, so the bar's edges and fill stay
  // crisp instead of nearest-neighbor-scaled into blocky pixels.
  /** `slot` stacks a second bar below the first when a herald pair is up (see _spawnMinibossFromQueue) — 0 for the primary, 1 for the secondary. */
  _drawBossHpBar(tc, renderer, boss, slot = 0) {
    const s = renderer.scale;
    const ox = renderer.offsetX;
    const oy = renderer.offsetY;
    const view = renderer.visibleVirtualBounds();
    const w = VIRTUAL_WIDTH * 0.7;
    const h = 4;
    const x = view.centerX - w / 2;
    const y = view.top + 10 + slot * 8;
    tc.fillStyle = '#2a2a3a';
    tc.fillRect(ox + x * s, oy + y * s, w * s, h * s);
    tc.fillStyle = '#e5484d';
    tc.fillRect(ox + x * s, oy + y * s, w * s * boss.hpFraction, h * s);
    tc.strokeStyle = '#ffffff';
    tc.lineWidth = 0.5 * s;
    tc.strokeRect(ox + x * s, oy + y * s, w * s, h * s);
  }

  /**
   * Card practice's one piece of HUD (BULLET-HELL.md §D8), in the boss HP
   * bar's slot: which card is being drilled, and how many times it's been
   * captured out of the times it's run this session. The lifetime record is a
   * menu's job; what the player needs mid-drill is "am I getting it *now*".
   */
  _drawPracticeBanner(tc, renderer) {
    const s = renderer.scale;
    const view = renderer.visibleVirtualBounds();
    const { captures, attempts } = this.captureTracker;
    tc.textAlign = 'center';
    tc.fillStyle = this.save.highContrast ? this._arenaInk : '#7e97ff';
    tc.font = `bold ${Math.round(4 * s)}px ${UI_FONT}`;
    tc.fillText(
      `PRACTICE · ${this.practicePattern.toUpperCase()} · ${captures}/${attempts}`,
      renderer.offsetX + view.centerX * s,
      renderer.offsetY + (view.top + 13) * s,
    );
  }

  _drawBossName(tc, renderer, boss) {
    const p = this.camera.worldToScreen(boss.x, boss.y);
    const s = renderer.scale;
    const ox = renderer.offsetX;
    const oy = renderer.offsetY;
    tc.fillStyle = this._arenaInk;
    tc.font = `bold ${Math.round(4 * s)}px ${UI_FONT}`;
    tc.textAlign = 'center';
    // boss.bossDef (not this.bossDef, the game-level "current gauntlet boss"
    // tracker) so a miniboss's own name shows here — this.bossDef stays
    // pointed at the real upcoming boss the whole time the herald is up
    // (see _startMinibossStage). Identical result for a real boss fight,
    // since boss.bossDef is either this.bossDef itself or a same-.name
    // spread copy of it (extraBossLayer ascension).
    tc.fillText(boss.bossDef.name.toUpperCase(), ox + p.x * s, oy + (p.y - boss.radius - 4) * s);
  }

  /** "LEVEL UP!" text spawned by xp:levelup:gained — drifts up and fades out over its life (see _addFloatingText). */
  _drawFloatingTexts(tc, renderer, camera) {
    const s = renderer.scale;
    const ox = renderer.offsetX;
    const oy = renderer.offsetY;
    tc.font = `bold ${Math.round(5 * s)}px ${UI_FONT}`;
    tc.textAlign = 'center';
    for (const t of this.juice.floatingTexts) {
      const elapsed = 1 - t.life / t.maxLife; // 0 -> 1 over its life
      const p = camera.worldToScreen(t.x, t.y - elapsed * 16);
      tc.globalAlpha = Math.min(1, t.life / t.maxLife * 2); // holds solid, fades over the back half
      tc.fillStyle = t.color;
      tc.fillText(t.text, ox + p.x * s, oy + p.y * s);
      tc.globalAlpha = 1;
    }
  }

  _drawDamageNumbers(tc, renderer, camera) {
    const s = renderer.scale;
    const ox = renderer.offsetX;
    const oy = renderer.offsetY;
    tc.textAlign = 'center';
    for (const n of this.juice.damageNumbers) {
      const progress = 1 - n.life / n.maxLife;
      // Spawn pop: slightly oversized for the first quarter of its life,
      // easing back to normal — the number "lands" instead of just appearing.
      const pop = progress < 0.25 ? 1 + (1 - progress / 0.25) * 0.3 : 1;
      tc.font = `bold ${Math.round(4 * s * pop)}px ${UI_FONT}`;
      const p = camera.worldToScreen(n.x, n.y - progress * n.rise);
      tc.globalAlpha = Math.min(1, n.life / n.maxLife * 2);
      tc.fillStyle = n.color;
      tc.fillText(n.text, ox + p.x * s, oy + p.y * s);
    }
    tc.globalAlpha = 1;
  }

  _drawArena(ctx, camera) {
    const topLeft = camera.worldToScreen(ARENA.x, ARENA.y);
    const theme = this.bossDef?.arenaTheme ?? { floor: '#15151f', line: '#3a3a52', accent: '#7e97ff' };
    ctx.fillStyle = theme.floor;
    ctx.fillRect(topLeft.x, topLeft.y, ARENA.width, ARENA.height);
    ctx.save();
    ctx.globalAlpha = 0.16;
    ctx.strokeStyle = theme.accent;
    const grid = 35;
    for (let x = ARENA.x + grid; x < ARENA.x + ARENA.width; x += grid) {
      const a = camera.worldToScreen(x, ARENA.y); const b = camera.worldToScreen(x, ARENA.y + ARENA.height);
      ctx.beginPath(); ctx.moveTo(a.x, a.y); ctx.lineTo(b.x, b.y); ctx.stroke();
    }
    for (let y = ARENA.y + grid; y < ARENA.y + ARENA.height; y += grid) {
      const a = camera.worldToScreen(ARENA.x, y); const b = camera.worldToScreen(ARENA.x + ARENA.width, y);
      ctx.beginPath(); ctx.moveTo(a.x, a.y); ctx.lineTo(b.x, b.y); ctx.stroke();
    }
    ctx.restore();
    const pulse = 0.04 + (this.juice.phasePulse > 0 ? this.juice.phasePulse * 0.12 : 0);
    const center = camera.worldToScreen(ARENA.x + ARENA.width / 2, ARENA.y + ARENA.height / 2);
    const radius = Math.max(ARENA.width, ARENA.height) * 0.55;
    const light = ctx.createRadialGradient(center.x, center.y, 20, center.x, center.y, radius);
    light.addColorStop(0, `${theme.accent}00`);
    light.addColorStop(1, `${theme.accent}${Math.round(pulse * 255).toString(16).padStart(2, '0')}`);
    ctx.fillStyle = light;
    ctx.fillRect(topLeft.x, topLeft.y, ARENA.width, ARENA.height);
    ctx.strokeStyle = theme.line;
    ctx.lineWidth = 1;
    ctx.strokeRect(topLeft.x, topLeft.y, ARENA.width, ARENA.height);
  }

  _drawBoss(ctx, camera, boss) {
    const idleBob = Math.sin(this.juice.bossMotion.idleTime * 2.2) * 1.25;
    const attackLift = this.juice.bossMotion.attackTime > 0 ? Math.sin(this.juice.bossMotion.attackTime * 22) * 2 : 0;
    const p = camera.worldToScreen(boss.x, boss.y - idleBob - attackLift);

    // This ramp (not each boss's own BOSS_SPRITES palette — paletteOverride.R
    // below always wins) is what actually colors every boss's body on
    // screen, escalating phase-by-phase from bright crimson toward
    // near-black as HP drops, so the "shrine idol" reads as growing more
    // corrupted rather than just cycling through unrelated hues.
    const phaseColors = ['#c0303a', '#8a1a3a', '#b5502a', '#4a0f18'];
    // boss.bossDef (not this.bossDef, the game-level "current gauntlet boss"
    // tracker) — same reasoning as _drawBossName below: this.bossDef stays
    // pointed at the real final boss the whole time a herald pair is up
    // (see _startMinibossStage), so using it here made every herald render
    // with the final boss's sprite/phase-colors regardless of which of the
    // 4 real bosses it's themed after (data/bosses.js's createMiniboss
    // spread-inherits each herald's OWN paired boss's spriteKey). Identical
    // result for a real boss fight, since boss.bossDef is either
    // this.bossDef itself or a same-.name spread copy of it (extraBossLayer
    // ascension). Phase-2 sprite swap and pulsing eye-glow are both gated on
    // bossDef fields only The Unmade King sets — every other boss's bossDef
    // simply lacks them, so this is a no-op there and they render exactly
    // as before.
    const usePhase2Sprite = boss.bossDef.spriteKeyPhase2 && boss.hpFraction <= 0.5;
    const spriteKey = usePhase2Sprite ? boss.bossDef.spriteKeyPhase2 : boss.bossDef.spriteKey;
    const paletteOverride = { R: boss.hitFlashTimer > 0 && !boss.invulnerable ? '#ffffff' : (phaseColors[boss.phase] || phaseColors[0]) };
    if (boss.bossDef.hasPulseGlow) {
      // Flickers between two accent tones rather than a smooth cross-fade —
      // reads as an unstable, glitching light, which suits "unmade" better
      // than a gentle pulse would.
      const flicker = Math.sin(performance.now() / 300) > 0;
      paletteOverride.W = flicker ? '#ffffff' : '#ff5d6c';
    }

    // A soft pulsing halo behind every boss, tinted to its current body
    // color — the shrine-idol art (data/sprites.js's BOSS_SPRITES) is meant
    // to sit inside a glowing ring like a summoned altar figure, not float
    // on a flat background. Drawn before the sprite so it sits behind it.
    const glowRadius = boss.radius * 2.4;
    const pulse = 0.5 + 0.3 * Math.sin(performance.now() / 450);
    const glow = ctx.createRadialGradient(p.x, p.y, boss.radius * 0.2, p.x, p.y, glowRadius);
    glow.addColorStop(0, paletteOverride.R + 'cc');
    glow.addColorStop(1, paletteOverride.R + '00');
    ctx.save();
    ctx.globalAlpha = pulse;
    ctx.fillStyle = glow;
    ctx.beginPath();
    ctx.arc(p.x, p.y, glowRadius, 0, Math.PI * 2);
    ctx.fill();
    ctx.restore();

    drawSprite(ctx, BOSS_SPRITES[spriteKey], p.x, p.y, { paletteOverride });
    this._drawBossSignature(ctx, p, boss.bossDef.spriteKey, boss);
    if (boss.isDying || boss.vulnerable || this.juice.phasePulse > 0) {
      ctx.save();
      ctx.strokeStyle = boss.isDying ? '#ff3b6b' : boss.vulnerable ? '#4ade80' : '#ffffff';
      ctx.globalAlpha = boss.isDying ? 0.8 : boss.vulnerable ? 0.9 : this.juice.phasePulse / 2;
      ctx.lineWidth = boss.isDying ? 2 : 1;
      ctx.beginPath();
      ctx.arc(p.x, p.y, boss.radius + 7 + Math.sin(performance.now() / 80) * (boss.isDying || boss.vulnerable ? 3 : 0), 0, Math.PI * 2);
      ctx.stroke();
      ctx.restore();
    }

    // A thin circle at the true collision radius, drawn over the sprite —
    // the hand-authored silhouette doesn't perfectly fill a 36px circle, and
    // this game's whole bullet-hell design is built on the hitbox being
    // readable (see ARCHIVE.md), so the actual boundary stays visible
    // regardless of the art.
    ctx.beginPath();
    ctx.strokeStyle = '#ffffff';
    ctx.lineWidth = boss.invulnerable ? 2 : 1;
    ctx.globalAlpha = 0.35;
    ctx.arc(p.x, p.y, boss.radius, 0, Math.PI * 2);
    ctx.stroke();
    ctx.globalAlpha = 1;
  }

  // Small animated focal details keep the new silhouettes alive without
  // turning the boss into a noisy effect blob. Each boss gets a different
  // motion language that reinforces its gameplay identity.
  _drawBossSignature(ctx, p, spriteKey, boss) {
    const t = performance.now() / 1000;
    ctx.save();
    if (spriteKey === 'hollowSovereign') {
      const pulse = 1 + Math.sin(t * 4) * 0.18;
      ctx.strokeStyle = '#ffd166';
      ctx.globalAlpha = 0.65;
      ctx.lineWidth = 1;
      ctx.beginPath();
      ctx.arc(p.x, p.y + 3, 8 * pulse, Math.PI * 0.15, Math.PI * 0.85);
      ctx.stroke();
    } else if (spriteKey === 'rivenChoir') {
      ctx.fillStyle = '#c084fc';
      ctx.globalAlpha = 0.8;
      for (let i = 0; i < 4; i++) {
        const angle = t * (i % 2 ? -0.8 : 0.8) + i * Math.PI / 2;
        const x = p.x + Math.cos(angle) * (18 + (i % 2) * 3);
        const y = p.y + Math.sin(angle) * (12 + (i % 2) * 3);
        ctx.fillRect(x - 1, y - 1, 2, 2);
      }
    } else if (spriteKey === 'ashenThrone') {
      const pulse = 0.2 + (Math.sin(t * 2) + 1) * 0.12;
      ctx.fillStyle = '#ffd166';
      ctx.globalAlpha = pulse;
      ctx.fillRect(p.x - 24, p.y + 15, 48, 2);
      ctx.fillRect(p.x - 19, p.y + 18, 38, 1);
    } else if (spriteKey === 'unmadeKing') {
      const jitter = Math.sin(t * 17) * 1.5;
      ctx.strokeStyle = boss.hpFraction <= 0.5 ? '#ff5d6c' : '#ffd166';
      ctx.globalAlpha = boss.hpFraction <= 0.5 ? 0.75 : 0.45;
      ctx.lineWidth = 1;
      ctx.beginPath();
      ctx.moveTo(p.x - 18 + jitter, p.y - 15);
      ctx.lineTo(p.x - 11, p.y - 21 + jitter);
      ctx.moveTo(p.x + 15, p.y + 13 - jitter);
      ctx.lineTo(p.x + 23 - jitter, p.y + 18);
      ctx.stroke();
    } else if (spriteKey === 'maelstromVerge') {
      // Foam arcs circling the maw — the whirlpool never stops turning.
      ctx.strokeStyle = '#4dd8c7';
      ctx.globalAlpha = 0.6;
      ctx.lineWidth = 1;
      for (let i = 0; i < 2; i++) {
        const angle = t * 1.6 + i * Math.PI;
        ctx.beginPath();
        ctx.arc(p.x, p.y, 15 + i * 4, angle, angle + Math.PI * 0.7);
        ctx.stroke();
      }
    } else if (spriteKey === 'theUnblinking') {
      // The iris drifts in a slow figure-eight — always looking, never blinking.
      ctx.fillStyle = '#8ae06e';
      ctx.globalAlpha = 0.85;
      const ix = p.x + Math.sin(t * 1.3) * 3;
      const iy = p.y - 2 + Math.sin(t * 2.6) * 1.5;
      ctx.fillRect(ix - 1.5, iy - 1.5, 3, 3);
    } else if (spriteKey === 'chronophage') {
      // A clock hand ticking in discrete backward steps around the frame.
      const step = Math.floor(t * 2) * (Math.PI / 6);
      ctx.strokeStyle = '#8c7bff';
      ctx.globalAlpha = 0.7;
      ctx.lineWidth = 1;
      ctx.beginPath();
      ctx.moveTo(p.x, p.y);
      ctx.lineTo(p.x + Math.cos(-step - Math.PI / 2) * 13, p.y + Math.sin(-step - Math.PI / 2) * 13);
      ctx.stroke();
    } else if (spriteKey === 'ironCadence') {
      // The pendulum: swings in HARD steps, not a smooth arc — snapping
      // between left and right like an escapement, faster in later phases.
      const beat = Math.floor(t * (1.6 + boss.phase * 0.4)) % 2 ? 1 : -1;
      ctx.strokeStyle = '#9fb8e8';
      ctx.globalAlpha = 0.85;
      ctx.lineWidth = 1.5;
      ctx.beginPath();
      ctx.moveTo(p.x, p.y - 12);
      ctx.lineTo(p.x + beat * 8, p.y + 8);
      ctx.stroke();
      ctx.fillStyle = '#e8f0ff';
      ctx.fillRect(p.x + beat * 8 - 1.5, p.y + 8 - 1.5, 3, 3);
    } else if (spriteKey === 'famishedCourt') {
      // The jaw: gapes wide on a slow chewing cycle, with a drool glint
      // falling from the corner of the maw while it hangs open.
      const gape = (Math.sin(t * 1.7) + 1) / 2; // 0 closed .. 1 wide
      ctx.strokeStyle = '#cdd45a';
      ctx.globalAlpha = 0.8;
      ctx.lineWidth = 1.5;
      ctx.beginPath();
      ctx.arc(p.x, p.y + 4, 9 + gape * 4, Math.PI * 0.1, Math.PI * 0.9);
      ctx.stroke();
      if (gape > 0.55) {
        const fall = (t * 30) % 8;
        ctx.fillStyle = '#e8ecb0';
        ctx.globalAlpha = 0.7 * (1 - fall / 8);
        ctx.fillRect(p.x + 7, p.y + 10 + fall, 1.5, 2.5);
      }
    } else if (spriteKey === 'jealousRegent') {
      // The heart: a rose diamond at the chest beating lub-dub — two quick
      // swells then a rest, the same rhythm the Embrace's rings fire on.
      const cycle = (t * 1.4) % 1;
      const beatPulse = cycle < 0.12 ? 1 : cycle < 0.2 ? 0.4 : cycle < 0.32 ? 0.8 : 0;
      const size = 3 + beatPulse * 2.5;
      ctx.fillStyle = '#ff87b3';
      ctx.globalAlpha = 0.6 + beatPulse * 0.4;
      ctx.beginPath();
      ctx.moveTo(p.x, p.y - size);
      ctx.lineTo(p.x + size, p.y);
      ctx.lineTo(p.x, p.y + size);
      ctx.lineTo(p.x - size, p.y);
      ctx.closePath();
      ctx.fill();
    }
    ctx.restore();
  }

  /**
   * Shared HUD palette. Every readout used to hardcode '#ffffff' strokes and
   * a '#2a2a3a' track, which meant `highContrast` was honoured by the graze
   * chain and the telegraph callout but silently ignored by HP, the dash
   * pips, the ultimate arc and the XP bar — a half-converted HUD, which is
   * arguably worse than none. These three getters are the single place that
   * decision now lives.
   */
  get _hudInk() {
    return this.save.highContrast ? this._arenaInk : '#ffffff';
  }

  // The unfilled part of any meter. Under highContrast it has to sit against
  // the ink rather than against the arena floor, so it flips with the ink.
  get _hudTrack() {
    if (!this.save.highContrast) return '#2a2a3a';
    return this._arenaInk === '#ffffff' ? '#1a1a24' : '#d8d8e0';
  }

  /**
   * The dark backing plate the corner HP bar has always had (it was added
   * because the raw track washed out against the arena floor). Every
   * player-anchored meter needs it *more* than the corner one does: they are
   * drawn on top of the densest VFX in the game — aura, particles, the
   * ultimate field — so they disappear exactly when they matter. Coordinates
   * are virtual units; the ox/oy + (…) * s transform is applied here.
   */
  _meterPlate(tc, renderer, x, y, w, h, pad = 1.5) {
    const s = renderer.scale;
    // Flips with the ink so the plate always backs the ink, never matches it.
    tc.fillStyle = this.save.highContrast && this._arenaInk !== '#ffffff'
      ? 'rgba(246, 246, 250, 0.82)'
      : 'rgba(10, 10, 18, 0.72)';
    tc.fillRect(
      renderer.offsetX + (x - pad) * s,
      renderer.offsetY + (y - pad) * s,
      (w + pad * 2) * s,
      (h + pad * 2) * s,
    );
  }

  /**
   * An arc meter hugging the player sprite, filling clockwise from `startAngle`
   * over `sweep` radians. Arcs exist to break the cluster's shape monotony:
   * HP and the ultimate used to be two more 2px rectangles among five, so
   * telling "ultimate charging" from "cadence building" was a colour-only
   * judgement at 2px. A ring reads at tiny sizes in a way a thin bar does
   * not, and top-vs-bottom placement separates the two without relying on hue.
   */
  _drawMeterArc(tc, renderer, cx, cy, radius, startAngle, sweep, fraction, color, width) {
    const s = renderer.scale;
    const px = renderer.offsetX + cx * s;
    const py = renderer.offsetY + cy * s;
    const pr = radius * s;
    tc.save();
    tc.lineCap = 'butt';
    // Backing stroke, same job as _meterPlate for the rectangular meters.
    tc.lineWidth = (width + 1.1) * s;
    tc.strokeStyle = this.save.highContrast && this._arenaInk !== '#ffffff'
      ? 'rgba(246, 246, 250, 0.82)'
      : 'rgba(10, 10, 18, 0.72)';
    tc.beginPath();
    tc.arc(px, py, pr, startAngle, startAngle + sweep);
    tc.stroke();

    tc.lineWidth = width * s;
    tc.strokeStyle = this._hudTrack;
    tc.beginPath();
    tc.arc(px, py, pr, startAngle, startAngle + sweep);
    tc.stroke();

    if (fraction > 0) {
      tc.strokeStyle = color;
      tc.beginPath();
      tc.arc(px, py, pr, startAngle, startAngle + sweep * Math.min(1, fraction));
      tc.stroke();
    }
    tc.restore();
  }

  /**
   * HP, anchored to the player as an arc over the top of the sprite.
   *
   * It used to live only in the bottom-left corner while dash, ultimate,
   * graze, cadence and plates were all drawn on the sprite — i.e. the one
   * vital that ends the run was the one vital you had to look away from the
   * bullets to read. The corner bar stays (it carries the exact numbers);
   * this is the at-a-glance state, in peripheral vision where the fight is.
   */
  _drawPlayerHpArc(tc, renderer, player) {
    const p = this.camera.worldToScreen(player.x, player.y);
    const fraction = Math.max(0, Math.min(1, player.hp / player.maxHp));
    const lowHp = fraction <= 0.3;
    // Same pulse policy as the corner bar and the low-HP vignette: urgency
    // reads in the periphery, and reduceFlash opts out of the blink.
    const pulse = lowHp && !this.save.reduceFlash
      ? 0.65 + 0.35 * Math.sin(performance.now() / 120)
      : 1;
    const color = lowHp ? '#ff5d6c' : '#4ade80';
    tc.save();
    tc.globalAlpha = pulse;
    // Upper half, drawn left-to-right over the top of the sprite so it
    // depletes the way the corner bar does.
    this._drawMeterArc(tc, renderer, p.x, p.y, player.radius + 2.6, Math.PI, Math.PI, fraction, color, 1.3);
    tc.restore();
  }

  _drawHudGraphics(tc, renderer, player) {
    const s = renderer.scale;
    const ox = renderer.offsetX;
    const oy = renderer.offsetY;
    const view = renderer.visibleVirtualBounds();
    const x = view.left + 4;
    const hpY = view.bottom - 14;
    const hpW = 70;
    const hpH = 5;

    // Dark backing plate — the raw bar background was near-invisible against
    // the arena floor, which made low HP easy to miss mid-dodge.
    this._meterPlate(tc, renderer, x, hpY, hpW, hpH);

    tc.fillStyle = this._hudTrack;
    tc.fillRect(ox + x * s, oy + hpY * s, hpW * s, hpH * s);
    const hpFraction = player.hp / player.maxHp;
    const lowHp = hpFraction <= 0.3;
    // Below 30% the fill pulses — urgency reads in peripheral vision without
    // adding any new screen element. reduceFlash opts out of the flash.
    const pulse = lowHp && !this.save.reduceFlash
      ? 0.65 + 0.35 * Math.sin(performance.now() / 120)
      : 1;
    tc.globalAlpha = pulse;
    tc.fillStyle = lowHp ? '#ff5d6c' : '#4ade80';
    tc.fillRect(ox + x * s, oy + hpY * s, hpW * s * hpFraction, hpH * s);
    tc.globalAlpha = 1;
    tc.strokeStyle = this._hudInk;
    tc.lineWidth = 0.5 * s;
    tc.strokeRect(ox + x * s, oy + hpY * s, hpW * s, hpH * s);
  }

  // Dash-charge pips float just above the player sprite (world-anchored,
  // via the same camera.worldToScreen -> text-overlay transform _drawBossName
  // uses: compute everything in virtual-space units first, then apply
  // ox/oy + (...) * s once per coordinate) instead of the screen corner, so
  // they're always where you're actually looking during a fight.
  _drawPlayerDashPips(tc, renderer, player) {
    const p = this.camera.worldToScreen(player.x, player.y);
    const s = renderer.scale;
    const ox = renderer.offsetX;
    const oy = renderer.offsetY;

    const pipW = 6;
    const pipH = 2;
    const pipGap = 1.5;
    const totalWidth = player.maxDashCharges * pipW + (player.maxDashCharges - 1) * pipGap;
    const startX = p.x - totalWidth / 2;
    const y = p.y - player.radius - 6;

    this._meterPlate(tc, renderer, startX, y, totalWidth, pipH, 1);

    for (let i = 0; i < player.maxDashCharges; i++) {
      const pipX = startX + i * (pipW + pipGap);
      tc.fillStyle = this._hudTrack;
      tc.fillRect(ox + pipX * s, oy + y * s, pipW * s, pipH * s);
      const fraction = i < player.dashCharges ? 1 : i === player.dashCharges ? player.dashCooldownFraction : 0;
      tc.fillStyle = fraction >= 1 ? '#7e97ff' : '#4a5a8a';
      tc.fillRect(ox + pipX * s, oy + y * s, pipW * s * fraction, pipH * s);
      // White blink on full pips the moment a charge finishes recharging
      // (player:dash-refilled -> JuiceDirector.dashRefillFlash) — "your
      // dash is back" reads without looking away from the fight. Halved
      // under reduceFlash rather than dropped: it's the readout for a
      // resource you spend on reaction, so it can't go silent.
      if (fraction >= 1 && this.juice.dashRefillFlash > 0) {
        tc.save();
        tc.globalAlpha = Math.min(1, this.juice.dashRefillFlash / 0.4) * (this.save.reduceFlash ? 0.45 : 1);
        tc.fillStyle = this._hudInk;
        tc.fillRect(ox + pipX * s, oy + y * s, pipW * s, pipH * s);
        tc.restore();
      }
      tc.strokeStyle = this._hudInk;
      tc.lineWidth = 0.5 * s;
      tc.strokeRect(ox + pipX * s, oy + y * s, pipW * s, pipH * s);
    }
  }

  // Vess's Cadence streak — a bar in the same above-the-pips slot Rook's
  // plates use (no champion has both, so they can't collide). Without it the
  // streak is invisible, which makes "don't get tagged" advice the player
  // can't act on.
  _drawCadenceMeter(tc, renderer, player) {
    if (!player.cadenceConfig) return;
    const p = this.camera.worldToScreen(player.x, player.y);
    const s = renderer.scale;
    const ox = renderer.offsetX;
    const oy = renderer.offsetY;

    const meterW = 14;
    const meterH = 2;
    const x = p.x - meterW / 2;
    const y = p.y - player.radius - 10;

    this._meterPlate(tc, renderer, x, y, meterW, meterH, 1);
    tc.fillStyle = this._hudTrack;
    tc.fillRect(ox + x * s, oy + y * s, meterW * s, meterH * s);
    const full = player.cadenceFraction >= 1;
    tc.fillStyle = full ? this._hudInk : this.character.color;
    tc.fillRect(ox + x * s, oy + y * s, meterW * s * player.cadenceFraction, meterH * s);
    tc.strokeStyle = full ? this._hudInk : '#8a8aa0';
    tc.lineWidth = 0.5 * s;
    tc.strokeRect(ox + x * s, oy + y * s, meterW * s, meterH * s);
  }

  /**
   * Cairn's Debt (data/characters.js) — banked damage awaiting repayment,
   * drawn as a ghost bar in the same slot as the other signature meters. It
   * has to be visible for "play clean and get it back" to be a decision
   * rather than a surprise; it drains left-to-right as the debt is repaid.
   */
  _drawDebtMeter(tc, renderer, player) {
    if (!player.debtConfig) return;
    const p = this.camera.worldToScreen(player.x, player.y);
    const s = renderer.scale;
    const ox = renderer.offsetX;
    const oy = renderer.offsetY;

    const meterW = 14;
    const meterH = 2;
    const x = p.x - meterW / 2;
    const y = p.y - player.radius - 10;

    this._meterPlate(tc, renderer, x, y, meterW, meterH, 1);
    tc.fillStyle = this._hudTrack;
    tc.fillRect(ox + x * s, oy + y * s, meterW * s, meterH * s);
    // Bright once the wait has elapsed and the bank is actively paying out —
    // that switch is the moment the player is being rewarded for.
    const repaying = player.debtBank > 0 && player._timeSinceDamage >= player.debtConfig.repayAfter;
    tc.fillStyle = repaying ? this._hudInk : this.character.color;
    tc.fillRect(ox + x * s, oy + y * s, meterW * s * player.debtFraction, meterH * s);
    tc.strokeStyle = repaying ? this._hudInk : '#8a8aa0';
    tc.lineWidth = 0.5 * s;
    tc.strokeRect(ox + x * s, oy + y * s, meterW * s, meterH * s);
  }

  // Rook's Aegis plates (data/characters.js) — a row of small blocks above
  // the dash pips, one per plate. Empty sockets stay drawn so "two of three
  // left" reads at a glance, the same way the dash pips show spent charges.
  _drawArmorPlates(tc, renderer, player) {
    if (player.maxArmorPlates === 0) return;
    const p = this.camera.worldToScreen(player.x, player.y);
    const s = renderer.scale;
    const ox = renderer.offsetX;
    const oy = renderer.offsetY;

    const plateW = 3;
    const plateH = 3;
    const gap = 1.5;
    const totalWidth = player.maxArmorPlates * plateW + (player.maxArmorPlates - 1) * gap;
    const startX = p.x - totalWidth / 2;
    // Its own slot above the cadence/debt bar. It used to sit at -10.5 with a
    // height of 3, i.e. spanning -10.5..-7.5 — straight through the
    // cadence/debt bar's -10..-8. Nothing rendered wrong only because no
    // champion currently has both, which is a silent constraint the layout
    // shouldn't be leaning on.
    const y = p.y - player.radius - 14;

    this._meterPlate(tc, renderer, startX, y, totalWidth, plateH, 1);

    for (let i = 0; i < player.maxArmorPlates; i++) {
      const plateX = startX + i * (plateW + gap);
      tc.fillStyle = i < player.armorPlates ? '#7ad67a' : '#2a3a2a';
      tc.fillRect(ox + plateX * s, oy + y * s, plateW * s, plateH * s);
      tc.strokeStyle = i < player.armorPlates ? this._hudInk : '#4a5a4a';
      tc.lineWidth = 0.5 * s;
      tc.strokeRect(ox + plateX * s, oy + y * s, plateW * s, plateH * s);
    }
  }

  // Ultimate charge meter (systems/combat/UltimateSystem.js) — an arc under
  // the player sprite, mirroring the HP arc over the top. Tinted to the
  // ultimate's own color, breathing white when it's ready to fire, and
  // draining while the window is open.
  //
  // It was a 14x2 bar in this slot, which made it the fourth near-identical
  // thin rectangle in the sprite cluster. Position (bottom half vs HP's top
  // half) plus form (arc vs the pips'/cadence's rectangles) now carry the
  // distinction, so hue is a reinforcement rather than the only cue.
  _drawUltimateMeter(tc, renderer, player) {
    const ult = this.ultimate;
    if (!ult?.def) return;
    const p = this.camera.worldToScreen(player.x, player.y);
    const radius = player.radius + 2.6;
    const fraction = ult.isActive ? ult.activeFraction : ult.chargeFraction;

    // Lower half, filling left-to-right along the bottom of the sprite.
    this._drawMeterArc(tc, renderer, p.x, p.y, radius, 0, Math.PI, fraction, ult.def.color, 1.3);

    // Charged and waiting: a slow white breathe over the full arc. Static
    // (not blinking) under reduceFlash, same policy as the mercy-window
    // tell on the player sprite.
    if (ult.isReady) {
      const s = renderer.scale;
      tc.save();
      tc.globalAlpha = this.save.reduceFlash ? 0.35 : 0.25 + 0.35 * (0.5 + 0.5 * Math.sin(performance.now() / 140));
      tc.lineWidth = 1.3 * s;
      tc.lineCap = 'butt';
      tc.strokeStyle = this._hudInk;
      tc.beginPath();
      tc.arc(renderer.offsetX + p.x * s, renderer.offsetY + p.y * s, radius * s, 0, Math.PI);
      tc.stroke();
      tc.restore();
    }
  }

  /**
   * The graze chain readout (BULLET-HELL.md §D5). Anchored to the player and
   * offset to the right — the one place on the sprite nothing else claims
   * (dash pips, cadence/debt/plates sit above it, the HP and ultimate arcs
   * hug the sprite itself) —
   * because the whole point is that it is readable WITHOUT looking away from
   * the bullets you are currently threading. A corner counter would be a
   * statistic; this is feedback.
   *
   * Three things, in the order they matter: what the next graze is worth, how
   * many are chained, and how long the window has left.
   */
  _drawGrazeChain(tc, renderer, player) {
    const chain = this.grazeChain;
    if (!chain.isVisible) return;
    const s = renderer.scale;
    const ox = renderer.offsetX;
    const oy = renderer.offsetY;
    const p = this.camera.worldToScreen(player.x, player.y);
    // Solid while the window is live, fading out over GRAZE.readoutFade once
    // it lapses so the number you finished on stays readable.
    const alpha = chain.remaining > 0 ? 1 : Math.max(0, chain.fade / GRAZE.readoutFade);
    const color = this.save.highContrast ? this._arenaInk : GRAZE.color;

    const barW = 16;
    const barH = 1.2;
    // The readout ran off the right edge of the view whenever the player was
    // pinned to the right wall — which is exactly when chains run longest,
    // because wall-hugging is how you thread a dense pattern. Mirror it to
    // the left when there isn't room. The resonance readout owns the left
    // side, so when both are live the graze block moves up a row instead of
    // landing on top of it.
    const view = renderer.visibleVirtualBounds();
    const blockW = Math.max(barW, 20);
    const flip = p.x + player.radius + 5 + blockW > view.right - 2;
    const x = flip ? p.x - player.radius - 5 : p.x + player.radius + 5;
    const dy = flip && this.resonance.isVisible ? -11 : 0;
    const barX = flip ? x - barW : x;

    tc.save();
    tc.globalAlpha = alpha;
    tc.textAlign = flip ? 'right' : 'left';
    tc.shadowColor = 'rgba(0, 0, 0, 0.85)';
    tc.shadowBlur = 2 * s;
    tc.fillStyle = color;
    tc.font = `bold ${Math.round(4.5 * s)}px ${UI_FONT}`;
    tc.fillText(`×${chain.multiplier.toFixed(2)}`, ox + x * s, oy + (p.y - 1 + dy) * s);
    tc.font = `${Math.round(3 * s)}px ${UI_FONT}`;
    tc.fillStyle = '#e8e8f0';
    tc.fillText(`${chain.count} GRAZE`, ox + x * s, oy + (p.y + 3.5 + dy) * s);
    tc.shadowBlur = 0;

    // Decay bar: the chain's remaining window, draining right to left. It is
    // the only part of this that has to be read in peripheral vision — the
    // question mid-pattern is never "what is my multiplier", it is "do I have
    // time to reach the next bullet".
    const barY = p.y + 5.5 + dy;
    tc.fillStyle = this._hudTrack;
    tc.fillRect(ox + barX * s, oy + barY * s, barW * s, barH * s);
    tc.fillStyle = color;
    // Drains toward the player either way, so the direction still reads as
    // "time running back to you" once mirrored.
    const fillW = barW * s * chain.fraction;
    tc.fillRect(ox + barX * s + (flip ? barW * s - fillW : 0), oy + barY * s, fillW, barH * s);
    tc.restore();
  }

  /**
   * The resonance readout (BULLET-HELL.md §D7) — how much the next absorbed
   * bullet is worth, and how many are banked toward the release. Anchored LEFT
   * of the player, the mirror of the graze chain's right, because the two are
   * the same kind of statement and are frequently both live: in a Twin Voice
   * ring you graze the mismatched half while absorbing the matched one, which
   * is exactly the moment §D7 exists to reward.
   *
   * The banked-progress ring is drawn separately by `_drawAttunement` — this is
   * the numeric half, for the player who wants to know how close the release is
   * rather than that it is close.
   */
  _drawResonance(tc, renderer, player) {
    const res = this.resonance;
    if (!res.isVisible) return;
    const s = renderer.scale;
    const ox = renderer.offsetX;
    const oy = renderer.offsetY;
    const p = this.camera.worldToScreen(player.x, player.y);
    const alpha = res.remaining > 0 ? 1 : Math.max(0, res.fade / POLARITY.readoutFade);
    const color = this.save.highContrast ? this._arenaInk : POLARITY.color;
    const x = p.x - player.radius - 5;

    tc.save();
    tc.globalAlpha = alpha;
    tc.textAlign = 'right';
    tc.shadowColor = 'rgba(0, 0, 0, 0.85)';
    tc.shadowBlur = 2 * s;
    tc.fillStyle = color;
    tc.font = `bold ${Math.round(4.5 * s)}px ${UI_FONT}`;
    tc.fillText(`×${res.multiplier.toFixed(2)}`, ox + x * s, oy + (p.y - 1) * s);
    tc.font = `${Math.round(3 * s)}px ${UI_FONT}`;
    tc.fillStyle = '#e8e8f0';
    tc.fillText(`${res.stacks}/${res.stacksPerRelease} TONE`, ox + x * s, oy + (p.y + 3.5) * s);
    tc.shadowBlur = 0;

    // The chain's remaining window, draining toward the player (right to left
    // is the graze bar's direction; this one mirrors it so the pair reads as one
    // symmetric instrument rather than two unrelated bars).
    const barW = 16;
    const barH = 1.2;
    const barY = p.y + 5.5;
    tc.fillStyle = this._hudTrack;
    tc.fillRect(ox + (x - barW) * s, oy + barY * s, barW * s, barH * s);
    tc.fillStyle = color;
    const fillW = barW * res.fraction;
    tc.fillRect(ox + (x - fillW) * s, oy + barY * s, fillW * s, barH * s);
    tc.restore();
  }

  // Thorn's Briar Aura — the ring has to be visible for "get close" to be a
  // readable instruction rather than a hidden stat. Deliberately faint: it's
  // a standing part of the frame for the whole run, not an event.
  /**
   * Quill's glyphs (Act III). Two things have to read instantly: how long a
   * glyph has left (its alpha), and whether the player is standing on one (the
   * fire bonus, drawn as a brighter rim). Under Fair Copy the field is also
   * tethered, which is the ultimate's whole silhouette.
   */
  _drawGlyphs(ctx, camera, player) {
    if (!player.glyphs?.length) return;
    const color = this.save.highContrast ? this._arenaInk : (this.character.color ?? '#ffd166');
    ctx.save();
    if (player.inscriptionInstant && player.glyphs.length > 1) {
      ctx.strokeStyle = color;
      ctx.globalAlpha = 0.35;
      ctx.lineWidth = 1;
      ctx.beginPath();
      for (let i = 0; i < player.glyphs.length; i++) {
        const a = camera.worldToScreen(player.glyphs[i].x, player.glyphs[i].y);
        const b = camera.worldToScreen(
          player.glyphs[(i + 1) % player.glyphs.length].x,
          player.glyphs[(i + 1) % player.glyphs.length].y,
        );
        ctx.moveTo(a.x, a.y);
        ctx.lineTo(b.x, b.y);
      }
      ctx.stroke();
    }
    for (const glyph of player.glyphs) {
      const p = camera.worldToScreen(glyph.x, glyph.y);
      const r = glyph.radius * camera.zoom;
      // Fair Copy freezes lifetimes, so a frozen glyph shouldn't read as
      // fading — it holds full brightness for the window.
      const life = player.inscriptionInstant
        ? 1
        : Math.min(1, glyph.life / (player.inscriptionConfig?.glyphLifetime ?? 1));
      const standing = Math.hypot(player.x - glyph.x, player.y - glyph.y) < glyph.radius;
      ctx.globalAlpha = 0.1 + life * 0.18;
      ctx.fillStyle = color;
      ctx.beginPath();
      ctx.arc(p.x, p.y, r, 0, Math.PI * 2);
      ctx.fill();
      ctx.globalAlpha = standing ? 0.9 : 0.35 + life * 0.3;
      ctx.strokeStyle = color;
      ctx.lineWidth = standing ? 1.5 : 1;
      ctx.beginPath();
      ctx.arc(p.x, p.y, r, 0, Math.PI * 2);
      ctx.stroke();
    }
    ctx.restore();
  }

  _drawAura(ctx, camera, player, combat) {
    if (!combat.aura) return;
    const p = camera.worldToScreen(player.x, player.y);
    ctx.save();
    ctx.globalAlpha = 0.22;
    ctx.strokeStyle = this.character.color;
    ctx.lineWidth = 1;
    ctx.beginPath();
    ctx.arc(p.x, p.y, combat.aura.radius * camera.zoom, 0, Math.PI * 2);
    ctx.stroke();
    ctx.restore();
  }

  // Generic field visual for any ultimate whose hitbox is a place rather than
  // a projectile (Thornsong today): the def parks its current reach in
  // `state.radius` and this draws it. Defs without one draw nothing.
  _drawUltimateField(ctx, camera) {
    const ult = this.ultimate;
    if (!ult?.isActive || !ult.state.radius) return;
    const player = this.player;
    const p = camera.worldToScreen(player.x, player.y);
    const radius = ult.state.radius * camera.zoom;
    const t = performance.now() / 1000;

    ctx.save();
    // Gradient built from the def's own color (via globalAlpha rather than
    // baked rgba stops, so it stays generic across ultimates) — denser at the
    // rim so the edge of the effect, which is the edge of its hitbox, is the
    // part that reads.
    const glow = ctx.createRadialGradient(p.x, p.y, 0, p.x, p.y, radius);
    glow.addColorStop(0, 'transparent');
    glow.addColorStop(0.75, 'transparent');
    glow.addColorStop(1, ult.def.color);
    ctx.globalAlpha = 0.3;
    ctx.fillStyle = glow;
    ctx.beginPath();
    ctx.arc(p.x, p.y, radius, 0, Math.PI * 2);
    ctx.fill();

    ctx.strokeStyle = ult.def.color;
    ctx.globalAlpha = 0.8;
    ctx.lineWidth = 1;
    ctx.beginPath();
    ctx.arc(p.x, p.y, radius, 0, Math.PI * 2);
    ctx.stroke();
    // Two counter-rotating briar spokes so the vortex reads as spinning
    // rather than as a static circle.
    ctx.globalAlpha = 0.45;
    for (let i = 0; i < 6; i++) {
      const angle = t * 2.4 + (i * Math.PI * 2) / 6;
      ctx.beginPath();
      ctx.moveTo(p.x + Math.cos(angle) * radius * 0.35, p.y + Math.sin(angle) * radius * 0.35);
      ctx.lineTo(p.x + Math.cos(angle) * radius, p.y + Math.sin(angle) * radius);
      ctx.stroke();
    }
    ctx.restore();
  }

  /**
   * XP, as a thin line along the full bottom edge of the screen.
   *
   * It used to be a 4x50 vertical bar inset from the right edge — which
   * claimed a whole corner for the least time-critical readout on screen
   * (level progress is a slow trend; nothing about it has to be read inside a
   * pattern). Along the very bottom edge it's still legible as a rising fill,
   * costs no interior screen space, and frees the right edge entirely. The
   * level number moves down beside the HP block, which is where the other
   * "how am I doing" numbers already are.
   */
  _drawXpBar(tc, renderer, progression) {
    const s = renderer.scale;
    const ox = renderer.offsetX;
    const oy = renderer.offsetY;
    const view = renderer.visibleVirtualBounds();

    const barH = 1.5;
    const x = view.left;
    const y = view.bottom - barH;
    const barW = view.right - view.left;

    tc.fillStyle = this._hudTrack;
    tc.fillRect(ox + x * s, oy + y * s, barW * s, barH * s);

    const fraction = Math.min(1, Math.max(0, progression.xpProgress));
    tc.fillStyle = '#c9a8ff';
    tc.fillRect(ox + x * s, oy + y * s, barW * s * fraction, barH * s);

    tc.fillStyle = this._hudInk;
    tc.font = `bold ${Math.round(3.5 * s)}px ${UI_FONT}`;
    tc.textAlign = 'left';
    tc.shadowColor = 'rgba(0, 0, 0, 0.85)';
    tc.shadowBlur = 2 * s;
    tc.fillText(
      `Lv ${progression.isMaxLevel ? 'MAX' : progression.level}`,
      ox + (view.left + 4) * s,
      oy + (view.bottom - 4) * s,
    );
    tc.shadowBlur = 0;
  }

  _drawHud(tc, renderer, player) {
    const s = renderer.scale;
    const ox = renderer.offsetX;
    const oy = renderer.offsetY;
    const view = renderer.visibleVirtualBounds();
    const x = view.left + 4;
    const hpY = view.bottom - 14;
    // Soft dark halo keeps HUD text readable over bright bullets/VFX.
    tc.shadowColor = 'rgba(0, 0, 0, 0.85)';
    tc.shadowBlur = 2 * s;
    tc.fillStyle = this._hudInk;
    tc.font = `bold ${Math.round(4 * s)}px ${UI_FONT}`;
    tc.textAlign = 'left';
    tc.fillText(`${Math.ceil(player.hp)}/${Math.round(player.maxHp)}`, ox + x * s, oy + (hpY - 2) * s);

    // Run clock, bottom-center — survival time is a leaderboard stat, so
    // show it live instead of only revealing it on the run-end screen. It
    // shares a baseline with the level number (left) and the mode label
    // (right): one row of slow-changing run context, one fixation.
    tc.font = `bold ${Math.round(3.5 * s)}px ${UI_FONT}`;
    tc.textAlign = 'center';
    tc.fillStyle = this.save.highContrast ? this._arenaInk : '#e8e8f0';
    tc.fillText(formatTime(this.progression.survivalTime), ox + view.centerX * s, oy + (view.bottom - 4) * s);

    tc.textAlign = 'right';
    tc.fillStyle = this.save.highContrast ? this._arenaInk : '#a8a8c0';
    const mode = this.endless
      ? `ENDLESS LOOP ${Math.floor(this.gauntletIndex / BOSSES.length) + 1}`
      : (this.ascension.level ? `ASCENSION ${this.ascension.level}` : 'NORMAL');
    // Was inset 15 to clear the old right-edge XP bar; that bar now runs
    // along the bottom edge, so this can sit in the actual corner.
    tc.fillText(mode, ox + (view.right - 4) * s, oy + (view.bottom - 4) * s);
    tc.shadowBlur = 0;
  }

  _drawTelegraphCallout(tc, renderer) {
    const callout = this.juice.telegraphCallout;
    if (!callout || this.stage !== 'boss') return;
    const s = renderer.scale;
    const ox = renderer.offsetX;
    const oy = renderer.offsetY;
    const view = renderer.visibleVirtualBounds();
    tc.fillStyle = this.save.highContrast ? this._arenaInk : '#ffd166';
    tc.font = `bold ${Math.round(5 * s)}px ${UI_FONT}`;
    tc.textAlign = 'center';
    const icon = this._telegraphIcon(callout.name);
    tc.fillText(`${icon} ${callout.name.toUpperCase()} ${icon}`, ox + view.centerX * s, oy + (view.top + 10) * s);
    tc.font = `${Math.round(3 * s)}px ${UI_FONT}`;
    tc.fillText(`${Math.max(0, callout.remaining).toFixed(1)}s`, ox + view.centerX * s, oy + (view.top + 15) * s);
  }

  /**
   * The CAPTURED title card (BULLET-HELL.md §D1) — the whole reason capture
   * works as a mechanic. Naming the attack you just survived is what turns a
   * wall of bullets into a list of things you can get good at, so this shows
   * the card's name as prominently as the payout.
   *
   * Sits below the telegraph callout rather than replacing it: the next card
   * is very often already telegraphing when a capture resolves, and the
   * incoming threat must never be the thing that gets covered up.
   */
  _drawCaptureBanner(tc, renderer) {
    const banner = this.juice.captureBanner;
    if (!banner) return;
    const s = renderer.scale;
    const ox = renderer.offsetX;
    const oy = renderer.offsetY;
    const view = renderer.visibleVirtualBounds();
    const t = banner.remaining / banner.maxLife;
    // Holds solid, then fades over the last third — long enough to read the
    // name at a glance mid-dodge, short enough not to become HUD furniture.
    const alpha = Math.min(1, t * 3);
    const x = ox + view.centerX * s;
    const y = oy + (view.top + 24 + (1 - t) * 3) * s;
    tc.save();
    tc.globalAlpha = alpha;
    tc.textAlign = 'center';
    tc.fillStyle = this.save.highContrast ? this._arenaInk : CAPTURE.color;
    tc.font = `bold ${Math.round((banner.desperation ? 7 : 6) * s)}px ${UI_FONT}`;
    tc.fillText(banner.desperation ? 'FINALE CAPTURED' : 'CAPTURED', x, y);
    tc.font = `${Math.round(4 * s)}px ${UI_FONT}`;
    tc.fillText(banner.name.toUpperCase(), x, y + 5.5 * s);
    tc.font = `${Math.round(3 * s)}px ${UI_FONT}`;
    tc.fillStyle = this.save.highContrast ? this._arenaInk : '#e8e8f0';
    const graze = banner.grazes > 0 ? `  ·  ${banner.grazes} GRAZE` : '';
    tc.fillText(`+${banner.glimmer} GLIMMER${graze}`, x, y + 10.5 * s);
    tc.restore();
  }

  _telegraphIcon(name) {
    const n = name.toLowerCase();
    if (n.includes('laser') || n.includes('beam') || n.includes('baton')) return '╱';
    if (n.includes('charge') || n.includes('slam')) return '►';
    if (n.includes('mine') || n.includes('denial') || n.includes('dagger')) return '◇';
    if (n.includes('shield') || n.includes('safe') || n.includes('vow') || n.includes('embrace')) return '⬡';
    if (n.includes('ring') || n.includes('burst') || n.includes('collapse') || n.includes('staccato') || n.includes('volley')) return '◎';
    return '◆';
  }

  _drawBossBullets(ctx, camera, spawning) {
    spawning.forEachBullet((bullet) => {
      const p = camera.worldToScreen(bullet.x, bullet.y);
      const prev = camera.worldToScreen(bullet.prevX, bullet.prevY);
      ctx.save();
      ctx.globalAlpha = bullet.trail?.alpha ?? 0.35;
      ctx.strokeStyle = bullet.color;
      ctx.lineWidth = bullet.trail?.width ?? 1;
      ctx.beginPath();
      ctx.moveTo(prev.x, prev.y);
      ctx.lineTo(p.x, p.y);
      ctx.stroke();
      ctx.restore();
      this._drawHostileBulletBody(ctx, p, bullet);
      if (bullet.tone) this._drawToneSigil(ctx, p, bullet.tone, bullet.radius + 2);
    });
  }

  /**
   * The body every hostile bullet is drawn with — boss patterns and minion
   * fire alike, which is why it lives here instead of being duplicated in
   * _drawBossBullets and _drawMinionBullets.
   *
   * Hot core, saturated body, dark rim. Hue cannot be what separates the two
   * sides: the player's weapon palette and the boss patterns' overlap
   * outright (#ffd166 is Nova Pulse AND Glyphpen AND four patterns; #8ad1ff
   * is Arc Needle and a pattern), and repainting ten weapons would cost each
   * of them the identity that makes it readable at a glance. Internal
   * structure is free instead: nothing the player fires has a core or a rim
   * (see _drawProjectiles, which draws flat and additive), so "solid disc
   * with a bright centre" means incoming whatever colour it came out as.
   *
   * The rim is inset to land inside `bullet.radius` — the same radius
   * SpawningSystem._checkPlayerCollisions tests — so the drawn edge is never
   * larger than the thing that can actually hit you.
   */
  _drawHostileBulletBody(ctx, p, bullet) {
    const r = bullet.radius;
    ctx.save();
    ctx.fillStyle = bullet.color;
    ctx.beginPath();
    ctx.arc(p.x, p.y, r, 0, Math.PI * 2);
    ctx.fill();
    // Alpha rather than an opaque white, so the pattern's own colour still
    // tints the centre and bullets stay individually identifiable.
    ctx.globalAlpha = 0.82;
    ctx.fillStyle = '#fff6e8';
    ctx.beginPath();
    ctx.arc(p.x, p.y, r * 0.45, 0, Math.PI * 2);
    ctx.fill();
    // Dark rim — the separation that holds on The Anthem's near-white floor
    // as well as on the nineteen near-black arenas.
    ctx.globalAlpha = 1;
    const lw = Math.max(0.6, r * 0.2);
    ctx.strokeStyle = 'rgba(12,8,20,0.85)';
    ctx.lineWidth = lw;
    ctx.beginPath();
    ctx.arc(p.x, p.y, Math.max(0.1, r - lw / 2), 0, Math.PI * 2);
    ctx.stroke();
    ctx.restore();
  }

  _drawToneSigil(ctx, p, tone, radius) {
    ctx.save();
    ctx.strokeStyle = '#ffffff';
    ctx.lineWidth = 0.75;
    ctx.beginPath();
    if (tone === 'warm') {
      ctx.moveTo(p.x, p.y - radius); ctx.lineTo(p.x + radius, p.y + radius * 0.75); ctx.lineTo(p.x - radius, p.y + radius * 0.75); ctx.closePath();
    } else {
      ctx.rect(p.x - radius * 0.72, p.y - radius * 0.72, radius * 1.44, radius * 1.44);
    }
    ctx.stroke();
    ctx.restore();
  }

  _drawAttackVisuals(ctx, camera, boss, spawning) {
    const highContrast = this.save.highContrast;
    for (const v of spawning.getVisuals(boss)) {
      switch (v.kind) {
        case 'flash': {
          const p = camera.worldToScreen(v.x, v.y);
          ctx.strokeStyle = v.color;
          ctx.lineWidth = 1.5;
          ctx.beginPath();
          ctx.arc(p.x, p.y, boss.radius + 4, 0, Math.PI * 2);
          ctx.stroke();
          break;
        }
        case 'ringPulse': {
          const p = camera.worldToScreen(v.x, v.y);
          ctx.strokeStyle = '#ff5d6c';
          ctx.globalAlpha = 0.6;
          ctx.lineWidth = 1;
          ctx.beginPath();
          ctx.arc(p.x, p.y, boss.radius + 6 + v.t * 10, 0, Math.PI * 2);
          ctx.stroke();
          ctx.globalAlpha = 1;
          break;
        }
        case 'sirenreef': {
          const bait = camera.worldToScreen(v.bait.x, v.bait.y);
          const safe = camera.worldToScreen(v.safe.x, v.safe.y);
          const color = highContrast ? this._arenaInk : '#ff6b9d';
          ctx.save();
          ctx.strokeStyle = color;
          ctx.globalAlpha = 0.45 + v.t * 0.45;
          ctx.setLineDash([4, 3]);
          ctx.beginPath(); ctx.arc(bait.x, bait.y, boss.radius + 9, 0, Math.PI * 2); ctx.stroke();
          ctx.setLineDash([]);
          ctx.globalAlpha = 0.95;
          ctx.strokeStyle = highContrast ? this._arenaInk : '#4dd8c7';
          ctx.fillStyle = highContrast ? 'rgba(255,255,255,0.25)' : 'rgba(77,216,199,0.18)';
          ctx.beginPath(); ctx.arc(safe.x, safe.y, 11 + v.t * 3, 0, Math.PI * 2); ctx.fill(); ctx.stroke();
          ctx.setLineDash([2, 2]);
          ctx.beginPath(); ctx.moveTo(bait.x, bait.y); ctx.lineTo(safe.x, safe.y); ctx.stroke();
          ctx.setLineDash([]);
          ctx.restore();
          break;
        }
        case 'homingPreview': {
          const from = camera.worldToScreen(v.x, v.y);
          const to = v.target ? camera.worldToScreen(v.target.x, v.target.y) : from;
          const color = highContrast ? this._arenaInk : v.color;
          ctx.save();
          ctx.strokeStyle = color;
          ctx.globalAlpha = 0.7;
          ctx.setLineDash([3, 3]);
          ctx.beginPath(); ctx.moveTo(from.x, from.y); ctx.lineTo(to.x, to.y); ctx.stroke();
          ctx.setLineDash([]);
          const aim = Math.atan2(to.y - from.y, to.x - from.x);
          for (let i = 0; i < Math.min(5, v.count); i++) {
            const spread = (i - (Math.min(5, v.count) - 1) / 2) * 0.12;
            const a = aim + spread;
            ctx.beginPath();
            ctx.moveTo(from.x + Math.cos(a) * 12, from.y + Math.sin(a) * 12);
            ctx.lineTo(from.x + Math.cos(a) * 21, from.y + Math.sin(a) * 21);
            ctx.stroke();
          }
          ctx.restore();
          break;
        }
        case 'implosionPreview': {
          const p = camera.worldToScreen(v.x, v.y);
          const color = highContrast ? this._arenaInk : v.color;
          ctx.save();
          ctx.strokeStyle = color;
          ctx.globalAlpha = 0.7;
          ctx.setLineDash([4, 3]);
          for (const radius of [boss.radius + 8, boss.radius + 22]) {
            ctx.beginPath(); ctx.arc(p.x, p.y, radius, 0, Math.PI * 2); ctx.stroke();
          }
          ctx.setLineDash([]);
          for (let i = 0; i < Math.min(8, v.count); i++) {
            const a = (i / Math.min(8, v.count)) * Math.PI * 2;
            ctx.beginPath();
            ctx.moveTo(p.x + Math.cos(a) * 30, p.y + Math.sin(a) * 30);
            ctx.lineTo(p.x + Math.cos(a) * 20, p.y + Math.sin(a) * 20);
            ctx.stroke();
          }
          ctx.restore();
          break;
        }
        case 'boardPreview': {
          const color = highContrast ? this._arenaInk : '#8ad1ff';
          const left = camera.worldToScreen(v.arena.x, v.arena.y);
          const right = camera.worldToScreen(v.arena.x + v.arena.width, v.arena.y + v.arena.height);
          const spacing = (v.arena.height - 16) / (v.cells - 1);
          ctx.save();
          ctx.strokeStyle = color;
          ctx.globalAlpha = 0.7;
          ctx.setLineDash([3, 3]);
          for (let wave = 0; wave < v.gapCells.length; wave++) {
            const x = v.direction > 0 ? left.x + wave * 12 : right.x - wave * 12;
            for (let cell = 0; cell < v.cells; cell++) {
              if (cell === v.gapCells[wave]) continue;
              const y = camera.worldToScreen(v.arena.x, v.arena.y + 8 + spacing * cell).y;
              ctx.beginPath(); ctx.arc(x, y, 3, 0, Math.PI * 2); ctx.stroke();
            }
          }
          ctx.setLineDash([]);
          ctx.restore();
          break;
        }
        case 'fanPreview': {
          const p = camera.worldToScreen(v.x, v.y);
          const color = highContrast ? this._arenaInk : v.color;
          ctx.save();
          ctx.strokeStyle = color;
          ctx.globalAlpha = 0.7;
          ctx.setLineDash([3, 3]);
          ctx.beginPath(); ctx.moveTo(p.x, p.y); ctx.lineTo(p.x + Math.cos(v.angle - v.spread / 2) * 70, p.y + Math.sin(v.angle - v.spread / 2) * 70); ctx.stroke();
          ctx.beginPath(); ctx.moveTo(p.x, p.y); ctx.lineTo(p.x + Math.cos(v.angle + v.spread / 2) * 70, p.y + Math.sin(v.angle + v.spread / 2) * 70); ctx.stroke();
          ctx.setLineDash([]);
          ctx.restore();
          break;
        }
        case 'toneSpiral': {
          const p = camera.worldToScreen(v.x, v.y);
          const color = highContrast ? this._arenaInk : v.color;
          ctx.save();
          ctx.strokeStyle = color;
          ctx.globalAlpha = 0.75;
          for (let arm = 0; arm < v.arms; arm++) {
            const a = arm * (Math.PI * 2 / v.arms);
            ctx.beginPath();
            ctx.moveTo(p.x + Math.cos(a) * 10, p.y + Math.sin(a) * 10);
            ctx.lineTo(p.x + Math.cos(a + 0.35) * 54, p.y + Math.sin(a + 0.35) * 54);
            ctx.stroke();
          }
          ctx.restore();
          break;
        }
        case 'silencePreview': {
          const p = camera.worldToScreen(v.x, v.y);
          const color = highContrast ? this._arenaInk : v.color;
          ctx.save();
          ctx.strokeStyle = color;
          ctx.globalAlpha = 0.75;
          ctx.setLineDash([5, 4]);
          ctx.beginPath(); ctx.arc(p.x, p.y, boss.radius + 14, 0, Math.PI * 2); ctx.stroke();
          ctx.setLineDash([]);
          for (let i = 0; i < Math.min(8, v.ringCount); i++) {
            const a = i / Math.min(8, v.ringCount) * Math.PI * 2;
            ctx.beginPath(); ctx.moveTo(p.x + Math.cos(a) * 22, p.y + Math.sin(a) * 22); ctx.lineTo(p.x + Math.cos(a) * 34, p.y + Math.sin(a) * 34); ctx.stroke();
          }
          ctx.restore();
          break;
        }
        case 'lockOn': {
          const from = camera.worldToScreen(v.x, v.y);
          const to = camera.worldToScreen(v.tx, v.ty);
          ctx.strokeStyle = '#ff8a4a';
          ctx.lineWidth = 1;
          ctx.setLineDash([2, 2]);
          ctx.beginPath();
          ctx.moveTo(from.x, from.y);
          ctx.lineTo(to.x, to.y);
          ctx.stroke();
          ctx.setLineDash([]);
          break;
        }
        case 'laserAim':
        case 'laserBeam': {
          const p = camera.worldToScreen(v.x, v.y);
          const ex = p.x + Math.cos(v.angle) * v.length;
          const ey = p.y + Math.sin(v.angle) * v.length;
          const beamColor = v.color || '#ff3b3b';
          ctx.strokeStyle = v.kind === 'laserBeam' ? beamColor : beamColor + '88';
          ctx.lineWidth = v.kind === 'laserBeam' ? v.width : 1;
          ctx.beginPath();
          ctx.moveTo(p.x, p.y);
          ctx.lineTo(ex, ey);
          ctx.stroke();
          break;
        }
        case 'shield': {
          const p = camera.worldToScreen(v.x, v.y);
          ctx.strokeStyle = '#ffffff';
          ctx.globalAlpha = 0.5;
          ctx.lineWidth = 2;
          ctx.beginPath();
          ctx.arc(p.x, p.y, boss.radius + 5, 0, Math.PI * 2);
          ctx.stroke();
          ctx.globalAlpha = 1;
          break;
        }
        // Riven Choir's Echo Chorus — dashed outline at each temporary
        // echo's position during the telegraph, previewing where the
        // synchronized ring bursts are about to fire from.
        case 'echoTelegraph': {
          ctx.strokeStyle = '#c04ae0';
          ctx.lineWidth = 1;
          ctx.setLineDash([2, 2]);
          for (const echo of v.echoes) {
            const p = camera.worldToScreen(echo.x, echo.y);
            ctx.beginPath();
            ctx.arc(p.x, p.y, boss.radius * 0.6, 0, Math.PI * 2);
            ctx.stroke();
          }
          ctx.setLineDash([]);
          break;
        }
        // Ashen Throne's Area Denial — zones scattered across the arena;
        // solid red once `imminent` (about to detonate), dashed while
        // still just a telegraph.
        case 'denialZones': {
          ctx.strokeStyle = highContrast ? this._arenaInk : '#e8873a';
          ctx.fillStyle = highContrast ? 'rgba(255,255,255,0.28)' : 'rgba(232,135,58,0.2)';
          ctx.lineWidth = v.imminent ? 2 : 1;
          if (!v.imminent) ctx.setLineDash([3, 3]);
          for (const zone of v.zones) {
            const p = camera.worldToScreen(zone.x, zone.y);
            ctx.beginPath();
            ctx.arc(p.x, p.y, zone.radius, 0, Math.PI * 2);
            ctx.fill();
            ctx.stroke();
          }
          ctx.setLineDash([]);
          break;
        }
        // The Great Hush's Hushfall — desaturated zones of silence; standing
        // in one mutes the player's weapons (see patterns/Hushfall.js). Grey,
        // solid once live, dashed while still a telegraph.
        case 'hushZones': {
          ctx.strokeStyle = highContrast ? this._arenaInk : '#9fb0a8';
          ctx.fillStyle = highContrast ? 'rgba(255,255,255,0.18)' : 'rgba(159,176,168,0.15)';
          ctx.lineWidth = v.imminent ? 2 : 1;
          if (!v.imminent) ctx.setLineDash([3, 3]);
          for (const zone of v.zones) {
            const p = camera.worldToScreen(zone.x, zone.y);
            ctx.beginPath();
            ctx.arc(p.x, p.y, zone.radius, 0, Math.PI * 2);
            ctx.fill();
            ctx.stroke();
          }
          if (v.totalHush) {
            ctx.fillStyle = highContrast ? 'rgba(255,255,255,0.12)' : 'rgba(8,8,10,0.34)';
            ctx.fillRect(0, 0, VIRTUAL_WIDTH, VIRTUAL_HEIGHT);
          }
          ctx.setLineDash([]);
          break;
        }
        // The Swell's telegraph — a dashed ring that previews how big the
        // incoming volley is (it grew with the player's recent damage), so
        // the mirror reads as cause -> effect.
        case 'swellRing': {
          const p = camera.worldToScreen(v.x, v.y);
          ctx.strokeStyle = highContrast ? this._arenaInk : '#57e0d0';
          ctx.globalAlpha = 0.4 + 0.5 * v.t;
          ctx.lineWidth = 1.5;
          ctx.setLineDash([4, 4]);
          ctx.beginPath();
          ctx.arc(p.x, p.y, v.radius * (0.4 + 0.6 * v.t), 0, Math.PI * 2);
          ctx.stroke();
          ctx.setLineDash([]);
          ctx.globalAlpha = 1;
          break;
        }
        // Ashen Throne's Cross Beam Sweep — a horizontal + vertical line
        // spanning the whole arena rectangle, anchored where the player
        // was standing when the telegraph began.
        case 'crossBeam': {
          const left = camera.worldToScreen(ARENA.x, v.y);
          const right = camera.worldToScreen(ARENA.x + ARENA.width, v.y);
          const top = camera.worldToScreen(v.x, ARENA.y);
          const bottom = camera.worldToScreen(v.x, ARENA.y + ARENA.height);
          ctx.strokeStyle = v.imminent ? '#ff3b3b' : 'rgba(255,90,90,0.5)';
          ctx.lineWidth = v.imminent ? v.thickness : 1;
          ctx.beginPath();
          ctx.moveTo(left.x, left.y);
          ctx.lineTo(right.x, right.y);
          ctx.moveTo(top.x, top.y);
          ctx.lineTo(bottom.x, bottom.y);
          ctx.stroke();
          break;
        }
        // Desperation finisher visuals — SovereignCollapse's expanding
        // shockwave preview and ThroneJudgment's inverted safe-zone.
        case 'growingRing': {
          const p = camera.worldToScreen(v.x, v.y);
          ctx.strokeStyle = '#e5484d';
          ctx.lineWidth = 2;
          ctx.beginPath();
          ctx.arc(p.x, p.y, v.radius * v.t, 0, Math.PI * 2);
          ctx.stroke();
          break;
        }
        case 'safeZone': {
          const p = camera.worldToScreen(v.x, v.y);
          ctx.strokeStyle = '#4ade80';
          ctx.lineWidth = 2;
          ctx.setLineDash([4, 2]);
          ctx.beginPath();
          ctx.arc(p.x, p.y, v.radius, 0, Math.PI * 2);
          ctx.stroke();
          ctx.setLineDash([]);
          break;
        }
        // The Unmade King's death spiral — the safe pocket remains the clear
        // rule, while the outer pulse and crown rays communicate that the
        // surrounding barrage is escalating toward its final phase.
        case 'unmakingFinale': {
          const p = camera.worldToScreen(v.x, v.y);
          const color = highContrast ? this._arenaInk : (v.final ? '#ff3b6b' : '#c084fc');
          ctx.save();
          ctx.strokeStyle = color;
          ctx.fillStyle = highContrast
            ? 'rgba(255,255,255,0.08)'
            : (v.final ? 'rgba(255,59,107,0.10)' : 'rgba(192,132,252,0.08)');
          ctx.globalAlpha = 0.9;
          ctx.beginPath(); ctx.arc(p.x, p.y, v.radius, 0, Math.PI * 2); ctx.fill(); ctx.stroke();
          ctx.setLineDash([4, 3]);
          ctx.globalAlpha = 0.5;
          ctx.beginPath(); ctx.arc(p.x, p.y, v.radius + 7, 0, Math.PI * 2); ctx.stroke();
          ctx.setLineDash([]);
          const rayCount = 6 + v.phase * 2;
          const rayLength = 24 + v.progress * 54;
          ctx.globalAlpha = v.final ? 0.85 : 0.45;
          for (let i = 0; i < rayCount; i++) {
            const angle = performance.now() / 1800 + (i / rayCount) * Math.PI * 2;
            ctx.beginPath();
            ctx.moveTo(p.x + Math.cos(angle) * (v.radius + 10), p.y + Math.sin(angle) * (v.radius + 10));
            ctx.lineTo(p.x + Math.cos(angle) * (v.radius + 10 + rayLength), p.y + Math.sin(angle) * (v.radius + 10 + rayLength));
            ctx.stroke();
          }
          if (v.pulse) {
            ctx.globalAlpha = 0.95;
            ctx.lineWidth = 2;
            ctx.beginPath(); ctx.arc(p.x, p.y, v.radius + 16, 0, Math.PI * 2); ctx.stroke();
          }
          ctx.restore();
          break;
        }
        // Grid Barrage — every interior grid line spanning the arena, with the
        // one safe column + safe row drawn green so the safe intersection reads.
        case 'gridLines': {
          ctx.lineWidth = v.imminent ? 2 : 1;
          if (!v.imminent) ctx.setLineDash([3, 3]);
          for (const x of v.xs) {
            const safe = x === v.safeX;
            ctx.strokeStyle = safe ? '#4ade80' : (v.imminent ? '#8ad1ff' : 'rgba(138,209,255,0.5)');
            const top = camera.worldToScreen(x, ARENA.y);
            const bottom = camera.worldToScreen(x, ARENA.y + ARENA.height);
            ctx.beginPath();
            ctx.moveTo(top.x, top.y);
            ctx.lineTo(bottom.x, bottom.y);
            ctx.stroke();
          }
          for (const y of v.ys) {
            const safe = y === v.safeY;
            ctx.strokeStyle = safe ? '#4ade80' : (v.imminent ? '#8ad1ff' : 'rgba(138,209,255,0.5)');
            const left = camera.worldToScreen(ARENA.x, y);
            const right = camera.worldToScreen(ARENA.x + ARENA.width, y);
            ctx.beginPath();
            ctx.moveTo(left.x, left.y);
            ctx.lineTo(right.x, right.y);
            ctx.stroke();
          }
          ctx.setLineDash([]);
          break;
        }
        // Telegraphed Charge — a bold lane along the boss's charge path. Dim/
        // dashed early, then solid + thick once `imminent` (about to commit).
        case 'chargeLane': {
          const p = camera.worldToScreen(v.x, v.y);
          const ex = p.x + Math.cos(v.angle) * v.length;
          const ey = p.y + Math.sin(v.angle) * v.length;
          ctx.strokeStyle = v.imminent ? 'rgba(255,59,59,0.8)' : 'rgba(255,90,90,0.35)';
          ctx.lineWidth = v.imminent ? v.width : Math.max(2, v.width * 0.5);
          if (!v.imminent) ctx.setLineDash([4, 4]);
          ctx.beginPath();
          ctx.moveTo(p.x, p.y);
          ctx.lineTo(ex, ey);
          ctx.stroke();
          ctx.setLineDash([]);
          break;
        }
        case 'sovereignSanctum': {
          const p = camera.worldToScreen(v.x, v.y);
          const radius = Math.max(70, Math.hypot(ARENA.width, ARENA.height));
          ctx.save();
          ctx.translate(p.x, p.y);
          ctx.rotate(v.angle);
          ctx.fillStyle = v.active ? 'rgba(255,59,107,0.24)' : 'rgba(255,138,74,0.12)';
          ctx.beginPath();
          ctx.moveTo(0, 0);
          ctx.arc(0, 0, radius, v.safeWidth / 2, Math.PI * 2 - v.safeWidth / 2);
          ctx.closePath();
          ctx.fill();
          ctx.strokeStyle = v.active ? '#ff5d6c' : '#ffb36b';
          ctx.lineWidth = v.active ? 2 : 1;
          ctx.beginPath(); ctx.arc(0, 0, v.innerRadius, 0, Math.PI * 2); ctx.stroke();
          ctx.restore();
          break;
        }
        case 'echoStep': {
          ctx.save();
          ctx.strokeStyle = '#c084fc';
          ctx.setLineDash([2, 2]);
          for (const pos of v.positions ?? []) {
            const p = camera.worldToScreen(pos.x, pos.y);
            ctx.beginPath(); ctx.arc(p.x, p.y, 7, 0, Math.PI * 2); ctx.stroke();
            ctx.beginPath(); ctx.moveTo(p.x - 3, p.y); ctx.lineTo(p.x + 3, p.y); ctx.stroke();
          }
          ctx.setLineDash([]);
          ctx.restore();
          break;
        }
        case 'throneDivide': {
          ctx.save();
          const center = v.vertical ? ARENA.x + ARENA.width * v.offset : ARENA.y + ARENA.height * v.offset;
          const gapCenter = (v.vertical ? ARENA.y : ARENA.x) + (v.vertical ? ARENA.height : ARENA.width) / 2;
          ctx.strokeStyle = v.active ? '#ff5d3b' : '#ff9f43';
          ctx.fillStyle = v.active ? 'rgba(255,59,59,0.3)' : 'rgba(255,159,67,0.12)';
          ctx.lineWidth = v.active ? v.width : 2;
          if (v.vertical) {
            const a = camera.worldToScreen(center, ARENA.y);
            const b = camera.worldToScreen(center, gapCenter - v.gap / 2);
            const c = camera.worldToScreen(center, gapCenter + v.gap / 2);
            const d = camera.worldToScreen(center, ARENA.y + ARENA.height);
            for (const [from, to] of [[a, b], [c, d]]) { ctx.beginPath(); ctx.moveTo(from.x, from.y); ctx.lineTo(to.x, to.y); ctx.stroke(); }
          } else {
            const a = camera.worldToScreen(ARENA.x, center);
            const b = camera.worldToScreen(gapCenter - v.gap / 2, center);
            const c = camera.worldToScreen(gapCenter + v.gap / 2, center);
            const d = camera.worldToScreen(ARENA.x + ARENA.width, center);
            for (const [from, to] of [[a, b], [c, d]]) { ctx.beginPath(); ctx.moveTo(from.x, from.y); ctx.lineTo(to.x, to.y); ctx.stroke(); }
          }
          ctx.restore();
          break;
        }
        case 'corruptedCopy': {
          const p = camera.worldToScreen(v.x, v.y);
          ctx.save();
          ctx.strokeStyle = v.color;
          ctx.globalAlpha = 0.7;
          ctx.setLineDash([3, 2]);
          ctx.beginPath(); ctx.arc(p.x, p.y, 9 + Math.sin(performance.now() / 100) * 2, 0, Math.PI * 2); ctx.stroke();
          ctx.setLineDash([]);
          ctx.restore();
          break;
        }
        case 'prismMines': {
          for (const mine of v.mines) {
            const p = camera.worldToScreen(mine.x, mine.y);
            ctx.strokeStyle = highContrast ? this._arenaInk : '#c084fc';
            ctx.fillStyle = v.imminent
              ? (highContrast ? 'rgba(255,255,255,0.45)' : 'rgba(192,132,252,0.45)')
              : (highContrast ? 'rgba(255,255,255,0.12)' : 'rgba(192,132,252,0.15)');
            ctx.lineWidth = v.imminent ? 2.5 : 1.5;
            if (!v.imminent) ctx.setLineDash([4, 3]);
            ctx.beginPath();
            ctx.arc(p.x, p.y, mine.radius, 0, Math.PI * 2);
            ctx.fill();
            ctx.stroke();
            ctx.setLineDash([]);
            ctx.beginPath();
            ctx.moveTo(p.x - mine.radius * 0.55, p.y);
            ctx.lineTo(p.x + mine.radius * 0.55, p.y);
            ctx.moveTo(p.x, p.y - mine.radius * 0.55);
            ctx.lineTo(p.x, p.y + mine.radius * 0.55);
            ctx.stroke();
          }
          break;
        }
        // Maelstrom Verge's Riptide Vortex — three spiral arms rotating
        // around the pull center, a dashed core danger ring, and a faint
        // outer boundary showing where the current's grip ends.
        case 'vortex': {
          const p = camera.worldToScreen(v.x, v.y);
          const color = highContrast ? this._arenaInk : '#4dd8c7';
          ctx.save();
          ctx.strokeStyle = color;
          ctx.globalAlpha = v.active ? 0.75 : 0.4 + v.t * 0.3;
          ctx.lineWidth = 1;
          for (let arm = 0; arm < 3; arm++) {
            ctx.beginPath();
            for (let s = 0; s <= 10; s++) {
              const frac = s / 10;
              const angle = v.spin + arm * (Math.PI * 2 / 3) + frac * 2.6;
              const r = (0.15 + frac * 0.85) * v.pullRadius * (v.active ? 1 : v.t);
              const x = p.x + Math.cos(angle) * r;
              const y = p.y + Math.sin(angle) * r;
              if (s === 0) ctx.moveTo(x, y); else ctx.lineTo(x, y);
            }
            ctx.stroke();
          }
          ctx.globalAlpha = v.active ? 0.85 : 0.5;
          ctx.setLineDash([3, 2]);
          ctx.beginPath();
          ctx.arc(p.x, p.y, v.coreRadius, 0, Math.PI * 2);
          ctx.stroke();
          ctx.setLineDash([]);
          ctx.globalAlpha = 0.2;
          ctx.beginPath();
          ctx.arc(p.x, p.y, v.pullRadius, 0, Math.PI * 2);
          ctx.stroke();
          ctx.restore();
          break;
        }
        // Maelstrom Verge's Undertow — parallel flow dashes drifting across
        // the whole visible arena in the push direction; brighter once the
        // current is actually live.
        case 'currentFlow': {
          const color = highContrast ? this._arenaInk : '#4dd8c7';
          ctx.save();
          ctx.strokeStyle = color;
          ctx.globalAlpha = v.active ? 0.4 : 0.22;
          ctx.lineWidth = 1;
          const spacing = 34;
          const dashLength = 12;
          const drift = (v.t * 80 * v.sign) % spacing;
          for (let wy = ARENA.y + 12; wy < ARENA.y + ARENA.height; wy += 24) {
            for (let wx = ARENA.x - spacing; wx < ARENA.x + ARENA.width + spacing; wx += spacing) {
              const from = camera.worldToScreen(wx + drift, wy);
              const to = camera.worldToScreen(wx + drift + dashLength * v.sign, wy);
              ctx.beginPath();
              ctx.moveTo(from.x, from.y);
              ctx.lineTo(to.x, to.y);
              ctx.stroke();
            }
          }
          ctx.restore();
          break;
        }
        // The Unblinking's Petrifying Gaze — an iris ring dilating open
        // during the telegraph; while the gaze is live, a bold double ring
        // with slowly sweeping lash ticks. Movement is the hazard, so the
        // visual centers on the eye rather than painting any floor zone.
        case 'gaze': {
          const p = camera.worldToScreen(v.x, v.y);
          const color = highContrast ? this._arenaInk : '#8ae06e';
          ctx.save();
          ctx.strokeStyle = color;
          if (!v.active) {
            ctx.globalAlpha = 0.35 + v.t * 0.45;
            ctx.setLineDash([4, 3]);
            ctx.beginPath();
            ctx.arc(p.x, p.y, 30 - v.t * 16, 0, Math.PI * 2);
            ctx.stroke();
            ctx.setLineDash([]);
          } else {
            ctx.globalAlpha = 0.85;
            ctx.lineWidth = 2;
            ctx.beginPath();
            ctx.arc(p.x, p.y, 26, 0, Math.PI * 2);
            ctx.stroke();
            ctx.lineWidth = 1;
            const sweep = performance.now() / 900;
            for (let i = 0; i < 8; i++) {
              const angle = sweep + (i / 8) * Math.PI * 2;
              ctx.beginPath();
              ctx.moveTo(p.x + Math.cos(angle) * 28, p.y + Math.sin(angle) * 28);
              ctx.lineTo(p.x + Math.cos(angle) * 34, p.y + Math.sin(angle) * 34);
              ctx.stroke();
            }
          }
          ctx.restore();
          break;
        }
        // The Chronophage's rewind warning — a dashed clock face shrinking
        // onto the boss with a pair of hands sweeping BACKWARD; solid and
        // bright in the 0.6s window before every bullet on screen reverses.
        case 'rewindPulse': {
          const p = camera.worldToScreen(v.x, v.y);
          const color = highContrast ? this._arenaInk : '#8c7bff';
          ctx.save();
          ctx.strokeStyle = color;
          ctx.globalAlpha = v.imminent ? 0.9 : 0.5;
          ctx.lineWidth = v.imminent ? 2 : 1;
          if (!v.imminent) ctx.setLineDash([4, 3]);
          const radius = 34 - v.t * 12;
          ctx.beginPath();
          ctx.arc(p.x, p.y, radius, 0, Math.PI * 2);
          ctx.stroke();
          ctx.setLineDash([]);
          const hand = -performance.now() / 350;
          ctx.beginPath();
          ctx.moveTo(p.x, p.y);
          ctx.lineTo(p.x + Math.cos(hand) * radius * 0.8, p.y + Math.sin(hand) * radius * 0.8);
          ctx.moveTo(p.x, p.y);
          ctx.lineTo(p.x + Math.cos(hand * 0.5) * radius * 0.55, p.y + Math.sin(hand * 0.5) * radius * 0.55);
          ctx.stroke();
          ctx.restore();
          break;
        }
        // The Chronophage's Déjà Vu — the player's recorded breadcrumb
        // trail as a dotted line, with armed strikes as circles that turn
        // solid when about to detonate.
        case 'dejaVu': {
          const color = highContrast ? this._arenaInk : '#8c7bff';
          ctx.save();
          ctx.strokeStyle = color;
          if (v.trail.length > 1) {
            ctx.globalAlpha = 0.4;
            ctx.setLineDash([2, 3]);
            ctx.beginPath();
            for (let i = 0; i < v.trail.length; i++) {
              const p = camera.worldToScreen(v.trail[i].x, v.trail[i].y);
              if (i === 0) ctx.moveTo(p.x, p.y); else ctx.lineTo(p.x, p.y);
            }
            ctx.stroke();
            ctx.setLineDash([]);
          }
          for (const strike of v.strikes) {
            const p = camera.worldToScreen(strike.x, strike.y);
            ctx.globalAlpha = strike.imminent ? 0.9 : 0.55;
            ctx.fillStyle = strike.imminent
              ? (highContrast ? 'rgba(255,255,255,0.4)' : 'rgba(140,123,255,0.4)')
              : 'rgba(140,123,255,0.12)';
            ctx.lineWidth = strike.imminent ? 2 : 1;
            if (!strike.imminent) ctx.setLineDash([3, 2]);
            ctx.beginPath();
            ctx.arc(p.x, p.y, strike.radius, 0, Math.PI * 2);
            ctx.fill();
            ctx.stroke();
            ctx.setLineDash([]);
          }
          ctx.restore();
          break;
        }
        // Hollow Sovereign's Summons — a royal seal (double ring + rotating
        // crown spikes) marking where the player is about to be teleported,
        // with a designation line from the boss to the spot.
        case 'summonsSeal': {
          const p = camera.worldToScreen(v.x, v.y);
          const from = camera.worldToScreen(v.fromX, v.fromY);
          const color = highContrast ? this._arenaInk : '#ffd166';
          ctx.save();
          ctx.strokeStyle = color;
          ctx.globalAlpha = v.active ? 0.9 : 0.35 + v.t * 0.5;
          ctx.lineWidth = 1;
          ctx.setLineDash([3, 3]);
          ctx.beginPath();
          ctx.moveTo(from.x, from.y);
          ctx.lineTo(p.x, p.y);
          ctx.stroke();
          ctx.setLineDash([]);
          ctx.lineWidth = v.active ? 2 : 1;
          ctx.beginPath();
          ctx.arc(p.x, p.y, 14, 0, Math.PI * 2);
          ctx.stroke();
          ctx.beginPath();
          ctx.arc(p.x, p.y, 8, 0, Math.PI * 2);
          ctx.stroke();
          const spin = performance.now() / 500;
          for (let i = 0; i < 3; i++) {
            const angle = spin + (i / 3) * Math.PI * 2;
            ctx.beginPath();
            ctx.moveTo(p.x + Math.cos(angle) * 14, p.y + Math.sin(angle) * 14);
            ctx.lineTo(p.x + Math.cos(angle) * 20, p.y + Math.sin(angle) * 20);
            ctx.stroke();
          }
          ctx.restore();
          break;
        }
        // Riven Choir's Choral Staves — horizontal voice lines across the
        // arena; singing lines brighten through the wind-up and flash as a
        // filled band on the beat, resting lines stay faint and dashed.
        case 'staves': {
          const color = highContrast ? this._arenaInk : '#c084fc';
          ctx.save();
          for (const line of v.lines) {
            const left = camera.worldToScreen(ARENA.x, line.y);
            const right = camera.worldToScreen(ARENA.x + ARENA.width, line.y);
            if (line.resting) {
              ctx.strokeStyle = color;
              ctx.globalAlpha = 0.2;
              ctx.lineWidth = 1;
              ctx.setLineDash([5, 5]);
              ctx.beginPath();
              ctx.moveTo(left.x, left.y);
              ctx.lineTo(right.x, right.y);
              ctx.stroke();
              ctx.setLineDash([]);
              continue;
            }
            if (v.singing) {
              ctx.fillStyle = highContrast ? 'rgba(255,255,255,0.4)' : 'rgba(192,132,252,0.38)';
              ctx.fillRect(left.x, left.y - v.bandHalf, right.x - left.x, v.bandHalf * 2);
            }
            ctx.strokeStyle = color;
            ctx.globalAlpha = 0.3 + v.windup * 0.6;
            ctx.lineWidth = v.singing ? 2 : 1;
            ctx.beginPath();
            ctx.moveTo(left.x, left.y);
            ctx.lineTo(right.x, right.y);
            ctx.stroke();
          }
          ctx.restore();
          break;
        }
        // Ashen Throne's Ember Wake — the player's own footsteps smolder
        // (dashed warming rings) then ignite into burning ground (filled
        // ember circles that fade as they burn out).
        // Act III — the Ostinato's replayed path. The ghost line is the
        // fairness lever the bible's §11 calls for: the whole stretch of path
        // that is *about* to ignite is drawn faint before it does, so the
        // counterplay is legible on screen rather than remembered. Only the
        // heads (where the replay actually is right now) are hot.
        case 'ostinatoTrail': {
          ctx.save();
          // Hot orange, NOT the Ostinato's gold arena accent. The accent is
          // also Quill's glyph colour and the Glyphpen's, so a Quill run
          // against this boss would otherwise draw her own harmless marks and
          // the boss's lethal replay in the same gold discs. The hazard has to
          // read as fire — same colour Ember Wake burns in, for the same reason.
          const color = highContrast ? this._arenaInk : '#ff7a3a';
          if (v.ghost.length > 1) {
            ctx.strokeStyle = color;
            ctx.globalAlpha = v.imminent ? 0.22 : 0.12;
            ctx.lineWidth = Math.max(1, v.width * camera.zoom * 0.5);
            ctx.lineJoin = 'round';
            ctx.setLineDash([3, 4]);
            ctx.beginPath();
            const first = camera.worldToScreen(v.ghost[0].x, v.ghost[0].y);
            ctx.moveTo(first.x, first.y);
            for (const point of v.ghost.slice(1)) {
              const p = camera.worldToScreen(point.x, point.y);
              ctx.lineTo(p.x, p.y);
            }
            ctx.stroke();
            ctx.setLineDash([]);
          }
          if (v.imminent) {
            for (const head of v.heads) {
              const p = camera.worldToScreen(head.x, head.y);
              const r = v.width * camera.zoom;
              ctx.globalAlpha = 0.45;
              ctx.fillStyle = highContrast ? 'rgba(255,255,255,0.6)' : 'rgba(255,122,58,0.55)';
              ctx.beginPath();
              ctx.arc(p.x, p.y, r, 0, Math.PI * 2);
              ctx.fill();
              ctx.globalAlpha = 1;
              ctx.fillStyle = highContrast ? this._arenaInk : '#ffd9a8';
              ctx.beginPath();
              ctx.arc(p.x, p.y, r * 0.35, 0, Math.PI * 2);
              ctx.fill();
            }
          }
          ctx.restore();
          break;
        }
        // Act III — the Understudy. Names what it has picked up off the player,
        // because "that's my build" is the entire read of the fight and there
        // is no other way to see it before the volley lands.
        case 'understudy': {
          const p = camera.worldToScreen(v.x, v.y);
          ctx.save();
          ctx.strokeStyle = (highContrast || v.curtainCall) ? this._arenaInk : '#7fe0a0';
          ctx.globalAlpha = v.imminent ? 0.8 : 0.4;
          ctx.lineWidth = v.curtainCall ? 2 : 1;
          ctx.setLineDash([2, 3]);
          ctx.beginPath();
          ctx.arc(p.x, p.y, boss.radius + 7, 0, Math.PI * 2);
          ctx.stroke();
          ctx.setLineDash([]);
          ctx.restore();
          break;
        }
        case 'emberWake': {
          ctx.save();
          for (const smolder of v.smolders) {
            const p = camera.worldToScreen(smolder.x, smolder.y);
            ctx.strokeStyle = highContrast ? this._arenaInk : '#ff9f43';
            ctx.globalAlpha = 0.25 + smolder.t * 0.45;
            ctx.lineWidth = 1;
            ctx.setLineDash([2, 3]);
            ctx.beginPath();
            ctx.arc(p.x, p.y, v.radius * (0.5 + smolder.t * 0.5), 0, Math.PI * 2);
            ctx.stroke();
            ctx.setLineDash([]);
          }
          for (const patch of v.patches) {
            const p = camera.worldToScreen(patch.x, patch.y);
            ctx.globalAlpha = 0.25 + patch.life * 0.45;
            ctx.fillStyle = highContrast ? 'rgba(255,255,255,0.5)' : 'rgba(255,159,67,0.5)';
            ctx.beginPath();
            ctx.arc(p.x, p.y, v.radius, 0, Math.PI * 2);
            ctx.fill();
            ctx.fillStyle = highContrast ? this._arenaInk : '#ffd166';
            ctx.beginPath();
            ctx.arc(p.x, p.y, v.radius * 0.35, 0, Math.PI * 2);
            ctx.fill();
          }
          ctx.restore();
          break;
        }
        // The Iron Cadence's beat indicator (Staccato Volley) — a ring that
        // contracts onto the boss over each beat and blinks wide open on
        // the downbeat, so the lurch rhythm is readable off the boss itself
        // even before the first frozen lattice forms.
        case 'cadenceBeat': {
          const p = camera.worldToScreen(v.x, v.y);
          const color = highContrast ? this._arenaInk : '#9fb8e8';
          ctx.save();
          ctx.strokeStyle = color;
          ctx.globalAlpha = v.hop ? 0.9 : 0.45;
          ctx.lineWidth = v.hop ? 2 : 1;
          const radius = boss.radius + 4 + (1 - v.beatT) * 14;
          ctx.beginPath();
          ctx.arc(p.x, p.y, radius, 0, Math.PI * 2);
          ctx.stroke();
          // Four conductor ticks on the ring — reads as a beat dial.
          for (let i = 0; i < 4; i++) {
            const angle = (i / 4) * Math.PI * 2 + v.beatT * Math.PI * 0.5;
            ctx.beginPath();
            ctx.moveTo(p.x + Math.cos(angle) * radius, p.y + Math.sin(angle) * radius);
            ctx.lineTo(p.x + Math.cos(angle) * (radius + 3), p.y + Math.sin(angle) * (radius + 3));
            ctx.stroke();
          }
          ctx.restore();
          break;
        }
        // The Iron Cadence's Grand Pause — a fermata (arc + dot) held over
        // the boss while every bullet on screen hangs frozen; flares white
        // in the warn window right before the release surge.
        case 'fermata': {
          const p = camera.worldToScreen(v.x, v.y);
          const color = (v.warn || highContrast) ? this._arenaInk : '#9fb8e8';
          ctx.save();
          ctx.strokeStyle = color;
          ctx.fillStyle = color;
          ctx.globalAlpha = v.holding ? (v.warn ? 1 : 0.85) : 0.35 + v.t * 0.5;
          ctx.lineWidth = v.warn ? 2.5 : 1.5;
          const width = 16 + (v.holding ? Math.sin(performance.now() / 180) * 1.5 : v.t * 4);
          ctx.beginPath();
          ctx.arc(p.x, p.y - boss.radius - 8, width, Math.PI * 1.15, Math.PI * 1.85);
          ctx.stroke();
          ctx.beginPath();
          ctx.arc(p.x, p.y - boss.radius - 12, v.warn ? 2.5 : 1.8, 0, Math.PI * 2);
          ctx.fill();
          ctx.restore();
          break;
        }
        // The Famished Court's Feast — dashed emergence marks at each spawn
        // point during the telegraph, then dotted procession lines from
        // every living courtier to the maw: the routes to cut.
        case 'feastMarks': {
          const color = highContrast ? this._arenaInk : '#cdd45a';
          ctx.save();
          ctx.strokeStyle = color;
          for (const mark of v.marks) {
            const m = camera.worldToScreen(mark.x, mark.y);
            if (v.marching) {
              const b = camera.worldToScreen(v.x, v.y);
              ctx.globalAlpha = 0.3;
              ctx.setLineDash([2, 4]);
              ctx.lineWidth = 1;
              ctx.beginPath();
              ctx.moveTo(m.x, m.y);
              ctx.lineTo(b.x, b.y);
              ctx.stroke();
              ctx.setLineDash([]);
            } else {
              ctx.globalAlpha = 0.3 + v.t * 0.5;
              ctx.setLineDash([3, 3]);
              ctx.lineWidth = 1;
              ctx.beginPath();
              ctx.arc(m.x, m.y, 6 + (1 - v.t) * 4, 0, Math.PI * 2);
              ctx.stroke();
              ctx.setLineDash([]);
            }
          }
          ctx.restore();
          break;
        }
        // The Famished Court's Repast — suction spiral: arms curling INTO
        // the maw (the Maelstrom vortex reversed), tightening as the inhale
        // builds toward the spit.
        case 'repastMaw': {
          const p = camera.worldToScreen(v.x, v.y);
          const color = highContrast ? this._arenaInk : '#cdd45a';
          ctx.save();
          ctx.strokeStyle = color;
          ctx.globalAlpha = v.inhaling ? 0.75 : 0.35 + v.t * 0.4;
          ctx.lineWidth = 1;
          const spin = -performance.now() / 400; // counter-spin reads as "inward"
          const reach = v.inhaling ? 60 * (1 - v.t * 0.5) : 45;
          for (let arm = 0; arm < 3; arm++) {
            ctx.beginPath();
            for (let s = 0; s <= 8; s++) {
              const frac = s / 8;
              const angle = spin + arm * (Math.PI * 2 / 3) - frac * 2.2;
              const r = boss.radius * 0.5 + frac * reach;
              const x = p.x + Math.cos(angle) * r;
              const y = p.y + Math.sin(angle) * r;
              if (s === 0) ctx.moveTo(x, y); else ctx.lineTo(x, y);
            }
            ctx.stroke();
          }
          ctx.restore();
          break;
        }
        // The Jealous Regent's Embrace — the devotion ring, with inward
        // "yearning" ticks just outside it; the whole thing flares when the
        // player is outside and being dragged back.
        case 'embrace': {
          const p = camera.worldToScreen(v.x, v.y);
          const color = highContrast ? this._arenaInk : '#ff87b3';
          ctx.save();
          ctx.strokeStyle = color;
          ctx.globalAlpha = v.active ? (v.playerOutside ? 0.95 : 0.6) : 0.3 + v.t * 0.4;
          ctx.lineWidth = v.playerOutside ? 2 : 1;
          if (!v.active) ctx.setLineDash([4, 3]);
          ctx.beginPath();
          ctx.arc(p.x, p.y, v.radius * (v.active ? 1 : 0.6 + v.t * 0.4), 0, Math.PI * 2);
          ctx.stroke();
          ctx.setLineDash([]);
          if (v.active) {
            // Inward ticks drifting toward the ring — the pull, made visible.
            const drift = (performance.now() / 900) % 1;
            for (let i = 0; i < 14; i++) {
              const angle = (i / 14) * Math.PI * 2;
              const r0 = v.radius + 18 - drift * 12;
              ctx.globalAlpha = (v.playerOutside ? 0.7 : 0.35) * (1 - drift * 0.6);
              ctx.beginPath();
              ctx.moveTo(p.x + Math.cos(angle) * r0, p.y + Math.sin(angle) * r0);
              ctx.lineTo(p.x + Math.cos(angle) * (r0 - 5), p.y + Math.sin(angle) * (r0 - 5));
              ctx.stroke();
            }
          }
          ctx.restore();
          break;
        }
        // The Jealous Regent's Waltz — dashed preview of the blade ring
        // with its two doorway gaps, before the daggers themselves spawn.
        case 'waltzRing': {
          const p = camera.worldToScreen(v.x, v.y);
          const color = highContrast ? this._arenaInk : '#ff87b3';
          ctx.save();
          ctx.strokeStyle = color;
          ctx.globalAlpha = 0.3 + v.t * 0.5;
          ctx.lineWidth = 1.5;
          ctx.setLineDash([3, 3]);
          for (let g = 0; g < v.gapCount; g++) {
            const gapCenter = v.spin + (g / v.gapCount) * Math.PI * 2;
            ctx.beginPath();
            ctx.arc(p.x, p.y, v.radius, gapCenter + v.gapHalf, gapCenter + (Math.PI * 2) / v.gapCount - v.gapHalf);
            ctx.stroke();
          }
          ctx.setLineDash([]);
          ctx.restore();
          break;
        }
        // The Jealous Regent's Vow — the anchor seal with a closing timer
        // arc; hold the circle until it winds shut or the seal erupts.
        case 'vowAnchor': {
          const p = camera.worldToScreen(v.x, v.y);
          const color = highContrast ? this._arenaInk : '#ff87b3';
          ctx.save();
          ctx.strokeStyle = color;
          ctx.globalAlpha = 0.35 + v.arming * 0.45;
          ctx.lineWidth = 1.5;
          if (v.arming < 1) ctx.setLineDash([4, 3]);
          ctx.beginPath();
          ctx.arc(p.x, p.y, v.radius, 0, Math.PI * 2);
          ctx.stroke();
          ctx.setLineDash([]);
          // Timer arc winds closed as the hold completes — "keep the vow
          // this much longer."
          if (v.arming >= 1) {
            ctx.globalAlpha = 0.9;
            ctx.lineWidth = 2;
            ctx.beginPath();
            ctx.arc(p.x, p.y, v.radius + 4, -Math.PI / 2, -Math.PI / 2 + (1 - v.t) * Math.PI * 2);
            ctx.stroke();
          }
          // The wedding-band core.
          ctx.globalAlpha = 0.8;
          ctx.lineWidth = 1;
          ctx.beginPath();
          ctx.arc(p.x, p.y, 4, 0, Math.PI * 2);
          ctx.stroke();
          ctx.restore();
          break;
        }
        // The Unmade King's Unmade Ground — arena cells being deleted:
        // flickering dashed outlines through the telegraph, then dark void
        // rectangles slashed with glitch strokes while live.
        case 'unmadeGround': {
          const color = highContrast ? this._arenaInk : '#ff3b6b';
          const flicker = Math.sin(performance.now() / 60) > 0;
          ctx.save();
          for (const cell of v.cells) {
            const p = camera.worldToScreen(cell.x, cell.y);
            if (!v.active) {
              ctx.strokeStyle = color;
              ctx.globalAlpha = (0.2 + v.t * 0.5) * (flicker ? 1 : 0.45);
              ctx.lineWidth = 1;
              ctx.setLineDash([4, 3]);
              ctx.strokeRect(p.x + 2, p.y + 2, cell.w - 4, cell.h - 4);
              ctx.setLineDash([]);
              continue;
            }
            ctx.globalAlpha = 0.72;
            ctx.fillStyle = highContrast ? 'rgba(0,0,0,0.8)' : 'rgba(8,5,14,0.82)';
            ctx.fillRect(p.x, p.y, cell.w, cell.h);
            ctx.strokeStyle = color;
            ctx.globalAlpha = flicker ? 0.7 : 0.35;
            ctx.lineWidth = 1;
            ctx.strokeRect(p.x + 1, p.y + 1, cell.w - 2, cell.h - 2);
            ctx.beginPath();
            ctx.moveTo(p.x + cell.w * 0.2, p.y + cell.h * (flicker ? 0.3 : 0.6));
            ctx.lineTo(p.x + cell.w * 0.8, p.y + cell.h * (flicker ? 0.35 : 0.65));
            ctx.stroke();
          }
          ctx.restore();
          break;
        }
      }
    }
  }

  /**
   * The player's own shots. `alpha` (1 outside boss stages, see
   * _playerFireAlpha) multiplies every alpha in the pass rather than wrapping
   * it, because the styles below set absolute alphas of their own — a wrapping
   * globalAlpha would simply be overwritten by the first one.
   */
  _drawProjectiles(ctx, camera, combat, alpha = 1) {
    // Equipped trail cosmetic (data/cosmetics.js) recolors only the trail
    // streak — the projectile body/ring keeps its weapon color so weapons
    // stay readable at a glance.
    const trailColor = this._cosmetics.trail?.color ?? null;
    // The player's shots are drawn as emitted LIGHT, hostile bullets as solid
    // matter (_drawHostileBulletBody) — two materials, so the sides separate
    // on something other than hue, which they share. Additive also stacks
    // where the player's own fire overlaps, which reads as concentrated
    // output rather than as clutter. Skipped on light arenas, where it would
    // blow out to white; see _additiveSafe.
    const additive = this._additiveSafe;
    if (additive) {
      ctx.save();
      ctx.globalCompositeOperation = 'lighter';
    }
    combat.forEachProjectile((proj) => {
      const p = camera.worldToScreen(proj.x, proj.y);
      if (proj.style !== 'ring' && (proj.prevX !== proj.x || proj.prevY !== proj.y)) {
        const prev = camera.worldToScreen(proj.prevX, proj.prevY);
        ctx.save();
        ctx.globalAlpha = 0.28 * alpha;
        ctx.strokeStyle = trailColor ?? proj.color;
        ctx.lineWidth = Math.max(1, proj.radius * 0.8);
        ctx.beginPath();
        ctx.moveTo(prev.x, prev.y);
        ctx.lineTo(p.x, p.y);
        ctx.stroke();
        ctx.restore();
      }
      if (proj.style === 'ring') {
        // Nova Pulse — a shockwave that expands outward from spawn radius 0
        // up to its full radius over its lifetime (age 0->1), fading out as
        // it grows, instead of popping in at full size and just fading in
        // place. Collision still uses the weapon's full `proj.radius` for
        // the whole lifetime (see CombatSystem) — this is purely visual.
        const age = 1 - Math.max(0, proj.lifetime) / proj.maxLifetime;
        const drawRadius = proj.radius * Math.min(1, age * 1.15); // slight overshoot reads as a "pop"
        ctx.strokeStyle = proj.color;
        ctx.globalAlpha = Math.max(0, 1 - age) * alpha;
        ctx.lineWidth = 3 - age * 1.5;
        ctx.beginPath();
        ctx.arc(p.x, p.y, drawRadius, 0, Math.PI * 2);
        ctx.stroke();
        // Bright inner flash at the origin, shrinking away quickly — sells
        // the sense that the ring is bursting outward from the player.
        ctx.globalAlpha = Math.max(0, (1 - age) * 0.3) * alpha;
        ctx.fillStyle = proj.color;
        ctx.beginPath();
        ctx.arc(p.x, p.y, drawRadius * 0.35, 0, Math.PI * 2);
        ctx.fill();
        ctx.globalAlpha = 1;
      } else {
        ctx.globalAlpha = alpha;
        ctx.fillStyle = proj.color;
        ctx.beginPath();
        ctx.arc(p.x, p.y, proj.radius, 0, Math.PI * 2);
        ctx.fill();
        ctx.globalAlpha = 1;
      }
    });
    if (additive) ctx.restore();
  }

  _drawPlayer(ctx, camera, player) {
    const sprite = PLAYER_SPRITES[this.character.id];
    // Equipped skin (data/cosmetics.js) recolors the body; falls back to the
    // sprite's own baked body hue when no skin is equipped.
    const skinPalette = this._cosmetics.skin?.palette ?? null;
    const bodyColor = skinPalette?.R ?? sprite.palette.R;
    for (const ghost of this.juice.dashAfterimages) {
      const gp = camera.worldToScreen(ghost.x, ghost.y);
      ctx.save();
      ctx.globalAlpha = (ghost.life / ghost.maxLife) * 0.35;
      // Ghost tinted to the player's actual body color (skin or character
      // hue) — previously hardcoded Vess's cyan for every character.
      drawSprite(ctx, sprite, gp.x, gp.y, { paletteOverride: { R: bodyColor } });
      ctx.restore();
    }
    const p = camera.worldToScreen(player.x, player.y);

    // Soft green glow behind the sprite while a heal is fresh (health pod or
    // Vampiric — see the 'player:healed' handler in JuiceDirector) so a heal
    // reads as a visible moment on the player, not just an HP-bar tick.
    if (this.juice.healGlowTimer > 0) {
      const t = this.juice.healGlowTimer / 0.4;
      const glowRadius = player.radius + 8;
      const glow = ctx.createRadialGradient(p.x, p.y, 0, p.x, p.y, glowRadius);
      glow.addColorStop(0, `rgba(74, 222, 128, ${0.45 * t})`);
      glow.addColorStop(1, 'rgba(74, 222, 128, 0)');
      ctx.save();
      ctx.fillStyle = glow;
      ctx.beginPath();
      ctx.arc(p.x, p.y, glowRadius, 0, Math.PI * 2);
      ctx.fill();
      ctx.restore();
    }

    // Flash priority, most urgent first: a fresh hit flinches the sprite
    // red (the same hitFlashTimer tell the boss and every minion already
    // have — the player was the one entity without it); dash i-frames flash
    // white ("you can't be hit right now"); an active ultimate surge
    // tints gold. Red vs. white keeps "I got tagged" and "I'm invincible"
    // visually distinct even mid-dash. An equipped skin is the base palette;
    // the flash spreads over it so a hit still reads red on top of any skin.
    const flash = player.hitFlashTimer > 0 ? { R: '#ff6b7a' }
      : player.isInvincible ? { R: '#ffffff' }
        : player.isSurging ? { R: '#ffd166' } : null;
    const paletteOverride = mergePaletteOverride(skinPalette, flash);
    // Brief scale-pop on revive/ultimate (see JuiceDirector.addPlayerPulse)
    // — decays fast so it reads as a snappy "pop," not a lingering wobble.
    const scale = 1 + this.juice.playerScalePop * 0.3;
    ctx.save();
    // Post-hit mercy window (PLAYER.hitInvulnDuration) outlives the 0.1s red
    // flinch, so it needs its own tell — otherwise the player can't read when
    // they're briefly safe or when the window is about to lapse. Same
    // treatment as the low-HP vignette above: a blink normally, dimmer and
    // STATIC under reduceFlash (which defaults on), so the state stays
    // legible either way without strobing.
    if (player.isHitInvulnerable) {
      ctx.globalAlpha *= this.save.reduceFlash
        ? 0.6
        : (Math.sin(player.hitInvulnRemaining * Math.PI * 16) > 0 ? 0.45 : 1);
    }
    ctx.translate(p.x, p.y);
    ctx.scale(scale, scale);
    ctx.translate(-p.x, -p.y);
    drawSprite(ctx, sprite, p.x, p.y, {
      flipX: player.facing.x < 0,
      paletteOverride,
    });
    // Equipped hat (data/cosmetics.js / HAT_SPRITES) perches above the head,
    // inside the same scale transform so it pops with the body. Drawn with
    // its own palette (no flash tint) so it stays bitmap-cached and keeps its
    // colors when the body flashes.
    const hatSprite = this._cosmetics.hat ? HAT_SPRITES[this._cosmetics.hat.spriteKey] : null;
    if (hatSprite) {
      const bodyHalfH = (sprite.rows.length * sprite.pixelSize) / 2;
      const hatHalfH = (hatSprite.rows.length * hatSprite.pixelSize) / 2;
      const HAT_OVERLAP = 2; // sink the hat's brim slightly onto the head so it doesn't float
      const hatY = p.y - bodyHalfH - hatHalfH + HAT_OVERLAP;
      drawSprite(ctx, hatSprite, p.x, hatY, { flipX: player.facing.x < 0 });
    }
    ctx.restore();

    // NOTE: the focus hitbox is deliberately NOT drawn here — _render draws it
    // after the hostile bullet pass (BULLET-HELL.md §D0).
    this._drawPlayerPulses(ctx, camera);
    this._drawDeathbombPrompt(ctx, camera, player);
  }

  // Focus mode's reveal (BULLET-HELL.md §D4): the player's actual 4px
  // collision circle as a bright dot, ringed by the graze band.
  //
  // Drawn AFTER the sprite — and, since §D0, after the hostile bullets too,
  // from _render rather than from _drawPlayer. The sprite is several times
  // wider than the hull, so a dot behind it is invisible, and the whole point
  // is that the player can finally see the circle the game has always been
  // testing. Both radii are read from the same constants
  // SpawningSystem._checkPlayerCollisions uses, so the drawing cannot drift
  // from the collision it's documenting.
  //
  // Exempt from the reduce settings: this is a readability affordance the player
  // asked for by holding a key, not an ambient effect.
  _drawFocusHitbox(ctx, camera, player) {
    if (player.focusReveal <= 0) return;
    const p = camera.worldToScreen(player.x, player.y);
    const reveal = player.focusReveal;

    ctx.save();
    // Graze band first, so the dot draws over its inner edge. Sized for the
    // nearest bullet's radius (_updateFocusRing) rather than for a point
    // bullet, so the ring is the boundary the collision test will actually
    // apply to the thing being threaded.
    ctx.globalAlpha = reveal * 0.9;
    ctx.strokeStyle = FOCUS.ringColor;
    ctx.lineWidth = 1;
    ctx.beginPath();
    ctx.arc(p.x, p.y, PLAYER.radius + GRAZE.band + this._focusRingBulletRadius, 0, Math.PI * 2);
    ctx.stroke();

    // The inner edge of that same band: where the nearest bullet stops grazing
    // and starts hitting. It moves with the bullet for the same reason the
    // outer ring does — without it the annulus between the dot and the ring
    // would overstate the safe part by exactly the bullet's radius.
    if (this._focusRingBulletRadius > 0.05) {
      ctx.globalAlpha = reveal * 0.55;
      ctx.strokeStyle = FOCUS.contactColor;
      ctx.beginPath();
      ctx.arc(p.x, p.y, PLAYER.radius + this._focusRingBulletRadius, 0, Math.PI * 2);
      ctx.stroke();
    }

    // The hull. A soft halo under it keeps the dot readable against a bright
    // sprite or a wall of bullets without inflating the dot itself — the dot
    // must stay exactly PLAYER.radius or it teaches the wrong hitbox.
    const halo = ctx.createRadialGradient(p.x, p.y, 0, p.x, p.y, PLAYER.radius + 3);
    halo.addColorStop(0, `rgba(0, 0, 0, ${0.5 * reveal})`);
    halo.addColorStop(1, 'rgba(0, 0, 0, 0)');
    ctx.globalAlpha = 1;
    ctx.fillStyle = halo;
    ctx.beginPath();
    ctx.arc(p.x, p.y, PLAYER.radius + 3, 0, Math.PI * 2);
    ctx.fill();

    ctx.globalAlpha = reveal;
    ctx.fillStyle = FOCUS.dotColor;
    ctx.beginPath();
    ctx.arc(p.x, p.y, PLAYER.radius, 0, Math.PI * 2);
    ctx.fill();
    ctx.restore();
  }

  // The deathbomb window (BULLET-HELL.md §D3): a gold ring collapsing onto
  // the player, whose radius IS the time left. Drawn rather than flashed so
  // it survives reduceFlash untouched — that setting kills screen flashes,
  // and this is the one cue that cannot be optional. Its key label rides the
  // crisp text canvas instead (_drawDeathbombLabel).
  _drawDeathbombPrompt(ctx, camera, player) {
    const prompt = this.juice.deathbombPrompt;
    if (!prompt) return;
    const t = Math.max(0, prompt.remaining / prompt.maxLife);
    const s = camera.worldToScreen(player.x, player.y);
    ctx.save();
    ctx.strokeStyle = this.save.highContrast ? this._arenaInk : DEATHBOMB.color;
    ctx.lineWidth = 2;
    ctx.beginPath();
    // Closes from well outside the sprite to just onto it — the player reads
    // "how much room is left" without reading a number.
    ctx.arc(s.x, s.y, player.radius + 3 + t * 26, 0, Math.PI * 2);
    ctx.stroke();
    ctx.restore();
  }

  // The key half of the deathbomb prompt, on the HUD canvas so it's legible
  // at the sixth of a second it exists for. Names the actual binding (or the
  // touch glyph) rather than hardcoding "SPACE" — cached per run, since
  // neither the binding nor the device mode changes mid-run.
  _drawDeathbombLabel(tc, renderer, camera, player) {
    if (!this.juice.deathbombPrompt) return;
    const s = renderer.scale;
    const p = camera.worldToScreen(player.x, player.y - player.radius - 13);
    this._ultimateKeyLabel ??= ultimateKeyLabel(this.save);
    tc.save();
    tc.textAlign = 'center';
    tc.font = `bold ${Math.round(5 * s)}px ${UI_FONT}`;
    tc.fillStyle = this.save.highContrast ? this._arenaInk : DEATHBOMB.color;
    tc.fillText(this._ultimateKeyLabel, renderer.offsetX + p.x * s, renderer.offsetY + p.y * s);
    tc.restore();
  }

  // Expanding, fading rings for the moment revive/ultimate-start/-end fires
  // (see JuiceDirector.addPlayerPulse) — reads as a distinct shockwave rather
  // than just another particle burst, color-coded to match each event (gold
  // surge, blue revive, purple meter-drain).
  _drawPlayerPulses(ctx, camera) {
    for (const pulse of this.juice.playerPulses) {
      const s = camera.worldToScreen(pulse.x, pulse.y);
      const t = 1 - pulse.life / pulse.maxLife;
      const radius = pulse.maxRadius * t;
      ctx.save();
      ctx.globalAlpha = Math.max(0, pulse.life / pulse.maxLife);
      ctx.strokeStyle = pulse.color;
      ctx.lineWidth = 2;
      ctx.beginPath();
      ctx.arc(s.x, s.y, radius, 0, Math.PI * 2);
      ctx.stroke();
      ctx.restore();
    }
  }

  // Small icons orbiting close to the player, one per owned weapon, facing
  // whatever's currently the nearest target (boss, or a minion during a
  // wave stage) — each pops outward and flashes white for a beat when its
  // weapon fires (driven by Weapon.fireFlash, see Weapon.js).
  _drawWeaponIcons(ctx, camera, player, combat) {
    const target = combat.getNearestTarget(player);
    const dx = target ? target.x - player.x : player.facing.x;
    const dy = target ? target.y - player.y : player.facing.y;
    const baseAngle = Math.atan2(dy, dx);
    const weapons = combat.weapons;
    const ringRadius = player.radius + 5;

    weapons.forEach((weapon, i) => {
      // Fused weapons borrow a parent's ring sprite via config.iconFallback
      // (see FUSED_WEAPONS) until they get sprites of their own.
      const sprite = WEAPON_SPRITES[weapon.name] ?? WEAPON_SPRITES[weapon.config?.iconFallback];
      if (!sprite) return;

      const angle = baseAngle + (i - (weapons.length - 1) / 2) * 0.6;
      const flashT = weapon.fireFlash / WEAPON_FIRE_FLASH_DURATION; // 1 right after firing, decaying to 0
      const recoil = flashT * 2;
      const wx = player.x + Math.cos(angle) * (ringRadius + recoil);
      const wy = player.y + Math.sin(angle) * (ringRadius + recoil);
      const p = camera.worldToScreen(wx, wy);

      drawSprite(ctx, sprite, p.x, p.y, {
        flipX: dx < 0,
        paletteOverride: flashT > 0.5 ? { R: '#ffffff' } : null,
      });
      if (flashT > 0) {
        ctx.save();
        ctx.globalAlpha = flashT;
        ctx.strokeStyle = weapon.config?.color ?? '#ffffff';
        ctx.lineWidth = Math.max(1, flashT * 2);
        ctx.beginPath();
        ctx.moveTo(p.x + (dx < 0 ? -3 : 3), p.y);
        ctx.lineTo(p.x + (dx < 0 ? -7 : 7), p.y);
        ctx.stroke();
        ctx.restore();
      }
    });
  }
}
